import { useState, useEffect, useRef } from "react";
import { initializeApp } from "firebase/app";
import { getFirestore, collection, addDoc, onSnapshot, query, orderBy, where, getDoc, setDoc, doc, serverTimestamp, deleteDoc, getDocs, limit, updateDoc, arrayUnion, arrayRemove } from "firebase/firestore";
import { getAuth, GoogleAuthProvider, signInWithPopup, signInWithRedirect, getRedirectResult, signOut, onAuthStateChanged, sendEmailVerification } from "firebase/auth";
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import earthImage from "./RBAi_Intro.PNG";
// ─── FIREBASE ────────────────────────────────────────────────
const firebaseConfig = {
  apiKey: "AIzaSyBWmjywTWqU1SqRiU7HwqTUzHgSFe9RKbU",
  authDomain: "roadburnai.firebaseapp.com",
  projectId: "roadburnai",
  storageBucket: "roadburnai.firebasestorage.app",
  messagingSenderId: "1073177176923",
  appId: "1:1073177176923:web:5df702e717a61344ec487e",
  measurementId: "G-9R3TQ84Y0J"
};
const firebaseApp = initializeApp(firebaseConfig);
const db = getFirestore(firebaseApp);
const auth = getAuth(firebaseApp);
const storage = getStorage(firebaseApp);
const googleProvider = new GoogleAuthProvider();

// ─── FOUNDER SYSTEM ──────────────────────────────────────────
const FOUNDER_CUTOFF = new Date("2026-12-31T23:59:59");

async function ensureUserProfile(user) {
  const userRef = doc(db, "users", user.uid);
  const snap = await getDoc(userRef);
  if (!snap.exists()) {
    const isFounder = new Date() < FOUNDER_CUTOFF;
    const profile = {
      uid: user.uid,
      displayName: user.displayName || "Nomad",
      email: user.email || "",
      photoURL: user.photoURL || "",
      isFounder,
      joinedAt: serverTimestamp(),
      credits: isFounder ? 5 : 0,
      founderGrantedAt: isFounder ? serverTimestamp() : null,
      bio: "",
      rigType: "Not set",
      seeking: [],
      birthdate: null,
      ageVerified: false,
      roadsVisible: true,
      rigPhotoURL: "",
      roadsLat: null,
      roadsLng: null,
      friends: [],
      friendRequestsSent: [],
      friendRequestsReceived: [],
      passedUids: [],
      blockedUids: [],
      likedUids: [],
    };
    await setDoc(userRef, profile);
    return profile;
  }
  return snap.data();
}

// ─── ROADS / FRIENDS HELPERS ──────────────────────────────────
async function sendFriendRequest(myUid, theirUid) {
  await updateDoc(doc(db, "users", myUid), { friendRequestsSent: arrayUnion(theirUid) });
  await updateDoc(doc(db, "users", theirUid), { friendRequestsReceived: arrayUnion(myUid) });
}
async function acceptFriendRequest(myUid, theirUid) {
  await updateDoc(doc(db, "users", myUid), {
    friends: arrayUnion(theirUid),
    friendRequestsReceived: arrayRemove(theirUid),
  });
  await updateDoc(doc(db, "users", theirUid), {
    friends: arrayUnion(myUid),
    friendRequestsSent: arrayRemove(myUid),
  });
}
async function declineFriendRequest(myUid, theirUid) {
  await updateDoc(doc(db, "users", myUid), { friendRequestsReceived: arrayRemove(theirUid) });
  await updateDoc(doc(db, "users", theirUid), { friendRequestsSent: arrayRemove(myUid) });
}
async function toggleRoadsAction(myUid, field, theirUid, add) {
  await updateDoc(doc(db, "users", myUid), { [field]: add ? arrayUnion(theirUid) : arrayRemove(theirUid) });
}
function isProfileComplete(profile) {
  if (!profile) return false;
  const hasBio = (profile.bio || "").trim().length >= 10;
  const hasRig = profile.rigType && profile.rigType !== "Not set";
  const hasSeeking = (profile.seeking || []).length > 0;
  return hasBio && hasRig && hasSeeking;
}
function calculateAge(birthdateStr) {
  if (!birthdateStr) return null;
  const b = new Date(birthdateStr);
  if (isNaN(b.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - b.getFullYear();
  const m = today.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < b.getDate())) age--;
  return age;
}
function cooldownDaysLeft(map, uid, days = 14) {
  const ts = map?.[uid];
  if (!ts) return 0;
  const elapsed = (Date.now() - new Date(ts).getTime()) / 86400000;
  return elapsed >= days ? 0 : Math.ceil(days - elapsed);
}
async function setFriendRequestCooldown(myUid, theirUid) {
  await updateDoc(doc(db, "users", myUid), { [`friendRequestCooldowns.${theirUid}`]: new Date().toISOString() });
}
async function setMessageCooldown(myUid, theirUid) {
  await updateDoc(doc(db, "users", myUid), { [`messageCooldowns.${theirUid}`]: new Date().toISOString() });
}
async function hasExistingMessages(convoId) {
  const snap = await getDocs(query(collection(db, "conversations", convoId, "messages"), limit(1)));
  return !snap.empty;
}
async function uploadRigPhoto(uid, file) {
  const ext = file.name.split(".").pop();
  const path = `rigPhotos/${uid}/${Date.now()}.${ext}`;
  const fileRef = storageRef(storage, path);
  await uploadBytes(fileRef, file);
  const url = await getDownloadURL(fileRef);
  await updateDoc(doc(db, "users", uid), { rigPhotoURL: url });
  return url;
}

// ─── DESIGN TOKENS ───────────────────────────────────────────
const C = {
  sunrise: "#e8720a",
  sunriseDeep: "#c85a08",
  sunriseSoft: "#fde8d0",
  canyon: "#c85a08",
  canyonLight: "#e8920a",
  canyonSoft: "#fde8d0",
  sage: "#3a9898",
  sageLight: "#5bbaba",
  sageSoft: "#d0eeee",
  sageDark: "#1a6868",
  pine: "#0a3838",
  sky: "#5bbaba",
  skySoft: "#d0eeee",
  parchment: "#f0e8d8",
  parchmentDark: "#e0d0b8",
  parchmentDeep: "#c8b898",
  cream: "#f8f0e4",
  dirt: "#8b6030",
  dirtLight: "#b08048",
  dust: "#c0a070",
  bark: "#2c1a08",
  barkMid: "#4a2e12",
  red: "#c84040",
  redSoft: "#fce8e8",
  green: "#3a9898",
  greenSoft: "#d0eeee",
};

// ─── CAMP DATA (expanded) ─────────────────────────────────────
const ALL_CAMPSITES = [
  {
    id: 1,
    name: "Coconino NF — FR 245",
    detail: "Free dispersed · 12 mi from Flagstaff · Paved access to site",
    tags: ["40ft+", "Large RV", "Free", "Fire OK", "No Hookups"],
    distance: "12 mi",
    lat: 35.18, lng: -111.82,
    elevation: "7,200 ft",
    surface: "Paved + packed dirt",
    maxRigLength: "45 ft",
    stayLimit: "14 days",
    amenities: ["Fire rings", "Dispersed"],
    contributed: false,
    rating: 4.5,
    reviews: 12,
    notes: "Wide pull-through spots. Good cell signal on T-Mobile. Stunning ponderosa views.",
  },
  {
    id: 2,
    name: "BLM — Prescott Valley Road",
    detail: "Free BLM land · 28 mi · Paved + gravel",
    tags: ["Large RV", "40ft+", "Free", "14-day", "Fire OK"],
    distance: "28 mi",
    lat: 34.72, lng: -112.44,
    elevation: "5,100 ft",
    surface: "Gravel",
    maxRigLength: "50 ft",
    stayLimit: "14 days",
    amenities: ["Open area", "No facilities"],
    contributed: false,
    rating: 4.2,
    reviews: 8,
    notes: "Flat open land, easy pull-through. No facilities but plenty of space.",
  },
  {
    id: 3,
    name: "Kaibab NF — FR 307",
    detail: "Free dispersed · 45 mi · High clearance last 2 miles",
    tags: ["Van", "High Clearance", "Free", "No Generators"],
    distance: "45 mi",
    lat: 36.45, lng: -112.05,
    elevation: "8,400 ft",
    surface: "Dirt / rocky",
    maxRigLength: "22 ft",
    stayLimit: "14 days",
    amenities: ["Dispersed", "Primitive"],
    contributed: false,
    rating: 4.8,
    reviews: 5,
    notes: "Incredible solitude. Last 2 miles need high clearance — skoolies stay home.",
  },
  {
    id: 4,
    name: "Lake Pleasant Regional — Roadrunner",
    detail: "Paid hookups · 38 mi · Full amenities",
    tags: ["40ft+", "Large RV", "Full Hookups", "Dump Station", "Paid"],
    distance: "38 mi",
    lat: 33.87, lng: -112.30,
    elevation: "1,670 ft",
    surface: "Paved",
    maxRigLength: "60 ft",
    stayLimit: "30 days",
    amenities: ["Electric", "Water", "Dump station", "Showers"],
    contributed: false,
    rating: 3.9,
    reviews: 22,
    notes: "Good for resupply stays. $35/night with hookups. Hot showers are a bonus.",
  },
  {
    id: 5,
    name: "BLM — Skull Valley Flats",
    detail: "Free BLM land · 52 mi · Any rig size welcome",
    tags: ["40ft+", "Large RV", "Van", "Free", "14-day"],
    distance: "52 mi",
    lat: 34.49, lng: -112.73,
    elevation: "4,250 ft",
    surface: "Dirt, mostly flat",
    maxRigLength: "No limit",
    stayLimit: "14 days",
    amenities: ["Open range", "No facilities"],
    contributed: false,
    rating: 4.3,
    reviews: 17,
    notes: "Open flat land, amazing sunsets. Any size rig can park here.",
  },
  {
    id: 6,
    name: "Fort Tuthill County Park",
    detail: "Paid · 8 mi · Electric hookups available",
    tags: ["40ft+", "Large RV", "Electric Hookup", "Dump Station", "Paid"],
    distance: "8 mi",
    lat: 35.11, lng: -111.65,
    elevation: "6,900 ft",
    surface: "Paved",
    maxRigLength: "55 ft",
    stayLimit: "No limit",
    amenities: ["Electric", "Dump station", "Restrooms", "Showers"],
    contributed: false,
    rating: 4.1,
    reviews: 31,
    notes: "Closest RV-friendly site to downtown Flagstaff. $24–32/night.",
  },
  {
    id: 7,
    name: "Dispersed — Woody Mountain Rd",
    detail: "Free USFS · 6 mi from Flagstaff · Easy access",
    tags: ["Van", "Small RV", "Free", "Fire OK", "14-day"],
    distance: "6 mi",
    lat: 35.13, lng: -111.74,
    elevation: "7,000 ft",
    surface: "Packed dirt",
    maxRigLength: "28 ft",
    stayLimit: "14 days",
    amenities: ["Dispersed", "Fire rings"],
    contributed: true,
    contributedBy: "VanLifeVera",
    rating: 4.6,
    reviews: 9,
    notes: "Community-added spot. Great for vans and small rigs close to town.",
  },
  {
    id: 8,
    name: "Senator Highway Dispersed",
    detail: "Free USFS · 34 mi · Shade trees, creek nearby",
    tags: ["Van", "Small RV", "High Clearance", "Free", "Creek"],
    distance: "34 mi",
    lat: 34.60, lng: -112.52,
    elevation: "5,800 ft",
    surface: "Rocky dirt",
    maxRigLength: "24 ft",
    stayLimit: "14 days",
    amenities: ["Dispersed", "Creek access"],
    contributed: true,
    contributedBy: "SkoolieSam",
    rating: 4.7,
    reviews: 6,
    notes: "Shaded pullouts along creek. High clearance recommended. Worth it.",
  },

  // ── NEW MEXICO ──────────────────────────────────────────────
  {
    id: 9,
    name: "BLM — El Malpais Dispersed",
    detail: "Free BLM · Near Grants, NM · Lava field views",
    tags: ["40ft+", "Large RV", "Free", "14-day", "Fire OK"],
    distance: "0 mi",
    lat: 35.03, lng: -107.97,
    elevation: "6,900 ft",
    surface: "Packed dirt",
    maxRigLength: "50 ft",
    stayLimit: "14 days",
    amenities: ["Dispersed", "No facilities"],
    contributed: false,
    rating: 4.4,
    reviews: 11,
    notes: "Incredible lava field backdrop. Wide open flat areas for big rigs. T-Mobile signal decent.",
  },
  {
    id: 10,
    name: "Carson NF — Dispersed FR 151",
    detail: "Free USFS · Taos area · Ponderosa pines",
    tags: ["Van", "Small RV", "Free", "Fire OK", "14-day"],
    distance: "0 mi",
    lat: 36.51, lng: -105.58,
    elevation: "8,200 ft",
    surface: "Dirt, mostly flat",
    maxRigLength: "28 ft",
    stayLimit: "14 days",
    amenities: ["Dispersed", "Fire rings"],
    contributed: false,
    rating: 4.6,
    reviews: 9,
    notes: "Cool temps even in summer. Beautiful ponderosa forest. Close to Taos for resupply.",
  },
  {
    id: 11,
    name: "BLM — Elephant Butte Flats",
    detail: "Free BLM · Truth or Consequences, NM · Desert flats",
    tags: ["40ft+", "Large RV", "Van", "Free", "14-day"],
    distance: "0 mi",
    lat: 33.18, lng: -107.22,
    elevation: "4,400 ft",
    surface: "Sand and gravel",
    maxRigLength: "No limit",
    stayLimit: "14 days",
    amenities: ["Open range", "No facilities"],
    contributed: false,
    rating: 4.2,
    reviews: 14,
    notes: "Huge open BLM land near the lake. Any size rig. Hot springs in T or C nearby.",
  },

  // ── UTAH ────────────────────────────────────────────────────
  {
    id: 12,
    name: "BLM — Moab Dispersed Kane Creek",
    detail: "Free BLM · Moab, UT · Red rock canyon",
    tags: ["Van", "Small RV", "High Clearance", "Free", "14-day"],
    distance: "0 mi",
    lat: 38.54, lng: -109.62,
    elevation: "4,050 ft",
    surface: "Sand and rock",
    maxRigLength: "24 ft",
    stayLimit: "14 days",
    amenities: ["Dispersed", "Primitive"],
    contributed: false,
    rating: 4.9,
    reviews: 38,
    notes: "World-class red rock setting. High clearance strongly recommended. One of the best free spots in the country.",
  },
  {
    id: 13,
    name: "Dixie NF — Dispersed Cedar Mountain",
    detail: "Free USFS · Cedar City, UT · High plateau",
    tags: ["40ft+", "Large RV", "Free", "Fire OK", "14-day"],
    distance: "0 mi",
    lat: 37.68, lng: -113.05,
    elevation: "9,100 ft",
    surface: "Packed dirt",
    maxRigLength: "45 ft",
    stayLimit: "14 days",
    amenities: ["Dispersed", "Fire rings"],
    contributed: false,
    rating: 4.5,
    reviews: 7,
    notes: "Cool high-altitude escape from desert heat. Wide pullouts for large rigs. Stars are unreal.",
  },
  {
    id: 14,
    name: "BLM — San Rafael Swell",
    detail: "Free BLM · Price, UT · Remote desert canyon country",
    tags: ["Van", "High Clearance", "Free", "14-day"],
    distance: "0 mi",
    lat: 38.92, lng: -110.68,
    elevation: "5,600 ft",
    surface: "Dirt and gravel",
    maxRigLength: "22 ft",
    stayLimit: "14 days",
    amenities: ["Dispersed", "Primitive"],
    contributed: false,
    rating: 4.8,
    reviews: 16,
    notes: "Remote and stunning canyon country. Best for vans and high-clearance rigs. No cell signal.",
  },

  // ── NEVADA ──────────────────────────────────────────────────
  {
    id: 15,
    name: "BLM — Valley of Fire Dispersed",
    detail: "Free BLM · Overton, NV · Red sandstone formations",
    tags: ["Van", "Small RV", "Free", "14-day", "Fire OK"],
    distance: "0 mi",
    lat: 36.46, lng: -114.53,
    elevation: "2,000 ft",
    surface: "Packed dirt",
    maxRigLength: "30 ft",
    stayLimit: "14 days",
    amenities: ["Dispersed", "No facilities"],
    contributed: false,
    rating: 4.3,
    reviews: 19,
    notes: "Just outside the state park boundary — free version of a stunning area. Hot in summer, perfect in winter.",
  },
  {
    id: 16,
    name: "Humboldt-Toiyabe NF — Dispersed Austin NV",
    detail: "Free USFS · Austin, NV · Central Nevada mountains",
    tags: ["40ft+", "Large RV", "Free", "Fire OK", "14-day"],
    distance: "0 mi",
    lat: 39.49, lng: -117.07,
    elevation: "6,600 ft",
    surface: "Gravel and dirt",
    maxRigLength: "45 ft",
    stayLimit: "14 days",
    amenities: ["Dispersed", "Fire rings"],
    contributed: false,
    rating: 4.1,
    reviews: 5,
    notes: "Lonely but beautiful. Middle of Nevada highway country. Quiet, cool, zero crowds.",
  },
  {
    id: 17,
    name: "BLM — Winnemucca Flats",
    detail: "Free BLM · Winnemucca, NV · High desert flats",
    tags: ["40ft+", "Large RV", "Van", "Free", "14-day"],
    distance: "0 mi",
    lat: 40.97, lng: -117.74,
    elevation: "4,300 ft",
    surface: "Flat dirt",
    maxRigLength: "No limit",
    stayLimit: "14 days",
    amenities: ["Open range", "No facilities"],
    contributed: false,
    rating: 3.9,
    reviews: 8,
    notes: "Flat open land good for any rig size. Convenient off I-80 corridor. Basic but functional.",
  },

  // ── COLORADO ────────────────────────────────────────────────
  {
    id: 18,
    name: "BLM — Gunnison Dispersed",
    detail: "Free BLM · Gunnison, CO · River valley",
    tags: ["Van", "Small RV", "Free", "Fire OK", "14-day"],
    distance: "0 mi",
    lat: 38.55, lng: -106.92,
    elevation: "7,700 ft",
    surface: "Gravel",
    maxRigLength: "28 ft",
    stayLimit: "14 days",
    amenities: ["Dispersed", "River access"],
    contributed: false,
    rating: 4.7,
    reviews: 22,
    notes: "River access, incredible scenery. Fills up in summer — arrive by Thursday. Cold nights year-round.",
  },
  {
    id: 19,
    name: "Pike NF — Dispersed Tarryall",
    detail: "Free USFS · Lake George, CO · Rocky Mountain views",
    tags: ["40ft+", "Large RV", "Free", "Fire OK", "14-day"],
    distance: "0 mi",
    lat: 39.02, lng: -105.51,
    elevation: "8,400 ft",
    surface: "Packed dirt",
    maxRigLength: "40 ft",
    stayLimit: "14 days",
    amenities: ["Dispersed", "Fire rings"],
    contributed: false,
    rating: 4.6,
    reviews: 13,
    notes: "Stunning Rockies backdrop. Wide pullouts work for most rigs. Bring layers — cold at night.",
  },
  {
    id: 20,
    name: "BLM — Dolores River Canyon",
    detail: "Free BLM · Dolores, CO · Desert canyon",
    tags: ["Van", "High Clearance", "Free", "14-day"],
    distance: "0 mi",
    lat: 37.47, lng: -108.79,
    elevation: "5,200 ft",
    surface: "Rocky dirt",
    maxRigLength: "22 ft",
    stayLimit: "14 days",
    amenities: ["Dispersed", "River access"],
    contributed: false,
    rating: 4.8,
    reviews: 10,
    notes: "Remote river canyon with excellent solitude. Vans and high-clearance only. No cell signal at all.",
  },

  // ── CALIFORNIA ──────────────────────────────────────────────
  {
    id: 21,
    name: "BLM — Anza-Borrego Dispersed",
    detail: "Free BLM · Borrego Springs, CA · Desert",
    tags: ["Van", "Small RV", "Free", "14-day"],
    distance: "0 mi",
    lat: 33.26, lng: -116.40,
    elevation: "1,500 ft",
    surface: "Sand and dirt",
    maxRigLength: "30 ft",
    stayLimit: "14 days",
    amenities: ["Dispersed", "No facilities"],
    contributed: false,
    rating: 4.5,
    reviews: 41,
    notes: "One of the largest BLM dispersed areas in the lower 48. Wildflowers in spring are legendary.",
  },
  {
    id: 22,
    name: "Shasta-Trinity NF — Dispersed Lewiston",
    detail: "Free USFS · Lewiston, CA · Northern CA mountains",
    tags: ["40ft+", "Large RV", "Free", "Fire OK", "14-day"],
    distance: "0 mi",
    lat: 40.72, lng: -122.80,
    elevation: "2,400 ft",
    surface: "Gravel and dirt",
    maxRigLength: "45 ft",
    stayLimit: "14 days",
    amenities: ["Dispersed", "River access"],
    contributed: false,
    rating: 4.4,
    reviews: 8,
    notes: "Remote northern CA forest. Wide spots work for big rigs. River swimming in summer.",
  },
  {
    id: 23,
    name: "BLM — Ocotillo Wells SVRA Staging",
    detail: "Free BLM · Ocotillo Wells, CA · Desert staging area",
    tags: ["40ft+", "Large RV", "Van", "Free", "14-day", "Dump Station"],
    distance: "0 mi",
    lat: 33.15, lng: -116.11,
    elevation: "420 ft",
    surface: "Flat dirt",
    maxRigLength: "No limit",
    stayLimit: "14 days",
    amenities: ["Dump station nearby", "Open area"],
    contributed: false,
    rating: 4.0,
    reviews: 27,
    notes: "Popular staging area. Dump station at the SVRA. Flat and open — any rig size welcome.",
  },

  // ── OREGON ──────────────────────────────────────────────────
  {
    id: 24,
    name: "Deschutes NF — Dispersed Bend OR",
    detail: "Free USFS · Bend, OR · High desert pine forest",
    tags: ["40ft+", "Large RV", "Free", "Fire OK", "14-day"],
    distance: "0 mi",
    lat: 43.95, lng: -121.45,
    elevation: "4,600 ft",
    surface: "Volcanic pumice",
    maxRigLength: "45 ft",
    stayLimit: "14 days",
    amenities: ["Dispersed", "Fire rings"],
    contributed: false,
    rating: 4.7,
    reviews: 33,
    notes: "Close to Bend amenities. Pumice surface stays dry. One of the best urban-adjacent free camping areas in the PNW.",
  },
  {
    id: 25,
    name: "BLM — Steens Mountain Dispersed",
    detail: "Free BLM · Burns, OR · Remote high desert",
    tags: ["Van", "High Clearance", "Free", "14-day"],
    distance: "0 mi",
    lat: 42.64, lng: -118.58,
    elevation: "7,200 ft",
    surface: "Rocky dirt",
    maxRigLength: "24 ft",
    stayLimit: "14 days",
    amenities: ["Dispersed", "Primitive"],
    contributed: false,
    rating: 4.9,
    reviews: 12,
    notes: "One of the most remote and spectacular places in Oregon. Zero light pollution. High clearance essential.",
  },
  {
    id: 26,
    name: "Umpqua NF — Dispersed Roseburg OR",
    detail: "Free USFS · Roseburg, OR · Old-growth forest",
    tags: ["Van", "Small RV", "Free", "Fire OK", "14-day"],
    distance: "0 mi",
    lat: 43.22, lng: -122.96,
    elevation: "2,100 ft",
    surface: "Dirt and gravel",
    maxRigLength: "30 ft",
    stayLimit: "14 days",
    amenities: ["Dispersed", "Creek access"],
    contributed: false,
    rating: 4.5,
    reviews: 9,
    notes: "Lush old-growth setting. Creeks everywhere. Wetter climate — bring waterproofing.",
  },

  // ── WASHINGTON ──────────────────────────────────────────────
  {
    id: 27,
    name: "Okanogan NF — Dispersed Twisp WA",
    detail: "Free USFS · Twisp, WA · Eastern WA dry forest",
    tags: ["40ft+", "Large RV", "Free", "Fire OK", "14-day"],
    distance: "0 mi",
    lat: 48.36, lng: -120.12,
    elevation: "3,400 ft",
    surface: "Packed dirt",
    maxRigLength: "40 ft",
    stayLimit: "14 days",
    amenities: ["Dispersed", "Fire rings"],
    contributed: false,
    rating: 4.3,
    reviews: 7,
    notes: "Dry eastern Washington forest — much less rain than the west side. Wide pullouts for large rigs.",
  },
  {
    id: 28,
    name: "BLM — Yakima River Canyon",
    detail: "Free BLM · Ellensburg, WA · River canyon",
    tags: ["Van", "Small RV", "Free", "14-day"],
    distance: "0 mi",
    lat: 46.92, lng: -120.54,
    elevation: "1,600 ft",
    surface: "Gravel",
    maxRigLength: "28 ft",
    stayLimit: "14 days",
    amenities: ["Dispersed", "River access"],
    contributed: false,
    rating: 4.6,
    reviews: 18,
    notes: "Riverside spots along the Yakima. Great fishing. Close to I-90 for easy access.",
  },
  {
    id: 29,
    name: "Colville NF — Dispersed Republic WA",
    detail: "Free USFS · Republic, WA · Remote northeast WA",
    tags: ["Van", "Small RV", "Free", "Fire OK", "14-day"],
    distance: "0 mi",
    lat: 48.65, lng: -118.74,
    elevation: "3,800 ft",
    surface: "Dirt",
    maxRigLength: "30 ft",
    stayLimit: "14 days",
    amenities: ["Dispersed", "Fire rings"],
    contributed: false,
    rating: 4.4,
    reviews: 4,
    notes: "Very remote and very quiet. Northeast WA is underrated for nomads. Minimal crowds.",
  },

  // ── TEXAS ───────────────────────────────────────────────────
  {
    id: 30,
    name: "Big Bend Ranch SP — Dispersed",
    detail: "Paid permit · Presidio, TX · Remote desert",
    tags: ["Van", "High Clearance", "14-day"],
    distance: "0 mi",
    lat: 29.35, lng: -103.90,
    elevation: "3,500 ft",
    surface: "Rocky dirt",
    maxRigLength: "22 ft",
    stayLimit: "14 days",
    amenities: ["Primitive", "Permit required"],
    contributed: false,
    rating: 4.8,
    reviews: 21,
    notes: "One of the most remote places in Texas. $12/night permit. Absolute solitude. High clearance required.",
  },
  {
    id: 31,
    name: "Amistad NRA — Dispersed",
    detail: "Free NPS · Del Rio, TX · Desert lake",
    tags: ["40ft+", "Large RV", "Van", "Free", "14-day"],
    distance: "0 mi",
    lat: 29.47, lng: -101.07,
    elevation: "1,100 ft",
    surface: "Packed dirt",
    maxRigLength: "No limit",
    stayLimit: "14 days",
    amenities: ["Dispersed", "Lake access"],
    contributed: false,
    rating: 4.1,
    reviews: 16,
    notes: "Free dispersed camping at the reservoir. Any rig size. Good fishing. Hot in summer.",
  },
  {
    id: 32,
    name: "Guadalupe Mountains NP — Dog Canyon",
    detail: "Paid · Salt Flat, TX · Remote mountain camping",
    tags: ["Van", "Small RV", "14-day"],
    distance: "0 mi",
    lat: 31.97, lng: -104.84,
    elevation: "6,300 ft",
    surface: "Gravel",
    maxRigLength: "28 ft",
    stayLimit: "14 days",
    amenities: ["Restrooms", "No hookups"],
    contributed: false,
    rating: 4.6,
    reviews: 11,
    notes: "Remote NPS campground on the New Mexico border side. $15/night. Cooler temps, incredible hiking.",
  },

  // ── FLORIDA ─────────────────────────────────────────────────
  {
    id: 33,
    name: "Ocala NF — Dispersed Lake Dorr",
    detail: "Free USFS · Altoona, FL · Florida forest",
    tags: ["40ft+", "Large RV", "Van", "Free", "14-day", "Fire OK"],
    distance: "0 mi",
    lat: 29.08, lng: -81.75,
    elevation: "60 ft",
    surface: "Sand",
    maxRigLength: "45 ft",
    stayLimit: "14 days",
    amenities: ["Dispersed", "Lake access"],
    contributed: false,
    rating: 4.2,
    reviews: 28,
    notes: "Florida's best free camping. Sandy sites work for big rigs in dry season. Swimming in the lake.",
  },
  {
    id: 34,
    name: "Apalachicola NF — Dispersed",
    detail: "Free USFS · Bristol, FL · Panhandle pine forest",
    tags: ["40ft+", "Large RV", "Free", "Fire OK", "14-day"],
    distance: "0 mi",
    lat: 30.10, lng: -84.87,
    elevation: "70 ft",
    surface: "Sand and dirt",
    maxRigLength: "40 ft",
    stayLimit: "14 days",
    amenities: ["Dispersed", "No facilities"],
    contributed: false,
    rating: 4.0,
    reviews: 14,
    notes: "Quiet panhandle forest. Good in winter when the rest of Florida is packed. Flat sandy sites.",
  },
  {
    id: 35,
    name: "Big Cypress NP — Loop Road Dispersed",
    detail: "Free NPS · Ochopee, FL · Everglades edge",
    tags: ["Van", "Small RV", "Free", "14-day"],
    distance: "0 mi",
    lat: 25.88, lng: -81.08,
    elevation: "8 ft",
    surface: "Limestone gravel",
    maxRigLength: "28 ft",
    stayLimit: "14 days",
    amenities: ["Dispersed", "Primitive"],
    contributed: false,
    rating: 4.4,
    reviews: 19,
    notes: "Unique Everglades-edge experience. Alligators are real — keep food secured. Incredible wildlife.",
  },

  // ── GEORGIA ─────────────────────────────────────────────────
  {
    id: 36,
    name: "Chattahoochee NF — Dispersed Blue Ridge",
    detail: "Free USFS · Blue Ridge, GA · Southern Appalachians",
    tags: ["Van", "Small RV", "Free", "Fire OK", "14-day"],
    distance: "0 mi",
    lat: 34.86, lng: -84.32,
    elevation: "2,200 ft",
    surface: "Dirt and leaves",
    maxRigLength: "28 ft",
    stayLimit: "14 days",
    amenities: ["Dispersed", "Fire rings"],
    contributed: false,
    rating: 4.5,
    reviews: 17,
    notes: "Beautiful southern Appalachian forest. Creek sounds all night. Best in fall for foliage.",
  },
  {
    id: 37,
    name: "Oconee NF — Dispersed Eatonton",
    detail: "Free USFS · Eatonton, GA · Georgia piedmont",
    tags: ["40ft+", "Large RV", "Free", "Fire OK", "14-day"],
    distance: "0 mi",
    lat: 33.34, lng: -83.39,
    elevation: "580 ft",
    surface: "Packed dirt",
    maxRigLength: "40 ft",
    stayLimit: "14 days",
    amenities: ["Dispersed", "Lake access"],
    contributed: false,
    rating: 4.0,
    reviews: 9,
    notes: "Flat Georgia forest with lake access. Warm most of the year. Good winter camping destination.",
  },
  {
    id: 38,
    name: "Cloudland Canyon SP — Backcountry",
    detail: "Paid permit · Rising Fawn, GA · Canyon views",
    tags: ["Van", "Small RV", "14-day"],
    distance: "0 mi",
    lat: 34.84, lng: -85.49,
    elevation: "1,800 ft",
    surface: "Gravel",
    maxRigLength: "30 ft",
    stayLimit: "7 days",
    amenities: ["Restrooms", "No hookups"],
    contributed: false,
    rating: 4.7,
    reviews: 23,
    notes: "Stunning canyon overlooks. $25/night. Georgia's most scenic camping. Book ahead — fills fast.",
  },

  // ── IDAHO ───────────────────────────────────────────────────
  {
    id: 39,
    name: "Boise NF — Dispersed Idaho City",
    detail: "Free USFS · Idaho City, ID · High desert mountains",
    tags: ["40ft+", "Large RV", "Free", "Fire OK", "14-day"],
    distance: "0 mi",
    lat: 43.83, lng: -115.84,
    elevation: "4,000 ft",
    surface: "Gravel and dirt",
    maxRigLength: "45 ft",
    stayLimit: "14 days",
    amenities: ["Dispersed", "Fire rings"],
    contributed: false,
    rating: 4.4,
    reviews: 11,
    notes: "Easy access from Boise. Wide pullouts for big rigs. Gold rush ghost town history nearby.",
  },
  {
    id: 40,
    name: "BLM — Owyhee Desert Dispersed",
    detail: "Free BLM · Murphy, ID · Remote high desert",
    tags: ["Van", "High Clearance", "Free", "14-day"],
    distance: "0 mi",
    lat: 43.17, lng: -116.54,
    elevation: "3,200 ft",
    surface: "Desert hardpan",
    maxRigLength: "24 ft",
    stayLimit: "14 days",
    amenities: ["Open range", "No facilities"],
    contributed: false,
    rating: 4.6,
    reviews: 7,
    notes: "One of the most remote BLM areas in the lower 48. Zero facilities. Absolute solitude. High clearance only.",
  },
  {
    id: 41,
    name: "Caribou-Targhee NF — Dispersed Ashton ID",
    detail: "Free USFS · Ashton, ID · Near Yellowstone",
    tags: ["40ft+", "Large RV", "Free", "Fire OK", "14-day"],
    distance: "0 mi",
    lat: 44.07, lng: -111.46,
    elevation: "5,300 ft",
    surface: "Volcanic ash dirt",
    maxRigLength: "45 ft",
    stayLimit: "14 days",
    amenities: ["Dispersed", "Fire rings"],
    contributed: false,
    rating: 4.5,
    reviews: 13,
    notes: "Gateway to Yellowstone without the crowds or fees. Wide open spots for any rig.",
  },

  // ── MONTANA ─────────────────────────────────────────────────
  {
    id: 42,
    name: "Gallatin NF — Dispersed Bozeman MT",
    detail: "Free USFS · Bozeman, MT · Rocky Mountain foothills",
    tags: ["Van", "Small RV", "Free", "Fire OK", "14-day"],
    distance: "0 mi",
    lat: 45.49, lng: -111.16,
    elevation: "5,800 ft",
    surface: "Packed dirt",
    maxRigLength: "30 ft",
    stayLimit: "14 days",
    amenities: ["Dispersed", "Fire rings"],
    contributed: false,
    rating: 4.7,
    reviews: 20,
    notes: "Close to Bozeman but feels truly wild. Bears present — use bear box or canister. Stunning Rockies views.",
  },
  {
    id: 43,
    name: "BLM — Missouri River Breaks",
    detail: "Free BLM · Lewistown, MT · Remote river breaks",
    tags: ["Van", "High Clearance", "Free", "14-day"],
    distance: "0 mi",
    lat: 47.62, lng: -108.77,
    elevation: "2,400 ft",
    surface: "Clay and gravel",
    maxRigLength: "24 ft",
    stayLimit: "14 days",
    amenities: ["Dispersed", "River access"],
    contributed: false,
    rating: 4.8,
    reviews: 8,
    notes: "One of the wildest places in the lower 48. Lewis and Clark country. Impassable when wet — check forecast.",
  },
  {
    id: 44,
    name: "Flathead NF — Dispersed Kalispell MT",
    detail: "Free USFS · Kalispell, MT · Glacier country",
    tags: ["40ft+", "Large RV", "Free", "Fire OK", "14-day"],
    distance: "0 mi",
    lat: 48.19, lng: -114.31,
    elevation: "3,100 ft",
    surface: "Gravel",
    maxRigLength: "40 ft",
    stayLimit: "14 days",
    amenities: ["Dispersed", "Fire rings"],
    contributed: false,
    rating: 4.6,
    reviews: 15,
    notes: "Near Glacier NP without the park fees. Large rig pullouts available. Short season — snow possible June and September.",
  },

  // ── WYOMING ─────────────────────────────────────────────────
  {
    id: 45,
    name: "Shoshone NF — Dispersed Cody WY",
    detail: "Free USFS · Cody, WY · Absaroka Range",
    tags: ["40ft+", "Large RV", "Free", "Fire OK", "14-day"],
    distance: "0 mi",
    lat: 44.33, lng: -109.56,
    elevation: "6,400 ft",
    surface: "Gravel and dirt",
    maxRigLength: "45 ft",
    stayLimit: "14 days",
    amenities: ["Dispersed", "Fire rings"],
    contributed: false,
    rating: 4.6,
    reviews: 12,
    notes: "Eastern gateway to Yellowstone. Big rig friendly pullouts. Bears and wolves present — take precautions.",
  },
  {
    id: 46,
    name: "BLM — Red Desert Dispersed",
    detail: "Free BLM · Rawlins, WY · High desert basin",
    tags: ["40ft+", "Large RV", "Van", "Free", "14-day"],
    distance: "0 mi",
    lat: 41.79, lng: -108.21,
    elevation: "6,700 ft",
    surface: "Flat hardpan",
    maxRigLength: "No limit",
    stayLimit: "14 days",
    amenities: ["Open range", "No facilities"],
    contributed: false,
    rating: 4.2,
    reviews: 6,
    notes: "Vast open basin — any size rig. Wild horses roam here. Wind can be extreme. Surreal emptiness.",
  },
  {
    id: 47,
    name: "Bridger-Teton NF — Dispersed Jackson WY",
    detail: "Free USFS · Jackson, WY · Teton views",
    tags: ["Van", "Small RV", "Free", "Fire OK", "14-day"],
    distance: "0 mi",
    lat: 43.48, lng: -110.76,
    elevation: "6,800 ft",
    surface: "Packed dirt",
    maxRigLength: "28 ft",
    stayLimit: "14 days",
    amenities: ["Dispersed", "Fire rings"],
    contributed: false,
    rating: 4.9,
    reviews: 29,
    notes: "Free camping near Jackson with Teton views. Extremely popular — arrive by Wednesday in peak season. Worth it.",
  },
];
const ALL_NOMADS = [
  { id:1, name:"Sierra", age:29, rig:"1998 Thomas Built Skoolie", rigType:"Skoolie", location:"Sedona, AZ", distance:"12 mi", seeking:["Travel Companion","Romance"], bio:"Full-time since 2022. Solar powered, dog friendly, always chasing sunsets. Cummins ISB — she runs loud but she runs.", emoji:"🚌", founder:true, crossingPaths:true, verified:true },
  { id:2, name:"Jake", age:34, rig:"2019 Ford Transit High Roof", rigType:"Van", location:"Flagstaff, AZ", distance:"8 mi", seeking:["Friendship","Convoy Partner"], bio:"Remote dev by day, trail runner by dawn. Van built out with a rooftop deck. Looking for convoy buddies heading west.", emoji:"🚐", founder:false, crossingPaths:false, verified:true },
  { id:3, name:"Maya", age:27, rig:"Converted Sprinter 144", rigType:"Van", location:"Jerome, AZ", distance:"28 mi", seeking:["Friendship","Travel Companion"], bio:"Artist and photographer. I paint landscapes from wherever I wake up. Looking for fellow creatives on the road.", emoji:"🎨", founder:true, crossingPaths:true, verified:false },
  { id:4, name:"Rex", age:41, rig:"2005 MCI Coach Conversion", rigType:"Bus", location:"Williams, AZ", distance:"35 mi", seeking:["Friendship","Romance"], bio:"Retired firefighter. Been living on this coach for 4 years. Cook good food, tell bad jokes. Two cats onboard.", emoji:"🚎", founder:false, crossingPaths:false, verified:true },
  { id:5, name:"Luna", age:31, rig:"1985 Bluebird All-American", rigType:"Skoolie", location:"Prescott, AZ", distance:"48 mi", seeking:["Convoy Partner","Travel Companion"], bio:"Plant-based chef on wheels. Built out my bus kitchen first — priorities! Heading to the Pacific Northwest this summer.", emoji:"🌿", founder:true, crossingPaths:true, verified:true },
];
const ALL_THREADS = [
  { id:1, author:"MesaKing_Bus", founder:true, time:"2h ago", location:"Sedona, AZ", title:"Best free camping near Sedona?", preview:"Found a killer spot off FR 525 last week, full hookup view of Cathedral Rock...", replies:14, views:89 },
  { id:2, author:"VanLifeVera", founder:false, time:"5h ago", location:"I-40, NM", title:"Diesel prices spiking on I-40?", preview:"Stopped at 4 stations between Albuquerque and Gallup — all over $4.20...", replies:7, views:43 },
  { id:3, author:"SkoolieSam", founder:true, time:"1d ago", location:"Moab, UT", title:"Cummins ISB coolant leak — anyone dealt with this?", preview:"Got a slow drip at the water pump. 2003 Thomas Built, 230k miles...", replies:22, views:156 },
  { id:4, author:"DesertRoseRV", founder:false, time:"2d ago", location:"Tucson, AZ", title:"Solar setup for full-time living?", preview:"Finally pulled the trigger on 400W of panels. Here's what I learned...", replies:31, views:204 },
];

const ALL_LISTINGS = [
  { id:1, emoji:"🚌", title:"2004 Thomas Built Skoolie — Full Convert", price:"$28,500", meta:"Flagstaff, AZ · Posted 2d ago", verified:true, founder:true },
  { id:2, emoji:"☀️", title:"200W Renogy Solar Kit — Never Installed", price:"$650", meta:"Passing through · Phoenix, AZ", verified:true, founder:false },
  { id:3, emoji:"🔋", title:"4x 100Ah Lithium Batteries — Battle Born", price:"$1,200", meta:"Sedona, AZ · Posted today", verified:false, founder:false },
];

// ─── SAVINGS TAB ─────────────────────────────────────────────
const SAVINGS_TIPS = [
  {
    id: 1,
    emoji: "🚛",
    title: "Mudflap — Diesel Savings App",
    subtitle: "Save up to $1.00/gal at 50,000+ truck stops",
    description: "The #1 app for skoolie owners and nomads who run diesel. Lock in discounted prices before you pull up to the pump. Used by 500,000+ drivers. Pilot, Flying J, Love's, TA, Petro and more.",
    cta: "GET THE APP FREE →",
    link: "https://dashboard.mudflapinc.com/invite/de0d6f",
    highlight: true,
    badge: "🔥 RoadBurnAI Recommends",
  },
  {
    id: 2,
    emoji: "⛺",
    title: "Freecampsites.net",
    subtitle: "Free & cheap camping across the US",
    description: "The largest database of free camping spots — BLM, USFS, dispersed, and more. Desktop site works great for trip planning.",
    cta: "FIND FREE CAMPS →",
    link: "https://freecampsites.net",
    highlight: false,
    badge: null,
  },
  {
    id: 3,
    emoji: "⛽",
    title: "GasBuddy",
    subtitle: "Find the cheapest fuel near you",
    description: "Real-time fuel prices reported by drivers. Pair it with your RoadBurnAI fuel tracker to always know if you're getting a good deal.",
    cta: "FIND CHEAP FUEL →",
    link: "https://www.gasbuddy.com",
    highlight: false,
    badge: null,
  },
  {
    id: 4,
    emoji: "🅿️",
    title: "Overnight Parking (iOverlander)",
    subtitle: "Community-vetted overnight spots",
    description: "Truck stops, Walmarts, rest areas, and wild spots rated by the overlanding and nomadic community. Great supplement to RoadBurnAI Camp.",
    cta: "FIND PARKING →",
    link: "https://www.ioverlander.com",
    highlight: false,
    badge: null,
  },
];

const ALL_RESCUE = [
  { id:1, name:"Freightliner of Flagstaff", type:"Dealership", engines:["Cummins","Detroit","Mercedes"], distance:"3.2 mi", phone:"928-555-0142" },
  { id:2, name:"High Desert Diesel Repair", type:"Independent Shop", engines:["Cummins","Cat","International"], distance:"5.8 mi", phone:"928-555-0187" },
  { id:3, name:"Road Dog Mobile Mechanic", type:"Mobile Mechanic", engines:["All engines"], distance:"Comes to you", phone:"928-555-0231" },
];

// ─── FONTS ───────────────────────────────────────────────────
const fonts = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700;900&family=Crimson+Pro:ital,wght@0,400;0,600;1,400&family=Space+Mono:wght@400;700&display=swap');
`;

// ─── SHARED COMPONENTS ───────────────────────────────────────
function MPGBadge({ mpg }) {
  if (!mpg) return null;
  const v = parseFloat(mpg);
  const [bg, color, border] = v < 7
    ? [C.redSoft, C.red, "#e8b8b8"]
    : v < 8.5
    ? [C.sunriseSoft, C.sunriseDeep, "#e8d4a0"]
    : [C.sageSoft, C.sageDark, "#b8d4b8"];
  return (
    <span style={{
      background: bg, color, border: `1px solid ${border}`,
      borderRadius: 6, padding: "2px 8px",
      fontFamily: "Space Mono, monospace", fontSize: 10, fontWeight: 700
    }}>
      {v} MPG
    </span>
  );
}

function FounderBadge({ size = "sm" }) {
  const sizes = { sm: { padding: "2px 7px", fontSize: 8, gap: 3 }, md: { padding: "4px 10px", fontSize: 10, gap: 4 }, lg: { padding: "6px 14px", fontSize: 12, gap: 5 } };
  const s = sizes[size] || sizes.sm;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: s.gap,
      background: `linear-gradient(135deg, ${C.sunrise}, ${C.canyon})`,
      color: "#fff", borderRadius: 6, padding: s.padding,
      fontFamily: "Space Mono, monospace", fontSize: s.fontSize,
      fontWeight: 700, letterSpacing: 0.5,
      boxShadow: "0 1px 6px rgba(232,114,10,0.4)", whiteSpace: "nowrap",
    }}>
      🔥 FOUNDER
    </span>
  );
}

function FounderRevealCard({ onDismiss }) {
  return (
    <div style={{
      background: `linear-gradient(135deg, ${C.sunriseSoft}, ${C.canyonSoft})`,
      border: `1.5px solid ${C.canyonLight}`, borderRadius: 16, padding: 20,
      animation: "fadeUp 0.5s ease both",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
        <div>
          <div style={{ fontFamily: "Playfair Display, serif", fontSize: 18, fontWeight: 700, color: C.parchment, marginBottom: 4 }}>You're a Founder. 🔥</div>
          <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 13, color: C.dirt, fontStyle: "italic" }}>You joined during the founding period.</div>
        </div>
        <FounderBadge size="md" />
      </div>
      <div style={{ background: "rgba(255,255,255,0.5)", borderRadius: 10, padding: "10px 14px", marginBottom: 14 }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
          <span style={{ background: C.sageSoft, color: C.sageDark, borderRadius: 20, fontSize: 11, padding: "3px 10px", fontFamily: "Space Mono, monospace", fontWeight: 700 }}>✓ +5 Credits added</span>
          <span style={{ fontFamily: "Crimson Pro, serif", fontSize: 12, color: C.dirt }}>Badge visible everywhere on the platform.</span>
        </div>
      </div>
      <button onClick={onDismiss} style={{ width: "100%", background: "none", border: `1.5px solid ${C.canyon}`, borderRadius: 10, padding: "9px 0", fontFamily: "Space Mono, monospace", fontSize: 10, fontWeight: 700, color: C.canyon, cursor: "pointer", letterSpacing: 0.5 }}>
        LET'S GO →
      </button>
    </div>
  );
}

function SectionHeader({ icon, title, color = "#ffffff" }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
      <div style={{ width: 34, height: 34, borderRadius: 10, background: C.sageSoft, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>{icon}</div>
      <div style={{ fontFamily: "Playfair Display, serif", fontSize: 19, fontWeight: 700, color }}>{title}</div>
    </div>
  );
}

function Card({ children, accent, style = {} }) {
  return (
    <div style={{
      background: "#231408", border: `1.5px solid ${C.parchmentDeep}`,
      borderLeft: accent ? `4px solid ${accent}` : undefined,
      borderRadius: 14, padding: 16, marginBottom: 12,
      boxShadow: "0 2px 12px rgba(61,43,31,0.06)", ...style
    }}>
      {children}
    </div>
  );
}

function Btn({ children, onClick, variant = "primary", style = {} }) {
  const base = { border: "none", borderRadius: 12, padding: "13px 16px", fontFamily: "Space Mono, monospace", fontSize: 11, fontWeight: 700, letterSpacing: 1, cursor: "pointer", width: "100%", marginTop: 8, transition: "opacity 0.15s" };
  if (variant === "primary") return <button onClick={onClick} style={{ ...base, background: `linear-gradient(135deg, ${C.sunrise}, ${C.canyon})`, color: "#fff", ...style }}>{children}</button>;
  if (variant === "secondary") return <button onClick={onClick} style={{ ...base, background: C.parchment, border: `1.5px solid ${C.parchmentDeep}`, color: C.bark, ...style }}>{children}</button>;
  if (variant === "sage") return <button onClick={onClick} style={{ ...base, background: C.sageSoft, border: `1.5px solid ${C.sageLight}`, color: C.sageDark, ...style }}>{children}</button>;
}

function InputField({ label, value, onChange, placeholder, type = "text", suffix, prefix, half }) {
  return (
    <div style={{ marginBottom: 14, flex: half ? 1 : undefined }}>
      <label style={{ display: "block", fontFamily: "Space Mono, monospace", fontSize: 9, fontWeight: 700, color: C.dust, letterSpacing: 1.2, marginBottom: 6, textTransform: "uppercase" }}>{label}</label>
      <div style={{ position: "relative" }}>
        {prefix && <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: C.dust, fontFamily: "Space Mono, monospace", fontSize: 12 }}>{prefix}</span>}
        <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} type={type} style={{ width: "100%", background: "#231408", border: `1.5px solid ${C.parchmentDeep}`, borderRadius: 10, padding: `12px ${suffix ? 36 : 14}px 12px ${prefix ? 32 : 14}px`, fontFamily: "Crimson Pro, serif", fontSize: 21, color: "#f0e8d8", outline: "none", caretColor: "#f0e8d8"

       }} />
        {suffix && <span style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", color: C.dust, fontFamily: "Space Mono, monospace", fontSize: 11 }}>{suffix}</span>}
      </div>
    </div>
  );
}

// ─── EARTH IMAGE (base64 stub — replace with full base64 in production) ──────
const EARTH_IMAGE_BASE64 = "/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAUDBAQEAwUEBAQFBQUGBwwIBwcHBw8LCwkMEQ8SEhEPERETFhwXExQaFRERGCEYGh0dHx8fExciJCIeJBweHx7/wAARCAGQASwDASIAAhEBAxEB/8QAGwABAAIDAQEAAAAAAAAAAAAAAAECAwQFBgf/xAAzEAABBAEDAwMDAwQCAwEAAAABAAIDERIhBBNBUTFhcQUUgSKRMlKhI0LB0eHw8WL/xAAYAQEBAQEBAAAAAAAAAAAAAAAAAQIDBP/EAB4RAQEBAQADAQEBAAAAAAAAAAABERIhAjFBUf/aAAwDAQACEQMRAD8A+mREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERARFzuJcap3r4lyfTmuqIi6oREQEREBERAREQEREBERARF8kBJPQICIiAiIgIiICIiAiIgIiICIiAiIgIiICIiAiIgIiICIiAiIgIiICIiAiIgIiICIiAiIgIiICIiAiIgIiICIiAiIgIiICIiAiIgIiICLnniGWXGWmGLYNqkLR+K8RiAkRPMhH3F2v8ABavw7jfFLnuIcXODQGjWglKKIiKgiIgIiICIiAiIgIiICIiAiIgIiICIiAiIgIiICIiAiIgIiICIiAiIgIiICIiAiIgIiICIiAiIgIiICIiAiIgIiICIiAiIgIiIPJ4r4ZlmeMc9ha0/iu01r5r5bI1ZNyXADoAK+h8TiZJJHFrWNrpb15K+cjMboyK1vRH9FaOeqUbgFrABdmkF2tqXggge1b7xr8bpWFrgGlo6Hss3MOOkxgVob7G3AdbK1HhvFpCIpnxSH+VzCAfxCsjRCf8AJMcQ4xFPGh52lrgf8XY4dxx7cMZMOob+j4K8rw2MzwSsxpWNkb0Cdj5LtcCyvgs8hH3NbbfqLVFnROiLtS5Z7K8nNF3HHFdv2UD+P8ApzgD7WfkVkHjXFOhbsyBl9Wk0SPwUFn3ERTxixJE5vsHC1JEBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAX5heTi+LeEfAOKyv5rvW0AfyXS8R8UfiwGRRvkFE9kHQC+iVx4YWiKMv1e7rf/AIuf1E9HXnERXBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERARZ4fkRpb1a0H+ZQeLgv2bT3OgfYbK1Cke8jqbqvVbnhHRg9Xz7VW3dPaL8/RQWc0f2cOz2+zuvh0TIiDSxjRfbS6XyPb8HXqBj8jaK3/IFxJrMY+CflKDKKupsBf+KZZnfljWj1sdqFr8P8A0r9FaKCiL6tnD/4yrP8AN4/5c/B/X/K9RoIiICIiAiIgIiICIiAiIgIiICIiAiIgIiICIiAiIgIiICIiAiIgIiICIiAiIgIiICIiAiIgIiICIiAiIgIiICIiAiIgIiICIiAiIgIiICIiAiIgIiICIiAiIgIiICIiAiIgIiICIiAiIgIiICIiAiIgIiICIiAiIgIiIP/2Q==";

// ─── LOGIN SCREEN ────────────────────────────────────────────
function LoginScreen() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isNewUser, setIsNewUser] = useState(false);

  const handleGoogleLogin = async () => {
    setLoading(true); setError("");
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (err) {
      if (err.code === "auth/popup-blocked") {
        try {
          await signInWithRedirect(auth, googleProvider);
        } catch (redirectErr) {
          setError("Google sign-in failed. Try email instead.");
        }
      } else if (err.code === "auth/cancelled-popup-request" || err.code === "auth/popup-closed-by-user") {
        setError("");
      } else {
        setError("Google sign-in failed. Try email instead.");
      }
    }
    setLoading(false);
  };

  const handleEmailAuth = async () => {
    if (!email || !password) { setError("Please enter email and password."); return; }
    setLoading(true); setError("");
    try {
      if (isNewUser) {
        const { createUserWithEmailAndPassword } = await import("firebase/auth");
        const result = await createUserWithEmailAndPassword(auth, email, password);
await sendEmailVerification(result.user);
setError("✅ Account created! Check your email to verify your account.");
      } else {
        const { signInWithEmailAndPassword } = await import("firebase/auth");
        await signInWithEmailAndPassword(auth, email, password);
      }
    } catch (err) {
      if (err.code === "auth/user-not-found" || err.code === "auth/invalid-credential") setError("No account found. Click Sign Up.");
      else if (err.code === "auth/wrong-password") setError("Incorrect password.");
      else if (err.code === "auth/email-already-in-use") setError("Email already in use.");
      else if (err.code === "auth/weak-password") setError("Password must be at least 6 characters.");
      else setError("Error: " + err.message);
    }
    setLoading(false);
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: `linear-gradient(160deg, ${C.pine} 0%, ${C.bark} 100%)`, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse at 70% 30%, rgba(245,166,35,0.08) 0%, transparent 60%)", pointerEvents: "none" }} />
      <div style={{ width: 80, height: 80, borderRadius: 24, background: `linear-gradient(135deg, ${C.sunrise}, ${C.canyon})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 36, marginBottom: 16, boxShadow: `0 8px 32px rgba(245,166,35,0.4)` }}>🔥</div>
      <div style={{ fontFamily: "Playfair Display, serif", fontSize: 36, fontWeight: 900, color: "#fff", marginBottom: 4 }}>Road<span style={{ color: C.sunrise }}>Burn</span><span style={{ fontFamily: "Space Mono, monospace", fontSize: 28 }}>AI</span></div>
      <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 16, color: "rgba(255,255,255,0.55)", marginBottom: 40 }}>The Complete Nomadic Life App</div>
      <div style={{ width: "100%", maxWidth: 340 }}>
        <input type="email" placeholder="Email address" value={email} onChange={e => setEmail(e.target.value)} style={{ width: "100%", padding: "14px 16px", borderRadius: 12, border: "1.5px solid rgba(255,255,255,0.2)", background: "rgba(255,255,255,0.1)", color: "#fff", fontFamily: "Space Mono, monospace", fontSize: 12, marginBottom: 10, outline: "none", boxSizing: "border-box" }} />
        <input type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key === "Enter" && handleEmailAuth()} style={{ width: "100%", padding: "14px 16px", borderRadius: 12, border: "1.5px solid rgba(255,255,255,0.2)", background: "rgba(255,255,255,0.1)", color: "#fff", fontFamily: "Space Mono, monospace", fontSize: 12, marginBottom: 16, outline: "none", boxSizing: "border-box" }} />
        <button onClick={handleEmailAuth} disabled={loading} style={{ width: "100%", padding: "14px", borderRadius: 12, border: "none", background: `linear-gradient(135deg, ${C.sunrise}, ${C.canyon})`, color: "#fff", fontFamily: "Space Mono, monospace", fontSize: 13, fontWeight: 700, letterSpacing: 1, cursor: "pointer", marginBottom: 12 }}>
          {loading ? "..." : isNewUser ? "CREATE ACCOUNT" : "SIGN IN"}
        </button>
        <button onClick={handleGoogleLogin} disabled={loading} style={{ width: "100%", padding: "13px", borderRadius: 12, border: "1.5px solid rgba(255,255,255,0.2)", background: "rgba(255,255,255,0.08)", color: "#fff", fontFamily: "Space Mono, monospace", fontSize: 12, fontWeight: 700, cursor: "pointer", marginBottom: 10, display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
          <svg width="18" height="18" viewBox="0 0 48 48">
            <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12s5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24s8.955,20,20,20s20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"/>
            <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"/>
            <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"/>
            <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571l6.19,5.238C42.021,35.591,44,30.036,44,24C44,22.659,43.862,21.35,43.611,20.083z"/>
          </svg>
          CONTINUE WITH GOOGLE
        </button>
        <button onClick={() => { setIsNewUser(!isNewUser); setError(""); }} style={{ width: "100%", padding: "12px", borderRadius: 12, border: "1.5px solid rgba(255,255,255,0.2)", background: "transparent", color: "rgba(255,255,255,0.7)", fontFamily: "Space Mono, monospace", fontSize: 11, cursor: "pointer" }}>
          {isNewUser ? "ALREADY HAVE AN ACCOUNT? SIGN IN" : "NEW HERE? CREATE ACCOUNT"}
        </button>
        {error && <div style={{ marginTop: 16, fontFamily: "Crimson Pro, serif", fontSize: 13, color: "#ff9999", textAlign: "center" }}>{error}</div>}
      </div>
      <div style={{ marginTop: 32, fontFamily: "Crimson Pro, serif", fontSize: 12, color: "rgba(255,255,255,0.35)" }}>Your data is private and only visible to you.</div>
    </div>
  );
}

// ─── LAUNCH SCREEN ───────────────────────────────────────────
function LaunchScreen({ onComplete }) {
  const [phase, setPhase] = useState(0);
  const [pingPos, setPingPos] = useState({ x: 41, y: 37 });

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        pos => setPingPos(gpsToPercent(pos.coords.latitude, pos.coords.longitude)),
        () => null,
        { timeout: 8000, maximumAge: 600000 }
      );
    }
  }, []);

  useEffect(() => {
    const t1 = setTimeout(() => setPhase(1), 800);
const t2 = setTimeout(() => setPhase(2), 5200);
const t3 = setTimeout(() => onComplete(), 6000);
return () => [t1, t2, t3].forEach(clearTimeout);
  }, []);

  // Maps real GPS coordinates to % position on this specific globe image.
  // Globe center ≈ 15°N, 90°W. Visible range roughly: lat 70N–40S, lng 150W–30W
  function gpsToPercent(lat, lng) {
    const LNG_LEFT = -150, LNG_RIGHT = -30;
    const LAT_TOP = 70, LAT_BOT = -40;
    // Globe is circular — apply slight spherical correction
    const rawX = (lng - LNG_LEFT) / (LNG_RIGHT - LNG_LEFT) * 100;
    const rawY = (LAT_TOP - lat) / (LAT_TOP - LAT_BOT) * 100;
    // Clamp to safe area on the globe (avoid ocean edges)
    return {
      x: Math.min(Math.max(rawX, 8), 88),
      y: Math.min(Math.max(rawY, 8), 88),
    };
  }

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 999,
      background: "#000",
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
    }}>
      {/* Smooth black fade-in overlay */}
      <div style={{
        position: "absolute", inset: 0, zIndex: 10,
        background: "#000",
        opacity: phase === 0 ? 1 : 0,
        transition: "opacity 1.2s ease",
        pointerEvents: "none",
      }} />
      {/* Globe image */}
      <div style={{ position: "relative", width: "100%", maxWidth: 430, height: "100vh" }}>
        <img
          src={earthImage}
          alt="Earth from space"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "center",
          }}
        />

        {/* GPS Ping */}
        {phase >= 1 && (
          <div style={{
            position: "absolute",
            left: `${pingPos.x}%`,
            top: `${pingPos.y}%`,
            transform: "translate(-50%, -50%)",
            pointerEvents: "none",
          }}>
            {/* Three rings — each appears one phase at a time */}
            {phase >= 1 && [0, 1, 2].map(pulse => (
              [0, 1, 2].map(ring => (
                <div key={`${pulse}-${ring}`} style={{
                  position: "absolute",
                  borderRadius: "50%",
                  border: "2px solid #3af0c8",
                  width: 8 + ring * 14,
                  height: 8 + ring * 14,
                  top: "50%", left: "50%",
                  transform: "translate(-50%, -50%)",
                  animation: `radarPulse 1.2s ease-out infinite`,
                  animationDelay: `${pulse * 0.4 + ring * 0.12}s`,
                  opacity: 0,
                }} />
              ))
            ))}
            {/* Center dot */}
            <div style={{
              position: "absolute",
              width: 8, height: 8,
              borderRadius: "50%",
              background: "#3af0c8",
              top: "50%", left: "50%",
              transform: "translate(-50%, -50%)",
              boxShadow: "0 0 10px #3af0c8, 0 0 20px rgba(58,240,200,0.6)",
            }} />
          </div>
        )}
      </div>

      {/* Skip / Enter button */}
      <button onClick={onComplete} style={{
        position: "absolute",
        bottom: 40, left: "50%", transform: "translateX(-50%)",
        background: "rgba(58,240,200,0.12)",
        border: "1.5px solid rgba(58,240,200,0.5)",
        borderRadius: 12, padding: "12px 36px",
        color: "#3af0c8",
        fontFamily: "Space Mono, monospace",
        fontSize: 11, fontWeight: 700, letterSpacing: 1.5,
        cursor: "pointer",
      }}>
        {phase >= 3 ? "ENTER APP →" : "SKIP →"}
      </button>
    </div>
  );
}
// ─── FUEL TAB ────────────────────────────────────────────────
function FuelTab({ fillUps, loading, user, vehicles, activeVehicleId, onVehicleChange }) {
  const [odo, setOdo] = useState("");
  const [gal, setGal] = useState("");
  const [ppg, setPpg] = useState("");
  const [station, setStation] = useState("");
  const [loc, setLoc] = useState("");
  const [notes, setNotes] = useState("");
  const [fullFillUp, setFullFillUp] = useState(true);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState("log");
  const [dieselPrices, setDieselPrices] = useState(null);
  const [pricesLoading, setPricesLoading] = useState(false);
  const [pricesError, setPricesError] = useState(false);

  const defaultVehicleId = vehicles[0]?.id;
  const activeVehicle = vehicles.find(v => v.id === activeVehicleId) || vehicles[0];
  const vehicleFillUps = vehicles.length === 0 ? fillUps : fillUps.filter(f => (f.vehicleId || defaultVehicleId) === activeVehicleId);

  const fetchDieselPrices = async () => {
    setPricesLoading(true); setPricesError(false);
    try {
      const res = await fetch("https://roadburnai-proxy.skoolie309.workers.dev/eia");
      const json = await res.json();
      const data = json.response?.data || [];
      const latest = {};
      data.forEach(d => {
        if (!latest[d.duoarea]) latest[d.duoarea] = d;
      });
      setDieselPrices(latest);
    } catch { setPricesError(true); }
    setPricesLoading(false);
  };
  const [fromId, setFromId] = useState("");
  const [toId, setToId] = useState("");

  const preview = gal && ppg ? (parseFloat(gal) * parseFloat(ppg)).toFixed(2) : null;
  const handleGPS = () => { setGpsLoading(true); setTimeout(() => { setLoc("Flagstaff, AZ"); setGpsLoading(false); }, 1200); };

  const handleSave = async () => {
    if (!odo || !gal || !ppg) return;
    setSaving(true);
    try {
      const newFillUp = { uid: user.uid, vehicleId: activeVehicleId || null, date: new Date().toISOString().split("T")[0], odometer: parseInt(odo), gallons: parseFloat(gal), ppg: parseFloat(ppg), station: station || "Unknown", location: loc || "Unknown", notes, fullFillUp, createdAt: new Date() };
      if (vehicleFillUps.length > 0 && fullFillUp) {
        const prevFull = vehicleFillUps.find(f => f.fullFillUp !== false);
        if (prevFull) {
          const miles = parseInt(odo) - prevFull.odometer;
          if (miles > 0) {
            const gallonsSinceLastFull = vehicleFillUps.filter(f => f.odometer > prevFull.odometer).reduce((sum, f) => sum + f.gallons, 0);
            const totalGallons = gallonsSinceLastFull + parseFloat(gal);
            newFillUp.miles = miles;
            newFillUp.mpg = (miles / totalGallons).toFixed(1);
          }
        }
      }
      await addDoc(collection(db, "fillups"), newFillUp);
      setOdo(""); setGal(""); setPpg(""); setStation(""); setLoc(""); setNotes("");
      setSaved(true); setTimeout(() => setSaved(false), 2000);
    } catch (err) { console.error("Error saving fill-up:", err); alert("Error saving — check console"); }
    setSaving(false);
  };

  useEffect(() => {
    if (vehicleFillUps.length >= 2) { setFromId(vehicleFillUps[0].id); setToId(vehicleFillUps[1].id); }
    else { setFromId(""); setToId(""); }
  }, [activeVehicleId, vehicleFillUps.length]);

  const getStats = () => {
    if (!fromId || !toId || fromId === toId) return null;
    const f1 = vehicleFillUps.find(f => f.id === fromId); const f2 = vehicleFillUps.find(f => f.id === toId);
    if (!f1 || !f2) return null;
    const higher = f1.odometer > f2.odometer ? f1 : f2; const lower = f1.odometer > f2.odometer ? f2 : f1;
    const totalMiles = higher.odometer - lower.odometer; if (totalMiles <= 0) return null;
    const inRange = vehicleFillUps.filter(f => f.odometer >= lower.odometer && f.odometer <= higher.odometer);
    const totalGal = inRange.reduce((a, f) => a + f.gallons, 0); const totalSpent = inRange.reduce((a, f) => a + (f.gallons * f.ppg), 0);
    if (totalGal <= 0) return null;
    return { totalMiles, totalGal, totalSpent, avgMPG: totalMiles / totalGal, avgPrice: totalSpent / totalGal, cpm: totalSpent / totalMiles };
  };
  const stats = getStats();

  return (
    <div style={{ padding: "20px 16px" }}>
      {vehicles.length > 1 && (
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", fontFamily: "Space Mono, monospace", fontSize: 9, fontWeight: 700, color: C.dust, letterSpacing: 1.2, marginBottom: 6, textTransform: "uppercase" }}>🚐 Vehicle</label>
          <select value={activeVehicleId || ""} onChange={e => onVehicleChange(e.target.value)} style={{ width: "100%", background: "#231408", border: `1.5px solid ${C.sage}`, borderRadius: 10, padding: "12px 14px", fontFamily: "Crimson Pro, serif", fontSize: 15, color: "#f0e8d8", outline: "none" }}>
            {vehicles.map(v => <option key={v.id} value={v.id}>{v.name}{(v.rigYear || v.rigMake || v.rigModel) ? ` — ${[v.rigYear, v.rigMake, v.rigModel].filter(Boolean).join(" ")}` : ""}</option>)}
          </select>
        </div>
      )}
      <div style={{ display: "flex", gap: 6, marginBottom: 20 }}>
        {[["log", "⛽ Log"], ["history", "🗂 History"], ["analyze", "📊 Analyze"], ["prices", "💰 Prices"]].map(([k, l]) => (
          <button key={k} onClick={() => setView(k)} style={{ flex: 1, padding: "9px 4px", background: view === k ? `linear-gradient(135deg, ${C.sunrise}, ${C.canyon})` : C.cream, color: view === k ? "#fff" : C.bark, border: `1.5px solid ${view === k ? C.sunrise : C.parchmentDeep}`, borderRadius: 10, fontFamily: "Space Mono, monospace", fontSize: 9, fontWeight: 700, letterSpacing: 0.5, cursor: "pointer" }}>{l}</button>
        ))}
      </div>

      {view === "log" && <>
        <SectionHeader icon="🛢" title={vehicles.length > 1 ? `Log a Fill-Up — ${activeVehicle?.name || ""}` : "Log a Fill-Up"} />
        <InputField label="Odometer" value={odo} onChange={setOdo} placeholder="42,810" type="number" suffix="mi" />
        <div style={{ display: "flex", gap: 10 }}>
          <InputField label="Gallons" value={gal} onChange={setGal} placeholder="28.4" type="number" suffix="gal" half />
          <InputField label="Price/Gal" value={ppg} onChange={setPpg} placeholder="3.89" type="number" prefix="$" half />
        </div>
        <InputField label="Station" value={station} onChange={setStation} placeholder="Pilot, Flying J, Love's..." />
        <div style={{ marginBottom: 14 }}>
          <label style={{ display: "block", fontFamily: "Space Mono, monospace", fontSize: 9, fontWeight: 700, color: C.dust, letterSpacing: 1.2, marginBottom: 6, textTransform: "uppercase" }}>Location</label>
          <div style={{ display: "flex", gap: 8 }}>
            <input value={loc} onChange={e => setLoc(e.target.value)} placeholder="City, State" style={{ flex: 1, background: "#231408", border: `1.5px solid ${C.parchmentDeep}`, borderRadius: 10, padding: "12px 14px", fontFamily: "Crimson Pro, serif", fontSize: 15, color: C.dust, outline: "none" }} />
            <button onClick={handleGPS} style={{ background: gpsLoading ? C.parchmentDark : C.sageSoft, border: `1.5px solid ${C.sageLight}`, borderRadius: 10, padding: "0 14px", cursor: "pointer", fontSize: 16 }}>{gpsLoading ? "⏳" : "📍"}</button>
          </div>
        </div>
        <div style={{ marginBottom: 14, display: "flex", alignItems: "center", justifyContent: "space-between", background: fullFillUp ? C.sageSoft : C.sunriseSoft, border: `1.5px solid ${fullFillUp ? C.sageLight : C.sunrise}`, borderRadius: 12, padding: "10px 14px" }}>
          <div>
            <div style={{ fontFamily: "Space Mono, monospace", fontSize: 10, fontWeight: 700, color: fullFillUp ? C.sageDark : C.canyon, letterSpacing: 0.5 }}>{fullFillUp ? "✓ FULL FILL-UP" : "PARTIAL FILL-UP"}</div>
            <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 11, color: C.dirt, marginTop: 2 }}>{fullFillUp ? "MPG will be calculated" : "MPG will not be calculated"}</div>
          </div>
          <button onClick={() => setFullFillUp(!fullFillUp)} style={{ width: 44, height: 26, borderRadius: 13, border: "none", cursor: "pointer", background: fullFillUp ? C.sage : C.parchmentDeep, position: "relative", transition: "background 0.2s" }}>
            <div style={{ width: 20, height: 20, borderRadius: "50%", background: "#fff", position: "absolute", top: 3, left: fullFillUp ? 21 : 3, transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }} />
          </button>
        </div>
        <InputField label="Notes" value={notes} onChange={setNotes} placeholder="Road conditions, wait time..." />
        {preview && (
          <div style={{ background: C.sunriseSoft, border: "1.5px solid #e8d4a0", borderRadius: 12, padding: "12px 16px", marginBottom: 14, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ fontFamily: "Space Mono, monospace", fontSize: 9, color: C.dirt, letterSpacing: 1 }}>ESTIMATED TOTAL</div>
              <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 12, color: C.dirtLight, fontStyle: "italic" }}>{gal} gal × ${ppg}</div>
            </div>
            <div style={{ fontFamily: "Playfair Display, serif", fontSize: 28, fontWeight: 900, color: C.canyon }}>${preview}</div>
          </div>
        )}
        <Btn onClick={handleSave} style={{ background: saved ? `linear-gradient(135deg, ${C.green}, #3a7a3a)` : `linear-gradient(135deg, ${C.sunrise}, ${C.canyon})` }}>
          {saved ? "✓ SAVED TO FIREBASE!" : saving ? "SAVING..." : "SAVE FILL-UP"}
        </Btn>
      </>}

      {view === "history" && <>
        <SectionHeader icon="🗂" title={vehicles.length > 1 ? `Fill-Up History — ${activeVehicle?.name || ""}` : "Fill-Up History"} />
        {loading && <div style={{ textAlign: "center", padding: "40px 20px", color: C.dust, fontFamily: "Crimson Pro, serif", fontStyle: "italic" }}>Loading your fill-ups...</div>}
        {!loading && vehicleFillUps.length === 0 && <div style={{ textAlign: "center", padding: "40px 20px", color: C.dust, fontFamily: "Crimson Pro, serif", fontStyle: "italic" }}>No fill-ups yet for {activeVehicle?.name || "this vehicle"}. Log your first one!</div>}
        {vehicleFillUps.map(f => (
          <Card key={f.id} accent={f.mpg && parseFloat(f.mpg) < 7 ? C.red : C.sage}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
              <div>
                <div style={{ fontFamily: "Playfair Display, serif", fontSize: 15, fontWeight: 700, color: C.parchment }}>{f.station}</div>
                <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 12, color: C.dirt, fontStyle: "italic" }}>{f.location} · {f.date}</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontFamily: "Playfair Display, serif", fontSize: 18, fontWeight: 900, color: C.canyon }}>${(f.gallons * f.ppg).toFixed(2)}</div>
                <div style={{ fontFamily: "Space Mono, monospace", fontSize: 9, color: C.dust }}>{f.gallons}g @ ${f.ppg}</div>
              </div>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
              <span style={{ fontFamily: "Space Mono, monospace", fontSize: 10, color: C.dust }}>ODO: {f.odometer?.toLocaleString()}</span>
              {f.miles && <><span style={{ color: C.parchmentDeep }}>·</span><span style={{ fontFamily: "Space Mono, monospace", fontSize: 10, color: C.dust }}>{f.miles} mi</span></>}
              {f.fullFillUp === false && <><span style={{ color: C.parchmentDeep }}>·</span><span style={{ fontFamily: "Space Mono, monospace", fontSize: 9, color: C.canyon, background: C.canyonSoft, borderRadius: 4, padding: "2px 6px" }}>PARTIAL</span></>}
              {f.mpg && f.fullFillUp !== false && <><span style={{ color: C.parchmentDeep }}>·</span><MPGBadge mpg={f.mpg} /></>}
              {f.miles && <span style={{ fontFamily: "Space Mono, monospace", fontSize: 10, color: C.dirtLight }}>${(f.gallons * f.ppg / f.miles).toFixed(3)}/mi</span>}
            </div>
            {f.notes && <div style={{ marginTop: 8, fontFamily: "Crimson Pro, serif", fontSize: 12, color: C.dirt, fontStyle: "italic" }}>{f.notes}</div>}
          </Card>
        ))}
      </>}

      {view === "prices" && <>
        <SectionHeader icon="⛽" title="Diesel Prices" />
        <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 13, color: C.dirt, fontStyle: "italic", marginBottom: 16 }}>
          Live national & regional diesel prices — updated every Monday
        </div>
        {!dieselPrices && !pricesLoading && (
          <Btn onClick={fetchDieselPrices}>⛽ LOAD LIVE PRICES</Btn>
        )}
        {pricesLoading && (
          <div style={{ textAlign: "center", padding: "40px 20px", color: C.dust, fontFamily: "Crimson Pro, serif", fontStyle: "italic" }}>Fetching live prices...</div>
        )}
        {pricesError && (
          <div style={{ textAlign: "center", padding: "20px", color: C.red, fontFamily: "Crimson Pro, serif" }}>Couldn't load prices. Try again.</div>
        )}
        {dieselPrices && (() => {
          const regions = [
            { key: "NUS", label: "🇺🇸 U.S. National", highlight: true },
            { key: "R30", label: "🤠 Gulf Coast" },
            { key: "R20", label: "🌽 Midwest" },
            { key: "R40", label: "⛰️ Rocky Mountain" },
            { key: "R10", label: "🗽 East Coast" },
            { key: "R1Z", label: "🌴 Lower Atlantic" },
            { key: "R50", label: "🌊 West Coast" },
            { key: "SCA", label: "🎬 California" },
          ];
          return (
            <>
              {regions.map(r => {
                const d = dieselPrices[r.key];
                if (!d) return null;
                const price = parseFloat(d.value);
                const color = price < 5.0 ? C.sageDark : price < 5.5 ? C.canyon : C.red;
                return (
                  <div key={r.key} style={{ background: r.highlight ? `linear-gradient(135deg, ${C.pine}, ${C.sageDark})` : "#231408", border: `1.5px solid ${r.highlight ? C.sage : C.parchmentDeep}`, borderRadius: 14, padding: "14px 16px", marginBottom: 10, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <div style={{ fontFamily: "Playfair Display, serif", fontSize: 15, fontWeight: 700, color: C.parchment }}>{r.label}</div>
                      <div style={{ fontFamily: "Space Mono, monospace", fontSize: 9, color: C.dust, marginTop: 4 }}>Week of {d.period}</div>
                    </div>
                    <div style={{ fontFamily: "Playfair Display, serif", fontSize: 26, fontWeight: 900, color: r.highlight ? "#fff" : color }}>${price.toFixed(2)}</div>
                  </div>
                );
              })}
              <div style={{ background: C.sunriseSoft, border: `1.5px solid ${C.canyonLight}`, borderRadius: 12, padding: "12px 16px", marginTop: 8 }}>
                <div style={{ fontFamily: "Space Mono, monospace", fontSize: 9, color: C.canyon, fontWeight: 700, marginBottom: 4 }}>💡 NOMAD TIP</div>
                <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 13, color: C.bark, lineHeight: 1.5 }}>Gulf Coast and Lower Atlantic consistently have the cheapest diesel. Plan your route through PADD 3 when possible to save on fuel.</div>
              </div>
              <Btn variant="sage" style={{ marginTop: 8 }} onClick={fetchDieselPrices}>🔄 REFRESH PRICES</Btn>
            </>
          );
        })()}
      </>}

      {view === "analyze" && <>
        <SectionHeader icon="📊" title={vehicles.length > 1 ? `Analyze Range — ${activeVehicle?.name || ""}` : "Analyze Range"} />
        {vehicleFillUps.length < 2 ? (
          <div style={{ textAlign: "center", padding: "40px 20px", color: C.dust, fontFamily: "Crimson Pro, serif", fontStyle: "italic" }}>Log at least 2 fill-ups for {activeVehicle?.name || "this vehicle"} to analyze.</div>
        ) : <>
          <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
            {[["From", fromId, setFromId], ["To", toId, setToId]].map(([label, val, setter]) => (
              <div key={label} style={{ flex: 1 }}>
                <label style={{ display: "block", fontFamily: "Space Mono, monospace", fontSize: 9, fontWeight: 700, color: C.dust, letterSpacing: 1.2, marginBottom: 6, textTransform: "uppercase" }}>{label}</label>
                <select value={val} onChange={e => setter(e.target.value)} style={{ width: "100%", background: "#231408", border: `1.5px solid ${C.parchmentDeep}`, borderRadius: 10, padding: "10px 12px", fontFamily: "Crimson Pro, serif", fontSize: 13, color: "#f0e8d8", outline: "none" }}>
                  {vehicleFillUps.map(f => <option key={f.id} value={f.id}>{f.location} ({f.date})</option>)}
                </select>
              </div>
            ))}
          </div>
          {stats ? (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
                {[["Total Miles", stats.totalMiles.toLocaleString(), "mi driven"], ["Total Spent", `$${stats.totalSpent.toFixed(2)}`, "fuel cost"], ["Avg MPG", stats.avgMPG.toFixed(1), "miles per gallon"], ["Cost/Mile", `$${stats.cpm.toFixed(3)}`, "per mile"]].map(([l, v, s]) => (
                  <div key={l} style={{ background: "#231408", border: `1.5px solid ${C.parchmentDeep}`, borderRadius: 12, padding: "14px 16px" }}>
                    <div style={{ fontFamily: "Space Mono, monospace", fontSize: 8, fontWeight: 700, color: C.dust, letterSpacing: 1, textTransform: "uppercase" }}>{l}</div>
                    <div style={{ fontFamily: "Playfair Display, serif", fontSize: 22, fontWeight: 900, color: C.parchment, margin: "4px 0" }}>{v}</div>
                    <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 11, color: C.dirt, fontStyle: "italic" }}>{s}</div>
                  </div>
                ))}
              </div>
              <div style={{ background: C.sunriseSoft, border: "1.5px solid #e8d4a0", borderRadius: 12, padding: "14px 16px", fontFamily: "Crimson Pro, serif", fontSize: 14, color: C.parchment, lineHeight: 1.5 }}>
                💡 At this rate, a 500 mile trip costs approximately <strong style={{ color: C.canyon }}>${(stats.cpm * 500).toFixed(2)}</strong>
              </div>
            </>
          ) : <div style={{ textAlign: "center", padding: "40px 20px", color: C.dust, fontFamily: "Crimson Pro, serif", fontStyle: "italic" }}>Select two different fill-ups to analyze.</div>}
        </>}
      </>}
    </div>
  );
}
// ─── STAR RATING ─────────────────────────────────────────────
function StarRating({ rating, reviews }) {
  const full = Math.floor(rating);
  const half = rating % 1 >= 0.5;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
      {[1,2,3,4,5].map(i => (
        <span key={i} style={{ color: i <= full ? C.sunrise : (i === full + 1 && half) ? C.canyonLight : C.parchmentDeep, fontSize: 11 }}>★</span>
      ))}
      <span style={{ fontFamily: "Space Mono, monospace", fontSize: 9, color: C.dust, marginLeft: 2 }}>{rating.toFixed(1)} ({reviews})</span>
    </div>
  );
}
// ─── HAVERSINE DISTANCE ──────────────────────────────────────
function getDistanceMiles(lat1, lon1, lat2, lon2) {
  const R = 3958.8;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
    Math.cos((lat2 * Math.PI) / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
// ─── WEATHER TAB ─────────────────────────────────────────────
const YOUTUBE_API_KEY = "AIzaSyBWmjywTWqU1SqRiU7HwqTUzHgSFe9RKbU";
const RYAN_HALL_CHANNEL_ID = "UCgFXm4TI8htWmCyJ6cVPG_A";

function WeatherTab() {
  const [forecast, setForecast] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loadingWeather, setLoadingWeather] = useState(false);
  const [weatherError, setWeatherError] = useState("");
  const [isRyanLive, setIsRyanLive] = useState(false);
  const [ryanRecentVideo, setRyanRecentVideo] = useState(null);
  const [locationName, setLocationName] = useState("");

  useEffect(() => {
    fetchWeather();
  }, []);

  const fetchWeather = async () => {
    setLoadingWeather(true);
    setWeatherError("");
    try {
      const pos = await new Promise((res, rej) =>
        navigator.geolocation.getCurrentPosition(res, rej, { timeout: 10000 })
      );
      const { latitude: lat, longitude: lon } = pos.coords;

      // NWS point lookup
      const ptRes = await fetch(`https://api.weather.gov/points/${lat.toFixed(4)},${lon.toFixed(4)}`);
      const ptData = await ptRes.json();
      const { forecast: forecastUrl, forecastHourly, relativeLocation } = ptData.properties;
      setLocationName(`${relativeLocation.properties.city}, ${relativeLocation.properties.state}`);

      // Forecast + alerts in parallel
      const [fcRes, alRes] = await Promise.all([
        fetch(forecastUrl),
        fetch(`https://api.weather.gov/alerts/active?point=${lat.toFixed(4)},${lon.toFixed(4)}`)
      ]);
      const fcData = await fcRes.json();
      const alData = await alRes.json();

      setForecast(fcData.properties.periods.slice(0, 14));
      setAlerts(alData.features || []);

      // Check Ryan Hall live only if alerts exist
      if (alData.features && alData.features.length > 0) {
        checkRyanHallLive();
      }
    } catch (err) {
      setWeatherError("Couldn't load weather. Check location permissions.");
      console.error(err);
    }
    setLoadingWeather(false);
  };

  const checkRyanHallLive = async () => {
    try {
      // Check if live first
      const liveRes = await fetch(
        `https://www.googleapis.com/youtube/v3/search?part=snippet&channelId=${RYAN_HALL_CHANNEL_ID}&eventType=live&type=video&key=${YOUTUBE_API_KEY}`
      );
      const liveData = await liveRes.json();
      if (liveData.items && liveData.items.length > 0) {
        setIsRyanLive(true);
        setRyanRecentVideo(null);
        return;
      }
      // If not live, check for video posted in last 24 hours
      const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      const recentRes = await fetch(
        `https://www.googleapis.com/youtube/v3/search?part=snippet&channelId=${RYAN_HALL_CHANNEL_ID}&type=video&order=date&maxResults=1&publishedAfter=${since}&key=${YOUTUBE_API_KEY}`
      );
      const recentData = await recentRes.json();
      if (recentData.items && recentData.items.length > 0) {
        const video = recentData.items[0];
        setRyanRecentVideo({
          videoId: video.id.videoId,
          title: video.snippet.title,
          publishedAt: video.snippet.publishedAt,
        });
      } else {
        setRyanRecentVideo(null);
      }
    } catch (err) {
      console.error("YouTube check failed:", err);
    }
  };

  const getWeatherEmoji = (shortForecast) => {
    const f = shortForecast?.toLowerCase() || "";
    if (f.includes("tornado") || f.includes("severe")) return "🌪️";
    if (f.includes("thunder")) return "⛈️";
    if (f.includes("snow") || f.includes("blizzard")) return "❄️";
    if (f.includes("rain") || f.includes("shower")) return "🌧️";
    if (f.includes("cloud") || f.includes("overcast")) return "☁️";
    if (f.includes("partly")) return "⛅";
    if (f.includes("sunny") || f.includes("clear")) return "☀️";
    if (f.includes("wind")) return "💨";
    if (f.includes("fog")) return "🌫️";
    return "🌤️";
  };

  const getAlertColor = (severity) => {
    if (severity === "Extreme") return { bg: "#fce8e8", border: C.red, text: C.red };
    if (severity === "Severe") return { bg: "#fff0e0", border: C.sunrise, text: C.sunriseDeep };
    return { bg: C.skySoft, border: C.sky, text: C.sageDark };
  };

  const dayPeriods = forecast ? forecast.filter(p => p.isDaytime) : [];
  const nightPeriods = forecast ? forecast.filter(p => !p.isDaytime) : [];
  return (
    <div style={{ padding: "20px 16px" }}>
      <SectionHeader icon="🌤️" title="Weather" />

      {/* Location + Refresh */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
        <div style={{ fontFamily:"Crimson Pro,serif", fontSize:13, color:C.dirt, fontStyle:"italic" }}>
          {locationName || "Detecting location..."}
        </div>
        <button onClick={fetchWeather} style={{ background:"none", border:`1px solid ${C.parchmentDeep}`, borderRadius:8, padding:"5px 10px", fontFamily:"Space Mono,monospace", fontSize:9, color:C.dust, cursor:"pointer" }}>
          ↻ REFRESH
        </button>
      </div>

      {/* Loading */}
      {loadingWeather && (
        <div style={{ textAlign:"center", padding:"40px 20px", color:C.dust, fontFamily:"Crimson Pro,serif", fontSize:14 }}>
          Fetching weather...
        </div>
      )}

      {/* Error */}
      {weatherError && (
        <div style={{ background:C.redSoft, border:`1.5px solid ${C.red}`, borderRadius:12, padding:16, marginBottom:16, fontFamily:"Crimson Pro,serif", fontSize:13, color:C.red }}>
          {weatherError}
        </div>
      )}

      {/* Active Alerts */}
      {alerts.length > 0 && (
        <div style={{ marginBottom:16 }}>
          {alerts.slice(0,3).map((a, i) => {
            const props = a.properties;
            const colors = getAlertColor(props.severity);
            return (
              <div key={i} style={{ background:colors.bg, border:`1.5px solid ${colors.border}`, borderRadius:12, padding:14, marginBottom:10 }}>
                <div style={{ fontFamily:"Space Mono,monospace", fontSize:9, fontWeight:700, color:colors.text, letterSpacing:1, marginBottom:4 }}>
                  ⚠️ {props.severity?.toUpperCase()} ALERT
                </div>
                <div style={{ fontFamily:"Playfair Display,serif", fontSize:14, fontWeight:700, color:C.bark, marginBottom:4 }}>
                  {props.event}
                </div>
                <div style={{ fontFamily:"Crimson Pro,serif", fontSize:12, color:C.dirt, lineHeight:1.5 }}>
                  {props.headline}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Ryan Hall Y'all Live Card */}
      {isRyanLive && (
        <div style={{ background:`linear-gradient(135deg,#1a0a0a,#2d0a0a)`, border:`1.5px solid ${C.red}`, borderRadius:14, padding:16, marginBottom:16 }}>
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10 }}>
            <div style={{ width:10, height:10, borderRadius:"50%", background:C.red, boxShadow:`0 0 8px ${C.red}`, animation:"pulse 1.5s ease-in-out infinite" }} />
            <div style={{ fontFamily:"Space Mono,monospace", fontSize:9, fontWeight:700, color:C.red, letterSpacing:1 }}>LIVE NOW</div>
          </div>
          <div style={{ fontFamily:"Playfair Display,serif", fontSize:16, fontWeight:700, color:"#fff", marginBottom:6 }}>
            Ryan Hall Y'all — Live Storm Coverage
          </div>
          <div style={{ fontFamily:"Crimson Pro,serif", fontSize:12, color:"rgba(255,255,255,0.7)", marginBottom:12 }}>
            Active severe weather in your area. Ryan is covering it live.
          </div>
          <div style={{ borderRadius:10, overflow:"hidden" }}>
            <iframe
              width="100%"
              height="200"
              src="https://www.youtube.com/embed/live_stream?channel=UCgFXm4TI8htWmCyJ6cVPG_A"
              title="Ryan Hall Y'all Live"
              frameBorder="0"
              allowFullScreen
            />
          </div>
        </div>
      )}
{/* Ryan Hall Recent Video (within 24hrs, not live) */}
      {!isRyanLive && ryanRecentVideo && (() => {
        const hoursAgo = Math.floor((Date.now() - new Date(ryanRecentVideo.publishedAt)) / 3600000);
        return (
          <div style={{ background:`linear-gradient(135deg,#0a1a2a,#0a2030)`, border:`1.5px solid ${C.sky}`, borderRadius:14, padding:16, marginBottom:16 }}>
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10 }}>
              <div style={{ width:10, height:10, borderRadius:"50%", background:C.sky, boxShadow:`0 0 8px ${C.sky}` }} />
              <div style={{ fontFamily:"Space Mono,monospace", fontSize:9, fontWeight:700, color:C.sky, letterSpacing:1 }}>POSTED {hoursAgo} HOUR{hoursAgo !== 1 ? "S" : ""} AGO</div>
            </div>
            <div style={{ fontFamily:"Playfair Display,serif", fontSize:15, fontWeight:700, color:"#fff", marginBottom:6 }}>
              {ryanRecentVideo.title}
            </div>
            <div style={{ fontFamily:"Crimson Pro,serif", fontSize:12, color:"rgba(255,255,255,0.7)", marginBottom:12 }}>
              Ryan Hall Y'all — Latest weather video
            </div>
            <div style={{ borderRadius:10, overflow:"hidden" }}>
              <iframe
                width="100%"
                height="200"
                src={`https://www.youtube.com/embed/${ryanRecentVideo.videoId}`}
                title={ryanRecentVideo.title}
                frameBorder="0"
                allowFullScreen
              />
            </div>
          </div>
        );
      })()}

      {/* 7-Day Forecast */}
      {dayPeriods.length > 0 && (
        <div style={{ marginBottom:16 }}>
          <div style={{ fontFamily:"Space Mono,monospace", fontSize:9, fontWeight:700, color:C.dust, letterSpacing:1, marginBottom:10 }}>
            7-DAY FORECAST
          </div>
          <div style={{ display:"flex", gap:8, overflowX:"auto", paddingBottom:8 }}>
            {dayPeriods.map((p, i) => {
              const night = nightPeriods[i];
              return (
                <div key={i} style={{ flexShrink:0, width:80, background:C.cream, border:`1.5px solid ${C.parchmentDeep}`, borderRadius:12, padding:"10px 8px", textAlign:"center" }}>
                  <div style={{ fontFamily:"Space Mono,monospace", fontSize:8, color:C.dust, marginBottom:4 }}>
                    {p.name?.split(" ")[0]?.toUpperCase()}
                  </div>
                  <div style={{ fontSize:22, marginBottom:4 }}>{getWeatherEmoji(p.shortForecast)}</div>
                  <div style={{ fontFamily:"Playfair Display,serif", fontSize:13, fontWeight:700, color:C.bark }}>
                    {p.temperature}°
                  </div>
                  {night && (
                    <div style={{ fontFamily:"Space Mono,monospace", fontSize:8, color:C.dust }}>
                      /{night.temperature}°
                    </div>
                  )}
                  {p.probabilityOfPrecipitation?.value && (
                    <div style={{ fontFamily:"Space Mono,monospace", fontSize:8, color:C.sky, marginTop:4 }}>
                      💧{p.probabilityOfPrecipitation.value}%
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* WeatherWise Card */}
      <div style={{ background:`linear-gradient(135deg,${C.skySoft},${C.sageSoft})`, border:`1.5px solid ${C.sky}`, borderRadius:14, padding:16, marginBottom:16 }}>
        <div style={{ fontFamily:"Space Mono,monospace", fontSize:9, fontWeight:700, color:C.sky, letterSpacing:1, marginBottom:6 }}>
          DEEPER ANALYSIS
        </div>
        <div style={{ fontFamily:"Playfair Display,serif", fontSize:15, fontWeight:700, color:C.bark, marginBottom:4 }}>
          WeatherWise
        </div>
        <div style={{ fontFamily:"Crimson Pro,serif", fontSize:12, color:C.dirt, marginBottom:12 }}>
          Advanced forecasting and storm analysis for serious weather watchers.
        </div>
        <a href="https://weatherwise.app" target="_blank" rel="noopener noreferrer" style={{ display:"inline-block", background:C.sky, color:"#fff", fontFamily:"Space Mono,monospace", fontSize:9, fontWeight:700, padding:"8px 14px", borderRadius:8, textDecoration:"none", letterSpacing:0.5 }}>
          OPEN WEATHERWISE →
        </a>
      </div>

    </div>
  );
}
// ─── CAMP TAB ────────────────────────────────────────────────
const FILTER_TAGS = ["All", "Free", "Full Hookups", "Electric Hookup", "Dump Station", "40ft+", "Large RV", "Van", "High Clearance", "Fire OK", "Community"];
const SORT_OPTIONS = ["Distance", "Rating", "Name"];

function CampTab({ userLocation, user }) {
  const [campView, setCampView] = useState("sites");
  const [activeFilter, setActiveFilter] = useState("All");
  const [sortBy, setSortBy] = useState("Distance");
  const [search, setSearch] = useState("");
  const [selectedSite, setSelectedSite] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [favorites, setFavorites] = useState(new Set());
  const [newSite, setNewSite] = useState({ name: "", detail: "", distance: "", tags: [], maxRigLength: "", notes: "" });
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [userGPS, setUserGPS] = useState(null);
  const [firebaseSites, setFirebaseSites] = useState([]);

  // Load community-submitted campsites from Firebase
  useEffect(() => {
    const q = query(collection(db, "campsites"), where("status", "==", "approved"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(q, snap => {
      setFirebaseSites(snap.docs.map(d => ({ ...d.data(), id: `fb_${d.id}`, contributed: true })));
    }, () => {
      // If index not ready yet, load without filter
      const q2 = query(collection(db, "campsites"), orderBy("createdAt", "desc"));
      onSnapshot(q2, snap => {
        setFirebaseSites(snap.docs.map(d => ({ ...d.data(), id: `fb_${d.id}`, contributed: true })));
      });
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setUserGPS({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => null,
        { timeout: 8000, maximumAge: 600000 }
      );
    }
  }, []);
  // Merge hardcoded + Firebase community sites
  const ALL_SITES = [...ALL_CAMPSITES, ...firebaseSites];

  // Filter logic
  const filtered = ALL_SITES
    .filter(c => {
      if (activeFilter === "All") return true;
      if (activeFilter === "Community") return c.contributed === true;
      if (activeFilter === "Free") return c.tags.includes("Free");
      if (activeFilter === "Fire OK") return c.tags.includes("Fire OK");
      if (activeFilter === "14-day") return c.tags.includes("14-day");
      if (activeFilter === "Small RV") return c.tags.includes("Small RV");
      return c.tags.some(t => t.toLowerCase() === activeFilter.toLowerCase());
    })
    .filter(c => {
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      const stateMap = {"AZ":"Arizona","NM":"New Mexico","UT":"Utah","NV":"Nevada","CO":"Colorado","CA":"California","OR":"Oregon","WA":"Washington","TX":"Texas","FL":"Florida","GA":"Georgia","ID":"Idaho","MT":"Montana","WY":"Wyoming"};
      const detailUpper = c.detail.toUpperCase();
      const nameUpper = c.name.toUpperCase();
      const matchedAbbr = Object.keys(stateMap).find(k => detailUpper.includes(` ${k} `) || detailUpper.includes(` ${k}·`) || detailUpper.includes(`, ${k}`));
      const stateFull = matchedAbbr ? stateMap[matchedAbbr].toLowerCase() : "";
      return c.name.toLowerCase().includes(q) || c.detail.toLowerCase().includes(q) || c.notes.toLowerCase().includes(q) || stateFull.includes(q) || c.tags.some(t => t.toLowerCase().includes(q));
    })
    .map(c => ({
      ...c,
      distanceMiles: (userGPS)
        ? getDistanceMiles(userGPS.lat, userGPS.lng, c.lat, c.lng)
        : parseFloat(c.distance),
    }))
    .sort((a, b) => {
      if (sortBy === "Distance") return a.distanceMiles - b.distanceMiles;
      if (sortBy === "Rating") return b.rating - a.rating;
      if (sortBy === "Name") return a.name.localeCompare(b.name);
      return 0;
    });

  const toggleFavorite = (id) => {
    setFavorites(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleAddTag = (tag) => {
    setNewSite(prev => ({
      ...prev,
      tags: prev.tags.includes(tag) ? prev.tags.filter(t => t !== tag) : [...prev.tags, tag]
    }));
  };

  const handleSubmitSite = async () => {
    if (!newSite.name || !newSite.detail) return;
    try {
      await addDoc(collection(db, "campsites"), {
        name: newSite.name,
        detail: newSite.detail,
        distance: newSite.distance || "Unknown",
        tags: newSite.tags,
        maxRigLength: newSite.maxRigLength || "Unknown",
        notes: newSite.notes || "",
        rating: 0,
        reviews: 0,
        amenities: [],
        elevation: "Unknown",
        surface: "Unknown",
        stayLimit: "14 days",
        lat: userGPS?.lat || 0,
        lng: userGPS?.lng || 0,
        contributed: true,
        contributedBy: user?.displayName || user?.email?.split("@")[0] || "Nomad",
        status: "approved",
        createdAt: serverTimestamp(),
      });
      setSubmitSuccess(true);
      setTimeout(() => { setShowAddForm(false); setSubmitSuccess(false); setNewSite({ name: "", detail: "", distance: "", tags: [], maxRigLength: "", notes: "" }); }, 2200);
    } catch (err) {
      console.error("Error submitting campsite:", err);
      alert("Error submitting — check console");
    }
  };

  // Detail view
  if (selectedSite) {
    const site = ALL_CAMPSITES.find(c => c.id === selectedSite);
    return (
      <div style={{ padding: "20px 16px" }}>
        <button onClick={() => setSelectedSite(null)} style={{ background: "none", border: "none", color: C.sage, fontFamily: "Space Mono, monospace", fontSize: 10, cursor: "pointer", marginBottom: 16, padding: 0, display: "flex", alignItems: "center", gap: 6 }}>
          ← Back to Camp
        </button>
        <div style={{ background: `linear-gradient(160deg, ${C.pine}, ${C.sageDark})`, borderRadius: 16, padding: 20, marginBottom: 16, position: "relative" }}>
          {site.contributed && (
            <div style={{ position: "absolute", top: 12, right: 12, background: `linear-gradient(135deg, ${C.sunrise}, ${C.canyon})`, color: "#fff", borderRadius: 8, padding: "3px 10px", fontFamily: "Space Mono, monospace", fontSize: 8, fontWeight: 700 }}>
              COMMUNITY
            </div>
          )}
          <div style={{ fontFamily: "Playfair Display, serif", fontSize: 20, fontWeight: 700, color: "#fff", marginBottom: 4 }}>{site.name}</div>
          <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 13, color: "rgba(255,255,255,0.7)", fontStyle: "italic", marginBottom: 12 }}>{site.detail}</div>
          <StarRating rating={site.rating} reviews={site.reviews} />
          {site.contributed && site.contributedBy && (
            <div style={{ marginTop: 8, fontFamily: "Space Mono, monospace", fontSize: 9, color: C.sageLight }}>Added by {site.contributedBy}</div>
          )}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
          {[
            ["📍 Distance", site.distance],
            ["⬆️ Elevation", site.elevation],
            ["🛣 Surface", site.surface],
            ["📏 Max Length", site.maxRigLength],
            ["📅 Stay Limit", site.stayLimit],
          ].map(([l, v]) => (
            <div key={l} style={{ background: C.barkMid, border: `1.5px solid ${C.parchmentDeep}`, borderRadius: 12, padding: "12px 14px" }}>
              <div style={{ fontFamily: "Space Mono, monospace", fontSize: 8, color: C.dust, letterSpacing: 1, marginBottom: 4 }}>{l}</div>
              <div style={{ fontFamily: "Playfair Display, serif", fontSize: 14, fontWeight: 700, color: C.parchment }}>{v}</div>
            </div>
          ))}
        </div>

        {/* Tags */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontFamily: "Space Mono, monospace", fontSize: 9, color: C.dust, letterSpacing: 1, marginBottom: 8 }}>FEATURES</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {site.tags.map(tag => (
              <span key={tag} style={{ background: tag.includes("Free") ? C.sageSoft : tag.includes("Paid") ? C.sunriseSoft : C.parchment, color: tag.includes("Free") ? C.sageDark : tag.includes("Paid") ? C.canyon : C.bark, border: `1px solid ${tag.includes("Free") ? C.sageLight : C.parchmentDeep}`, borderRadius: 8, padding: "4px 10px", fontFamily: "Space Mono, monospace", fontSize: 9, fontWeight: 700 }}>{tag}</span>
            ))}
          </div>
        </div>

        {/* Amenities */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontFamily: "Space Mono, monospace", fontSize: 9, color: C.dust, letterSpacing: 1, marginBottom: 8 }}>AMENITIES</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {site.amenities.map(a => <span key={a} style={{ background: "#231408", border: `1px solid ${C.parchmentDeep}`, borderRadius: 8, padding: "4px 10px", fontFamily: "Crimson Pro, serif", fontSize: 12, color: C.parchment }}>• {a}</span>)}
          </div>
        </div>

        {/* Community notes */}
        <Card accent={C.sage}>
          <div style={{ fontFamily: "Space Mono, monospace", fontSize: 9, color: C.sageDark, letterSpacing: 1, marginBottom: 6 }}>💬 COMMUNITY NOTES</div>
          <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 14, color: C.parchment, lineHeight: 1.6, fontStyle: "italic" }}>"{site.notes}"</div>
        </Card>

        <div style={{ display: "flex", gap: 10 }}>
          <Btn variant="sage" style={{ flex: 1 }}>🗺 Get Directions</Btn>
          <button onClick={() => toggleFavorite(site.id)} style={{ background: favorites.has(site.id) ? C.sunriseSoft : "#231408", border: `1.5px solid ${favorites.has(site.id) ? C.sunrise : C.parchmentDeep}`, borderRadius: 12, padding: "0 18px", fontSize: 20, cursor: "pointer" }}>{favorites.has(site.id) ? "❤️" : "🤍"}</button>
        </div>
        <Btn variant="secondary" style={{ marginTop: 8 }}>📸 Add Photos</Btn>
      </div>
    );
  }

  // Add site form
  if (showAddForm) {
    const addableTags = ["Free", "40ft+", "Large RV", "Van", "High Clearance", "Fire OK", "Full Hookups", "Electric Hookup", "Dump Station", "Water", "No Generators", "14-day", "Creek"];
    return (
      <div style={{ padding: "20px 16px" }}>
        <button onClick={() => setShowAddForm(false)} style={{ background: "none", border: "none", color: C.sage, fontFamily: "Space Mono, monospace", fontSize: 10, cursor: "pointer", marginBottom: 16, padding: 0 }}>← Cancel</button>
        <SectionHeader icon="📍" title="Add a Campsite" />
        <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 13, color: C.dirt, fontStyle: "italic", marginBottom: 20 }}>Earn RoadBurnAI Credits for verified community contributions</div>

        <InputField label="Site Name" value={newSite.name} onChange={v => setNewSite(p => ({ ...p, name: v }))} placeholder="Coconino NF — FR 245" />
        <InputField label="Brief Description" value={newSite.detail} onChange={v => setNewSite(p => ({ ...p, detail: v }))} placeholder="Free dispersed · 12 mi · Paved access" />
        <div style={{ display: "flex", gap: 10 }}>
          <InputField label="Distance from You" value={newSite.distance} onChange={v => setNewSite(p => ({ ...p, distance: v }))} placeholder="12 mi" half />
          <InputField label="Max Rig Length" value={newSite.maxRigLength} onChange={v => setNewSite(p => ({ ...p, maxRigLength: v }))} placeholder="45 ft" half />
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={{ display: "block", fontFamily: "Space Mono, monospace", fontSize: 9, fontWeight: 700, color: C.dust, letterSpacing: 1.2, marginBottom: 8, textTransform: "uppercase" }}>Features (select all that apply)</label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {addableTags.map(tag => (
              <button key={tag} onClick={() => handleAddTag(tag)} style={{ padding: "5px 12px", borderRadius: 20, border: `1.5px solid ${newSite.tags.includes(tag) ? C.sage : C.parchmentDeep}`, background: newSite.tags.includes(tag) ? C.sageSoft : "#231408", color: newSite.tags.includes(tag) ? C.sageDark : C.dust, fontFamily: "Space Mono, monospace", fontSize: 9, fontWeight: 700, cursor: "pointer" }}>{tag}</button>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={{ display: "block", fontFamily: "Space Mono, monospace", fontSize: 9, fontWeight: 700, color: C.dust, letterSpacing: 1.2, marginBottom: 6, textTransform: "uppercase" }}>Nomad Notes</label>
          <textarea value={newSite.notes} onChange={e => setNewSite(p => ({ ...p, notes: e.target.value }))} placeholder="Tips for other nomads — road conditions, cell signal, hazards..." style={{ width: "100%", background: "#231408", border: `1.5px solid ${C.parchmentDeep}`, borderRadius: 10, padding: "12px 14px", fontFamily: "Crimson Pro, serif", fontSize: 14, color: C.parchment, outline: "none", minHeight: 80, resize: "vertical" }} />
        </div>

        {submitSuccess ? (
          <div style={{ background: C.sageSoft, border: `1.5px solid ${C.sageLight}`, borderRadius: 12, padding: 16, textAlign: "center" }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>✅</div>
            <div style={{ fontFamily: "Playfair Display, serif", fontSize: 16, fontWeight: 700, color: C.sageDark }}>Site submitted for review!</div>
            <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 13, color: C.dirt, fontStyle: "italic", marginTop: 4 }}>You'll earn 1 Credit when verified.</div>
          </div>
        ) : (
          <Btn onClick={handleSubmitSite}>📍 SUBMIT CAMPSITE (+1 Credit)</Btn>
        )}
      </div>
    );
  }

  if (campView === "restrooms") {
    return <RestroomsSection userGPS={userGPS} user={user} userLocation={userLocation} campView={campView} setCampView={setCampView} />;
  }

  return (
    <div style={{ padding: "20px 16px" }}>
      <SectionHeader icon="⛺" title="RoadBurnAI Camp" />
      <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
        {[["sites","⛺ Campsites"],["restrooms","🚻 Restrooms"]].map(([k,l]) => (
          <button key={k} onClick={() => setCampView(k)} style={{ flex: 1, padding: "9px 4px", background: campView === k ? `linear-gradient(135deg, ${C.sunrise}, ${C.canyon})` : "#231408", color: campView === k ? "#fff" : C.dust, border: `1.5px solid ${campView === k ? C.sunrise : C.parchmentDeep}`, borderRadius: 10, fontFamily: "Space Mono, monospace", fontSize: 9, fontWeight: 700, letterSpacing: 0.5, cursor: "pointer" }}>{l}</button>
        ))}
      </div>
      <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 16, color: C.parchment, fontStyle: "italic", marginBottom: 14 }}>
        Free & paid campsites filtered for your rig — near {userLocation || "you"}
      </div>

      {/* Search */}
      <div style={{ position: "relative", marginBottom: 14 }}>
        <span style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", fontSize: 14 }}>🔍</span>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, location, feature..." autoComplete="off" style={{ width: "100%", background: "#231408", border: `1.5px solid ${C.parchmentDeep}`, borderRadius: 12, padding: "12px 14px 12px 38px", fontFamily: "Crimson Pro, serif", fontSize: 14, color: "#f0e8d8 !important", WebkitTextFillColor: "#f0e8d8", caretColor: "#f0e8d8", outline: "none", boxSizing: "border-box" }} />
        {search && <button onClick={() => setSearch("")} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: C.dust, cursor: "pointer", fontSize: 16 }}>×</button>}
      </div>

      {/* Sort + count */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <div style={{ fontFamily: "Space Mono, monospace", fontSize: 9, color: C.dust }}>{filtered.length} SITE{filtered.length !== 1 ? "S" : ""}</div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontFamily: "Space Mono, monospace", fontSize: 8, color: C.dust }}>SORT:</span>
          {SORT_OPTIONS.map(opt => (
            <button key={opt} onClick={() => setSortBy(opt)} style={{ padding: "4px 8px", borderRadius: 6, border: `1px solid ${sortBy === opt ? C.sage : C.parchmentDeep}`, background: sortBy === opt ? C.sageSoft : "transparent", color: sortBy === opt ? C.sageDark : C.dust, fontFamily: "Space Mono, monospace", fontSize: 8, cursor: "pointer" }}>{opt}</button>
          ))}
        </div>
      </div>

      {/* Filter chips */}
      <div style={{ display: "flex", gap: 6, marginBottom: 16, overflowX: "auto", paddingBottom: 4 }}>
        {FILTER_TAGS.map(f => (
          <button key={f} onClick={() => setActiveFilter(f)} style={{ flexShrink: 0, padding: "7px 14px", background: activeFilter === f ? `linear-gradient(135deg, ${C.sunrise}, ${C.canyon})` : "#231408", border: `1.5px solid ${activeFilter === f ? C.sunrise : C.parchmentDeep}`, borderRadius: 20, fontFamily: "Space Mono, monospace", fontSize: 9, fontWeight: 700, color: activeFilter === f ? "#fff" : C.dust, cursor: "pointer" }}>
            {f === "Free" ? "🆓 Free" : f === "Full Hookups" ? "⚡ Full Hookups" : f === "Community" ? "👥 Community" : f === "High Clearance" ? "⬆️ High Clearance" : f}
          </button>
        ))}
      </div>

      {/* Simulated map strip */}
      <div style={{ background: "linear-gradient(160deg, #0d1a0d, #0a120a)", border: `1.5px solid ${C.sageDark}`, borderRadius: 14, height: 110, marginBottom: 16, position: "relative", overflow: "hidden" }}>
        {[...Array(6)].map((_, i) => <div key={i} style={{ position: "absolute", left: `${i * 20}%`, top: 0, bottom: 0, width: 1, background: "rgba(74,110,78,0.15)" }} />)}
        {[...Array(4)].map((_, i) => <div key={i} style={{ position: "absolute", top: `${i * 33}%`, left: 0, right: 0, height: 1, background: "rgba(74,110,78,0.15)" }} />)}
        {/* Simulated pins */}
        {filtered.slice(0, 5).map((site, i) => (
          <div key={site.id} onClick={() => setSelectedSite(site.id)} style={{ position: "absolute", left: `${15 + i * 15}%`, top: `${20 + (i % 3) * 22}%`, cursor: "pointer", transform: "translate(-50%, -50%)" }}>
            <div style={{ width: 10, height: 10, borderRadius: "50%", background: site.tags.includes("Free") ? C.sage : C.sunrise, border: "2px solid #fff", boxShadow: "0 0 6px rgba(0,0,0,0.5)" }} />
          </div>
        ))}
        <div style={{ position: "absolute", bottom: 8, left: 12, display: "flex", gap: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}><div style={{ width: 8, height: 8, borderRadius: "50%", background: C.sage }} /><span style={{ fontFamily: "Space Mono, monospace",fontSize: 10, color: C.sageLight }}>FREE</span></div>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}><div style={{ width: 8, height: 8, borderRadius: "50%", background: C.sunrise }} /><span style={{ fontFamily: "Space Mono, monospace", fontSize: 7, color: C.sageLight }}>PAID</span></div>
        </div>
        <div style={{ position: "absolute", bottom: 8, right: 12, fontFamily: "Space Mono, monospace", fontSize: 7, color: C.sageDark }}>TAP PIN FOR DETAIL</div>
      </div>

      {/* Results */}
      {filtered.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px 20px", color: C.dust, fontFamily: "Crimson Pro, serif", fontStyle: "italic" }}>
          No campsites match your filter. Try a different one!
        </div>
      ) : filtered.map(c => (
        <div key={c.id} onClick={() => setSelectedSite(c.id)} style={{ background: "#231408", border: `1.5px solid ${c.contributed ? C.sageLight : C.parchmentDeep}`, borderRadius: 14, padding: 16, marginBottom: 12, cursor: "pointer", position: "relative" }}>
          {c.contributed && (
            <div style={{ position: "absolute", top: 10, right: 10, background: `linear-gradient(135deg, ${C.sage}, ${C.sageDark})`, color: "#fff", borderRadius: 6, padding: "2px 8px", fontFamily: "Space Mono, monospace", fontSize: 11, fontWeight: 700 }}>👥 COMMUNITY</div>
          )}
          <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
            <div style={{ width: 42, height: 42, borderRadius: 12, background: c.tags.includes("Free") ? `linear-gradient(135deg, ${C.pine}, ${C.sageDark})` : `linear-gradient(135deg, ${C.barkMid}, ${C.bark})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>
              {c.tags.includes("Free") ? "⛺" : c.tags.includes("Full Hookups") ? "⚡" : "🏕"}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: "Playfair Display, serif", fontSize: 14, fontWeight: 700, color: "#e8d5b8", marginBottom: 2, paddingRight
                : c.contributed ? 70 : 0 }}>{c.name}</div>
              <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 12, color: C.dirt, fontStyle: "italic", marginBottom: 8 }}>{c.detail}</div>
              <StarRating rating={c.rating} reviews={c.reviews} />
              <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 8 }}>
                {c.tags.slice(0, 4).map(t => (
                  <span key={t} style={{ background: t.includes("Free") ? C.sageSoft : t.includes("Paid") ? C.sunriseSoft : "#2c1a08", color: t.includes("Free") ? C.sageDark : t.includes("Paid") ? C.canyon : C.parchmentDeep, border: `1px solid ${t.includes("Free") ? C.sageLight : t.includes("Paid") ? C.canyonLight : C.parchmentDeep}`, borderRadius: 6, padding: "2px 7px", fontFamily: "Space Mono, monospace", fontSize: 8 }}>{t}</span>
                ))}
                {c.tags.length > 4 && <span style={{ color: C.dust, fontFamily: "Space Mono, monospace", fontSize: 8, padding: "2px 0" }}>+{c.tags.length - 4} more</span>}
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6, flexShrink: 0 }}>
              <div style={{ fontFamily: "Playfair Display, serif", fontSize: 14, fontWeight: 900, color: C.sage }}>{c.distance}</div>
              <button onClick={e => { e.stopPropagation(); toggleFavorite(c.id); }} style={{ background: "none", border: "none", fontSize: 16, cursor: "pointer", padding: 0 }}>{favorites.has(c.id) ? "❤️" : "🤍"}</button>
            </div>
          </div>
        </div>
      ))}

      {/* Favorites summary */}
      {favorites.size > 0 && (
        <div style={{ background: C.sunriseSoft, border: `1.5px solid ${C.canyonLight}`, borderRadius: 12, padding: "12px 16px", marginBottom: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 13, color: C.parchment }}>❤️ {favorites.size} site{favorites.size !== 1 ? "s" : ""} saved to favorites</div>
          <button onClick={() => setFavorites(new Set())} style={{ background: "none", border: "none", fontFamily: "Space Mono, monospace", fontSize: 9, color: C.canyon, cursor: "pointer" }}>CLEAR</button>
        </div>
      )}

      <button onClick={() => setShowAddForm(true)} style={{ width: "100%", background: "none", border: `1.5px dashed ${C.sageLight}`, borderRadius: 14, padding: 16, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginTop: 4 }}>
        <span style={{ fontSize: 20 }}>📍</span>
        <div style={{ textAlign: "left" }}>
          <div style={{ fontFamily: "Space Mono, monospace", fontSize: 10, fontWeight: 700, color: C.sageDark }}>ADD A CAMPSITE</div>
          <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 12, color: C.dirt, fontStyle: "italic" }}>Earn 1 Credit when verified by the community</div>
        </div>
      </button>
    </div>
  );
}

// ─── RESTROOM DATA ────────────────────────────────────────────
const RESTROOM_TYPES = ["Gas Station", "Rest Area", "Truck Stop", "Library", "Park", "Other"];
const RESTROOM_ACCESS = ["Free", "Purchase Required", "Code Needed"];
const CLEAN_OPTIONS = ["Clean", "Okay", "Avoid"];
const SAFETY_OPTIONS = ["Felt Safe", "Neutral", "Unsafe"];
const REPORT_ISSUES = ["Broken Lock", "No Lighting", "Needle/Drug Activity", "Out of Order", "Other"];

function ratingColor(val) {
  if (val === "Clean" || val === "Felt Safe") return { bg: C.sageSoft, color: C.sageDark, border: C.sageLight };
  if (val === "Okay" || val === "Neutral") return { bg: C.sunriseSoft, color: C.canyon, border: C.canyonLight };
  if (val === "Avoid" || val === "Unsafe") return { bg: C.redSoft, color: C.red, border: "#e8b8b8" };
  return { bg: "#2c1a08", color: C.dust, border: C.parchmentDeep };
}

function RatingPill({ label, value }) {
  const c = ratingColor(value);
  return (
    <span style={{ background: c.bg, color: c.color, border: `1px solid ${c.border}`, borderRadius: 6, padding: "2px 8px", fontFamily: "Space Mono, monospace", fontSize: 9, fontWeight: 700 }}>
      {label}: {value || "N/A"}
    </span>
  );
}

function formatRelativeDate(date) {
  if (!date) return "Unknown";
  const days = Math.floor((new Date() - date) / 86400000);
  if (days < 1) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months > 1 ? "s" : ""} ago`;
  return date.toLocaleDateString("en-US", { month: "short", year: "numeric" });
}

function RestroomsSection({ userGPS, user, userLocation, campView, setCampView }) {
  const [restrooms, setRestrooms] = useState([]);
  const [activeFilter, setActiveFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showReportForm, setShowReportForm] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [reportSuccess, setReportSuccess] = useState(false);
  const [reportIssue, setReportIssue] = useState(REPORT_ISSUES[0]);
  const [reportNote, setReportNote] = useState("");
  const [newRestroom, setNewRestroom] = useState({
    name: "", type: "Gas Station", access: "Free", cleanliness: "Clean",
    safetyDay: "Felt Safe", safetyNight: "Felt Safe", stalls: "", notes: ""
  });
  const [formGPS, setFormGPS] = useState(userGPS || null);
  const [gpsRequesting, setGpsRequesting] = useState(false);
  const [gpsError, setGpsError] = useState(false);

  useEffect(() => {
    if (userGPS && !formGPS) setFormGPS(userGPS);
  }, [userGPS]);

  const requestGPS = () => {
    setGpsRequesting(true); setGpsError(false);
    if (!navigator.geolocation) { setGpsError(true); setGpsRequesting(false); return; }
    navigator.geolocation.getCurrentPosition(
      pos => { setFormGPS({ lat: pos.coords.latitude, lng: pos.coords.longitude }); setGpsRequesting(false); },
      () => { setGpsError(true); setGpsRequesting(false); },
      { timeout: 10000, maximumAge: 60000 }
    );
  };
  const [locationMode, setLocationMode] = useState("gps");
  const [manualLocation, setManualLocation] = useState("");
  const [manualAck, setManualAck] = useState(false);

  useEffect(() => {
    const q = query(collection(db, "restrooms"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(q, snap => {
      setRestrooms(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, err => console.error("Restrooms error:", err));
    return () => unsub();
  }, []);

  const filtered = restrooms
    .filter(r => {
      if (activeFilter === "All") return true;
      if (activeFilter === "Free") return r.access === "Free";
      if (activeFilter === "Felt Safe") return r.safetyDay === "Felt Safe" || r.safetyNight === "Felt Safe";
      if (activeFilter === "Has Reports") return r.openReports && r.openReports.length > 0;
      return r.type === activeFilter;
    })
    .filter(r => {
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return (r.name || "").toLowerCase().includes(q) || (r.type || "").toLowerCase().includes(q) || (r.notes || "").toLowerCase().includes(q);
    })
    .map(r => ({
      ...r,
      distanceMiles: userGPS && r.lat && r.lng ? getDistanceMiles(userGPS.lat, userGPS.lng, r.lat, r.lng) : null,
    }))
    .sort((a, b) => {
      if (a.distanceMiles == null && b.distanceMiles == null) return 0;
      if (a.distanceMiles == null) return 1;
      if (b.distanceMiles == null) return -1;
      return a.distanceMiles - b.distanceMiles;
    });

  const handleSubmit = async () => {
    if (!newRestroom.name) return;
    if (locationMode === "gps" && !formGPS) return;
    if (locationMode === "manual" && (!manualLocation.trim() || !manualAck)) return;
    try {
      await addDoc(collection(db, "restrooms"), {
        ...newRestroom,
        lat: locationMode === "gps" ? formGPS.lat : null,
        lng: locationMode === "gps" ? formGPS.lng : null,
        locationMethod: locationMode,
        manualLocation: locationMode === "manual" ? manualLocation.trim() : "",
        contributedBy: user?.displayName || user?.email?.split("@")[0] || "Nomad",
        openReports: [],
        createdAt: serverTimestamp(),
        lastReportedAt: serverTimestamp(),
      });
      setSubmitSuccess(true);
      setTimeout(() => {
        setSubmitSuccess(false); setShowAddForm(false);
        setNewRestroom({ name: "", type: "Gas Station", access: "Free", cleanliness: "Clean", safetyDay: "Felt Safe", safetyNight: "Felt Safe", stalls: "", notes: "" });
        setLocationMode("gps"); setManualLocation(""); setManualAck(false);
      }, 2000);
    } catch (err) { console.error("Error submitting restroom:", err); alert("Error submitting — check console"); }
  };

  const handleQuickReport = async () => {
    if (!selectedId) return;
    try {
      await updateDoc(doc(db, "restrooms", selectedId), {
        openReports: arrayUnion({ issue: reportIssue, note: reportNote || "", reportedAt: new Date() }),
        lastReportedAt: serverTimestamp(),
      });
      setReportSuccess(true);
      setTimeout(() => { setReportSuccess(false); setShowReportForm(false); setReportNote(""); setReportIssue(REPORT_ISSUES[0]); }, 1800);
    } catch (err) { console.error("Error filing report:", err); alert("Error submitting report — check console"); }
  };

  const Toggle = (
    <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
      {[["sites","⛺ Campsites"],["restrooms","🚻 Restrooms"]].map(([k,l]) => (
        <button key={k} onClick={() => setCampView(k)} style={{ flex: 1, padding: "9px 4px", background: campView === k ? `linear-gradient(135deg, ${C.sunrise}, ${C.canyon})` : "#231408", color: campView === k ? "#fff" : C.dust, border: `1.5px solid ${campView === k ? C.sunrise : C.parchmentDeep}`, borderRadius: 10, fontFamily: "Space Mono, monospace", fontSize: 9, fontWeight: 700, letterSpacing: 0.5, cursor: "pointer" }}>{l}</button>
      ))}
    </div>
  );

  if (selectedId) {
    const r = restrooms.find(x => x.id === selectedId);
    if (!r) { setSelectedId(null); return null; }
    const lastReported = r.lastReportedAt?.toDate ? r.lastReportedAt.toDate() : null;

    if (showReportForm) {
      return (
        <div style={{ padding: "20px 16px" }}>
          <button onClick={() => setShowReportForm(false)} style={{ background: "none", border: "none", color: C.sage, fontFamily: "Space Mono, monospace", fontSize: 10, cursor: "pointer", marginBottom: 16, padding: 0 }}>← Cancel</button>
          <SectionHeader icon="⚠️" title="Report a Problem" />
          <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 13, color: C.dust, fontStyle: "italic", marginBottom: 20 }}>{r.name}</div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: "block", fontFamily: "Space Mono, monospace", fontSize: 9, fontWeight: 700, color: C.dust, letterSpacing: 1.2, marginBottom: 8, textTransform: "uppercase" }}>Issue</label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {REPORT_ISSUES.map(issue => (
                <button key={issue} onClick={() => setReportIssue(issue)} style={{ padding: "7px 12px", borderRadius: 20, border: `1.5px solid ${reportIssue === issue ? C.red : C.parchmentDeep}`, background: reportIssue === issue ? C.redSoft : "#231408", color: reportIssue === issue ? C.red : C.dust, fontFamily: "Space Mono, monospace", fontSize: 9, fontWeight: 700, cursor: "pointer" }}>{issue}</button>
              ))}
            </div>
          </div>
          <InputField label="Notes (optional)" value={reportNote} onChange={setReportNote} placeholder="Any other details..." />
          {reportSuccess ? (
            <div style={{ background: C.sageSoft, border: `1.5px solid ${C.sageLight}`, borderRadius: 12, padding: 16, textAlign: "center" }}>
              <div style={{ fontSize: 28, marginBottom: 8 }}>✅</div>
              <div style={{ fontFamily: "Playfair Display, serif", fontSize: 16, fontWeight: 700, color: C.sageDark }}>Report filed — thank you</div>
            </div>
          ) : (
            <Btn onClick={handleQuickReport}>⚠️ SUBMIT REPORT</Btn>
          )}
        </div>
      );
    }

    return (
      <div style={{ padding: "20px 16px" }}>
        <button onClick={() => setSelectedId(null)} style={{ background: "none", border: "none", color: C.sage, fontFamily: "Space Mono, monospace", fontSize: 10, cursor: "pointer", marginBottom: 16, padding: 0 }}>← Back to Restrooms</button>
        {r.openReports && r.openReports.length > 0 && (
          <div style={{ background: C.redSoft, border: "1.5px solid #e8b8b8", borderRadius: 12, padding: "12px 16px", marginBottom: 16 }}>
            <div style={{ fontFamily: "Space Mono, monospace", fontSize: 9, fontWeight: 700, color: C.red, letterSpacing: 1, marginBottom: 6 }}>⚠️ ACTIVE REPORTS</div>
            {r.openReports.map((rep, i) => (
              <div key={i} style={{ fontFamily: "Crimson Pro, serif", fontSize: 13, color: C.bark, marginBottom: 4 }}>
                • {rep.issue}{rep.note ? ` — ${rep.note}` : ""}
              </div>
            ))}
          </div>
        )}
        <div style={{ background: `linear-gradient(160deg, ${C.pine}, ${C.sageDark})`, borderRadius: 16, padding: 20, marginBottom: 16 }}>
          <div style={{ fontFamily: "Playfair Display, serif", fontSize: 20, fontWeight: 700, color: "#fff", marginBottom: 4 }}>{r.name}</div>
          <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 13, color: "rgba(255,255,255,0.7)", fontStyle: "italic", marginBottom: 8 }}>{r.type} · {r.access}{r.manualLocation ? ` · ${r.manualLocation}` : ""}</div>
          <div style={{ marginBottom: 12 }}>
            {r.lat && r.lng ? (
              <span style={{ background: "rgba(58,152,152,0.25)", color: "#9be8e8", border: "1px solid rgba(91,186,186,0.5)", borderRadius: 6, padding: "3px 9px", fontFamily: "Space Mono, monospace", fontSize: 9, fontWeight: 700 }}>🛰️ GPS VERIFIED</span>
            ) : (
              <span style={{ background: "rgba(232,114,10,0.2)", color: "#f5c089", border: "1px solid rgba(232,114,10,0.4)", borderRadius: 6, padding: "3px 9px", fontFamily: "Space Mono, monospace", fontSize: 9, fontWeight: 700 }}>📝 REPORTED FROM MEMORY</span>
            )}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            <RatingPill label="Clean" value={r.cleanliness} />
            <RatingPill label="Day" value={r.safetyDay} />
            <RatingPill label="Night" value={r.safetyNight} />
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
          <div style={{ background: C.barkMid, border: `1.5px solid ${C.parchmentDeep}`, borderRadius: 12, padding: "12px 14px" }}>
            <div style={{ fontFamily: "Space Mono, monospace", fontSize: 8, color: C.dust, letterSpacing: 1, marginBottom: 4 }}>🚽 STALLS</div>
            <div style={{ fontFamily: "Playfair Display, serif", fontSize: 14, fontWeight: 700, color: C.parchment }}>{r.stalls || "Unknown"}</div>
          </div>
          <div style={{ background: C.barkMid, border: `1.5px solid ${C.parchmentDeep}`, borderRadius: 12, padding: "12px 14px" }}>
            <div style={{ fontFamily: "Space Mono, monospace", fontSize: 8, color: C.dust, letterSpacing: 1, marginBottom: 4 }}>🕒 LAST REPORTED</div>
            <div style={{ fontFamily: "Playfair Display, serif", fontSize: 14, fontWeight: 700, color: C.parchment }}>{formatRelativeDate(lastReported)}</div>
          </div>
        </div>
        {r.notes && (
          <Card accent={C.sage}>
            <div style={{ fontFamily: "Space Mono, monospace", fontSize: 9, color: C.sageDark, letterSpacing: 1, marginBottom: 6 }}>💬 NOTES</div>
            <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 14, color: C.parchment, lineHeight: 1.6, fontStyle: "italic" }}>"{r.notes}"</div>
          </Card>
        )}
        <div style={{ fontFamily: "Space Mono, monospace", fontSize: 9, color: C.dust, marginBottom: 16 }}>Added by {r.contributedBy || "Nomad"}</div>
        <Btn variant="sage">🗺 Get Directions</Btn>
        <button onClick={() => setShowReportForm(true)} style={{ width: "100%", background: "none", border: `1.5px solid ${C.red}`, borderRadius: 12, padding: "13px", marginTop: 10, fontFamily: "Space Mono, monospace", fontSize: 11, fontWeight: 700, color: C.red, cursor: "pointer" }}>⚠️ REPORT A PROBLEM</button>
      </div>
    );
  }

  if (showAddForm) {
    return (
      <div style={{ padding: "20px 16px" }}>
        <button onClick={() => setShowAddForm(false)} style={{ background: "none", border: "none", color: C.sage, fontFamily: "Space Mono, monospace", fontSize: 10, cursor: "pointer", marginBottom: 16, padding: 0 }}>← Cancel</button>
        <SectionHeader icon="🚻" title="Add a Restroom" />
        <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 13, color: C.dirt, fontStyle: "italic", marginBottom: 20 }}>Help other nomads find clean, safe restrooms on the road</div>

        <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
          {[["gps","📍 I'm Here Now"],["manual","📝 Not Here — Add From Memory"]].map(([k,l]) => (
            <button key={k} onClick={() => setLocationMode(k)} style={{ flex: 1, padding: "9px 4px", background: locationMode === k ? `linear-gradient(135deg, ${C.sunrise}, ${C.canyon})` : "#231408", color: locationMode === k ? "#fff" : C.dust, border: `1.5px solid ${locationMode === k ? C.sunrise : C.parchmentDeep}`, borderRadius: 10, fontFamily: "Space Mono, monospace", fontSize: 9, fontWeight: 700, letterSpacing: 0.5, cursor: "pointer" }}>{l}</button>
          ))}
        </div>

        {locationMode === "gps" ? (
          <div style={{ background: formGPS ? C.sageSoft : C.redSoft, border: `1.5px solid ${formGPS ? C.sageLight : "#e8b8b8"}`, borderRadius: 12, padding: "12px 16px", marginBottom: 16 }}>
            <div style={{ fontFamily: "Space Mono, monospace", fontSize: 9, fontWeight: 700, color: formGPS ? C.sageDark : C.red, letterSpacing: 1, marginBottom: 4 }}>
              {formGPS ? "✓ LOCATION CAPTURED" : "📍 LOCATION REQUIRED"}
            </div>
            <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 12, color: C.bark }}>
              {formGPS ? "Your current GPS coordinates will be saved with this entry." : "For safety, every GPS-verified entry needs your real location captured at the time of submission."}
            </div>
            {!formGPS && (
              <button onClick={requestGPS} disabled={gpsRequesting} style={{ marginTop: 10, width: "100%", background: C.red, border: "none", borderRadius: 10, padding: "10px", fontFamily: "Space Mono, monospace", fontSize: 10, fontWeight: 700, color: "#fff", cursor: "pointer" }}>
                {gpsRequesting ? "GETTING LOCATION..." : "📍 SHARE MY LOCATION"}
              </button>
            )}
            {gpsError && <div style={{ marginTop: 8, fontFamily: "Crimson Pro, serif", fontSize: 11, color: C.red }}>Couldn't get your location. Check that location access is allowed for this site in your browser settings, then try again.</div>}
          </div>
        ) : (
          <div style={{ background: C.sunriseSoft, border: `1.5px solid ${C.canyonLight}`, borderRadius: 12, padding: "12px 16px", marginBottom: 16 }}>
            <div style={{ fontFamily: "Space Mono, monospace", fontSize: 9, fontWeight: 700, color: C.canyon, letterSpacing: 1, marginBottom: 4 }}>📝 REPORTED FROM MEMORY</div>
            <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 12, color: C.bark, marginBottom: 10 }}>This entry will be clearly labeled as not GPS-verified so other nomads know the difference.</div>
            <input value={manualLocation} onChange={e => setManualLocation(e.target.value)} placeholder="Approximate location — city, state, or nearest exit" style={{ width: "100%", background: "#231408", border: `1.5px solid ${C.parchmentDeep}`, borderRadius: 10, padding: "12px 14px", fontFamily: "Crimson Pro, serif", fontSize: 14, color: C.parchment, outline: "none", boxSizing: "border-box", marginBottom: 10 }} />
            <label style={{ display: "flex", alignItems: "flex-start", gap: 8, cursor: "pointer" }}>
              <input type="checkbox" checked={manualAck} onChange={e => setManualAck(e.target.checked)} style={{ marginTop: 2 }} />
              <span style={{ fontFamily: "Crimson Pro, serif", fontSize: 12, color: C.bark }}>I confirm this information is accurate to the best of my knowledge, even though I'm not at this location right now.</span>
            </label>
          </div>
        )}

        <InputField label="Location Name" value={newRestroom.name} onChange={v => setNewRestroom(p => ({ ...p, name: v }))} placeholder="Pilot Travel Center — Exit 201" />

        <div style={{ marginBottom: 14 }}>
          <label style={{ display: "block", fontFamily: "Space Mono, monospace", fontSize: 9, fontWeight: 700, color: C.dust, letterSpacing: 1.2, marginBottom: 8, textTransform: "uppercase" }}>Type</label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {RESTROOM_TYPES.map(t => (
              <button key={t} onClick={() => setNewRestroom(p => ({ ...p, type: t }))} style={{ padding: "7px 12px", borderRadius: 20, border: `1.5px solid ${newRestroom.type === t ? C.sage : C.parchmentDeep}`, background: newRestroom.type === t ? C.sageSoft : "#231408", color: newRestroom.type === t ? C.sageDark : C.dust, fontFamily: "Space Mono, monospace", fontSize: 9, fontWeight: 700, cursor: "pointer" }}>{t}</button>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={{ display: "block", fontFamily: "Space Mono, monospace", fontSize: 9, fontWeight: 700, color: C.dust, letterSpacing: 1.2, marginBottom: 8, textTransform: "uppercase" }}>Access</label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {RESTROOM_ACCESS.map(t => (
              <button key={t} onClick={() => setNewRestroom(p => ({ ...p, access: t }))} style={{ padding: "7px 12px", borderRadius: 20, border: `1.5px solid ${newRestroom.access === t ? C.sage : C.parchmentDeep}`, background: newRestroom.access === t ? C.sageSoft : "#231408", color: newRestroom.access === t ? C.sageDark : C.dust, fontFamily: "Space Mono, monospace", fontSize: 9, fontWeight: 700, cursor: "pointer" }}>{t}</button>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={{ display: "block", fontFamily: "Space Mono, monospace", fontSize: 9, fontWeight: 700, color: C.dust, letterSpacing: 1.2, marginBottom: 8, textTransform: "uppercase" }}>Cleanliness</label>
          <div style={{ display: "flex", gap: 6 }}>
            {CLEAN_OPTIONS.map(t => (
              <button key={t} onClick={() => setNewRestroom(p => ({ ...p, cleanliness: t }))} style={{ flex: 1, padding: "9px 4px", borderRadius: 10, border: `1.5px solid ${newRestroom.cleanliness === t ? ratingColor(t).border : C.parchmentDeep}`, background: newRestroom.cleanliness === t ? ratingColor(t).bg : "#231408", color: newRestroom.cleanliness === t ? ratingColor(t).color : C.dust, fontFamily: "Space Mono, monospace", fontSize: 9, fontWeight: 700, cursor: "pointer" }}>{t}</button>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={{ display: "block", fontFamily: "Space Mono, monospace", fontSize: 9, fontWeight: 700, color: C.dust, letterSpacing: 1.2, marginBottom: 8, textTransform: "uppercase" }}>Safety — Day</label>
          <div style={{ display: "flex", gap: 6 }}>
            {SAFETY_OPTIONS.map(t => (
              <button key={t} onClick={() => setNewRestroom(p => ({ ...p, safetyDay: t }))} style={{ flex: 1, padding: "9px 4px", borderRadius: 10, border: `1.5px solid ${newRestroom.safetyDay === t ? ratingColor(t).border : C.parchmentDeep}`, background: newRestroom.safetyDay === t ? ratingColor(t).bg : "#231408", color: newRestroom.safetyDay === t ? ratingColor(t).color : C.dust, fontFamily: "Space Mono, monospace", fontSize: 9, fontWeight: 700, cursor: "pointer" }}>{t}</button>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={{ display: "block", fontFamily: "Space Mono, monospace", fontSize: 9, fontWeight: 700, color: C.dust, letterSpacing: 1.2, marginBottom: 8, textTransform: "uppercase" }}>Safety — Night</label>
          <div style={{ display: "flex", gap: 6 }}>
            {[...SAFETY_OPTIONS, "N/A"].map(t => (
              <button key={t} onClick={() => setNewRestroom(p => ({ ...p, safetyNight: t }))} style={{ flex: 1, padding: "9px 4px", borderRadius: 10, border: `1.5px solid ${newRestroom.safetyNight === t ? ratingColor(t).border : C.parchmentDeep}`, background: newRestroom.safetyNight === t ? ratingColor(t).bg : "#231408", color: newRestroom.safetyNight === t ? ratingColor(t).color : C.dust, fontFamily: "Space Mono, monospace", fontSize: 9, fontWeight: 700, cursor: "pointer" }}>{t}</button>
            ))}
          </div>
        </div>

        <InputField label="Number of Stalls" value={newRestroom.stalls} onChange={v => setNewRestroom(p => ({ ...p, stalls: v }))} placeholder="3" type="number" />
        <div style={{ marginBottom: 14 }}>
          <label style={{ display: "block", fontFamily: "Space Mono, monospace", fontSize: 9, fontWeight: 700, color: C.dust, letterSpacing: 1.2, marginBottom: 6, textTransform: "uppercase" }}>Notes</label>
          <textarea value={newRestroom.notes} onChange={e => setNewRestroom(p => ({ ...p, notes: e.target.value }))} placeholder="Tips for other nomads — lighting, location inside building, etc." style={{ width: "100%", background: "#231408", border: `1.5px solid ${C.parchmentDeep}`, borderRadius: 10, padding: "12px 14px", fontFamily: "Crimson Pro, serif", fontSize: 14, color: C.parchment, outline: "none", minHeight: 80, resize: "vertical" }} />
        </div>

        {submitSuccess ? (
          <div style={{ background: C.sageSoft, border: `1.5px solid ${C.sageLight}`, borderRadius: 12, padding: 16, textAlign: "center" }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>✅</div>
            <div style={{ fontFamily: "Playfair Display, serif", fontSize: 16, fontWeight: 700, color: C.sageDark }}>Restroom submitted — thank you!</div>
          </div>
        ) : locationMode === "gps" && !formGPS ? (
          <div style={{ textAlign: "center", padding: "12px 0", color: C.dust, fontFamily: "Crimson Pro, serif", fontSize: 12, fontStyle: "italic" }}>Share your location above to submit.</div>
        ) : locationMode === "manual" && (!manualLocation.trim() || !manualAck) ? (
          <div style={{ textAlign: "center", padding: "12px 0", color: C.dust, fontFamily: "Crimson Pro, serif", fontSize: 12, fontStyle: "italic" }}>Enter an approximate location and confirm the checkbox above to submit.</div>
        ) : (
          <Btn onClick={handleSubmit}>🚻 SUBMIT RESTROOM</Btn>
        )}
      </div>
    );
  }

  return (
    <div style={{ padding: "20px 16px" }}>
      <SectionHeader icon="🚻" title="Restrooms" />
      {Toggle}
      <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 16, color: C.parchment, fontStyle: "italic", marginBottom: 14 }}>
        Clean, safe restrooms reported by the community — near {userLocation || "you"}
      </div>

      <div style={{ position: "relative", marginBottom: 14 }}>
        <span style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", fontSize: 14 }}>🔍</span>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or type..." autoComplete="off" style={{ width: "100%", background: "#231408", border: `1.5px solid ${C.parchmentDeep}`, borderRadius: 12, padding: "12px 14px 12px 38px", fontFamily: "Crimson Pro, serif", fontSize: 14, color: "#f0e8d8", outline: "none", boxSizing: "border-box" }} />
        {search && <button onClick={() => setSearch("")} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: C.dust, cursor: "pointer", fontSize: 16 }}>×</button>}
      </div>

      <div style={{ display: "flex", gap: 6, marginBottom: 16, overflowX: "auto", paddingBottom: 4 }}>
        {["All", "Free", "Felt Safe", "Has Reports", ...RESTROOM_TYPES].map(f => (
          <button key={f} onClick={() => setActiveFilter(f)} style={{ flexShrink: 0, padding: "7px 14px", background: activeFilter === f ? `linear-gradient(135deg, ${C.sunrise}, ${C.canyon})` : "#231408", border: `1.5px solid ${activeFilter === f ? C.sunrise : C.parchmentDeep}`, borderRadius: 20, fontFamily: "Space Mono, monospace", fontSize: 9, fontWeight: 700, color: activeFilter === f ? "#fff" : C.dust, cursor: "pointer" }}>{f}</button>
        ))}
      </div>

      <div style={{ fontFamily: "Space Mono, monospace", fontSize: 9, color: C.dust, marginBottom: 12 }}>{filtered.length} RESTROOM{filtered.length !== 1 ? "S" : ""}</div>

      {filtered.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px 20px", color: C.dust, fontFamily: "Crimson Pro, serif", fontStyle: "italic" }}>
          No restrooms reported yet. Be the first to add one!
        </div>
      ) : filtered.map(r => (
        <div key={r.id} onClick={() => setSelectedId(r.id)} style={{ background: "#231408", border: `1.5px solid ${r.openReports?.length > 0 ? C.red : C.parchmentDeep}`, borderRadius: 14, padding: 16, marginBottom: 12, cursor: "pointer", position: "relative" }}>
          {r.openReports?.length > 0 && (
            <div style={{ position: "absolute", top: 10, right: 10, background: C.red, color: "#fff", borderRadius: 6, padding: "2px 8px", fontFamily: "Space Mono, monospace", fontSize: 9, fontWeight: 700 }}>⚠️ REPORT</div>
          )}
          <div style={{ fontFamily: "Playfair Display, serif", fontSize: 15, fontWeight: 700, color: "#e8d5b8", marginBottom: 2 }}>{r.name}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
            {r.lat && r.lng ? (
              <span style={{ background: C.sageSoft, color: C.sageDark, border: `1px solid ${C.sageLight}`, borderRadius: 6, padding: "2px 7px", fontFamily: "Space Mono, monospace", fontSize: 8, fontWeight: 700 }}>🛰️ GPS VERIFIED</span>
            ) : (
              <span style={{ background: C.sunriseSoft, color: C.canyon, border: `1px solid ${C.canyonLight}`, borderRadius: 6, padding: "2px 7px", fontFamily: "Space Mono, monospace", fontSize: 8, fontWeight: 700 }}>📝 FROM MEMORY</span>
            )}
          </div>
          <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 12, color: C.dirt, fontStyle: "italic", marginBottom: 8 }}>
            {r.type} · {r.access}{r.distanceMiles != null ? ` · ${r.distanceMiles.toFixed(1)} mi` : r.manualLocation ? ` · ${r.manualLocation}` : ""}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            <RatingPill label="Clean" value={r.cleanliness} />
            <RatingPill label="Day" value={r.safetyDay} />
            <RatingPill label="Night" value={r.safetyNight} />
          </div>
        </div>
      ))}

      <button onClick={() => setShowAddForm(true)} style={{ width: "100%", background: "none", border: `1.5px dashed ${C.sageLight}`, borderRadius: 14, padding: 16, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginTop: 4 }}>
        <span style={{ fontSize: 20 }}>🚻</span>
        <div style={{ textAlign: "left" }}>
          <div style={{ fontFamily: "Space Mono, monospace", fontSize: 10, fontWeight: 700, color: C.sageDark }}>ADD A RESTROOM</div>
          <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 12, color: C.dirt, fontStyle: "italic" }}>Help other nomads stay safe and comfortable</div>
        </div>
      </button>
    </div>
  );
}
// ─── ROADS TAB ───────────────────────────────────────────────
function RoadsTab({ isFounder, user, userProfile, onOpenMessages, onProfileUpdate, onNavigateToProfile }) {
  const [view, setView] = useState("browse");
  const [activeFilter, setActiveFilter] = useState("All");
  const [seekingFilter, setSeekingFilter] = useState("All");
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [userGPS, setUserGPS] = useState(null);

  const liked = new Set(userProfile?.likedUids || []);
  const passed = new Set(userProfile?.passedUids || []);
  const blocked = new Set(userProfile?.blockedUids || []);
  const friends = new Set(userProfile?.friends || []);
  const sentRequests = new Set(userProfile?.friendRequestsSent || []);
  const receivedRequests = new Set(userProfile?.friendRequestsReceived || []);
  const friendCooldowns = userProfile?.friendRequestCooldowns || {};
  const messageCooldowns = userProfile?.messageCooldowns || {};

  const RIG_FILTERS = ["All","Skoolie","Van","Bus","Truck Camper","Car Camp","Not set"];
  const SEEKING_FILTERS = ["All","Romance","Friendship","Travel Companion","Convoy Partner"];

  useEffect(() => {
    const q = query(collection(db, "users"), limit(100));
    const unsub = onSnapshot(q, snap => {
      setAllUsers(snap.docs.map(d => ({ uid: d.id, ...d.data() })).filter(u => u.uid !== user.uid));
      setLoadingUsers(false);
    }, err => { console.error("Roads users error:", err); setLoadingUsers(false); });
    return () => unsub();
  }, [user.uid]);

  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      pos => {
        const lat = Math.round(pos.coords.latitude * 100) / 100;
        const lng = Math.round(pos.coords.longitude * 100) / 100;
        setUserGPS({ lat, lng });
        updateDoc(doc(db, "users", user.uid), { roadsLat: lat, roadsLng: lng }).catch(() => null);
      },
      () => null,
      { timeout: 10000, maximumAge: 600000 }
    );
  }, [user.uid]);

  const distanceTo = (n) => (userGPS && n.roadsLat != null && n.roadsLng != null) ? getDistanceMiles(userGPS.lat, userGPS.lng, n.roadsLat, n.roadsLng) : null;

  const visible = allUsers.filter(n => {
    if (blocked.has(n.uid)) return false;
    if (passed.has(n.uid)) return false;
    if (n.roadsVisible === false) return false;
    if (!n.ageVerified) return false;
    if (activeFilter !== "All" && (n.rigType || "Not set") !== activeFilter) return false;
    if (seekingFilter !== "All" && !(n.seeking || []).includes(seekingFilter)) return false;
    return true;
  }).sort((a, b) => {
    const da = distanceTo(a), db_ = distanceTo(b);
    if (da == null && db_ == null) return 0;
    if (da == null) return 1;
    if (db_ == null) return -1;
    return da - db_;
  });

  const patchLocal = (patch) => { if (onProfileUpdate) onProfileUpdate({ ...userProfile, ...patch }); };

  const toggleLike = async (uid, e) => {
    e.stopPropagation();
    const next = liked.has(uid) ? [...liked].filter(x => x !== uid) : [...liked, uid];
    patchLocal({ likedUids: next });
    await toggleRoadsAction(user.uid, "likedUids", uid, !liked.has(uid));
  };

  const handlePass = async (uid, e) => {
    e.stopPropagation();
    patchLocal({ passedUids: [...passed, uid] });
    await toggleRoadsAction(user.uid, "passedUids", uid, true);
    if (selectedProfile === uid) setSelectedProfile(null);
  };

  const handleBlock = async (uid) => {
    patchLocal({ blockedUids: [...blocked, uid] });
    await toggleRoadsAction(user.uid, "blockedUids", uid, true);
    setSelectedProfile(null);
  };

  const handleSendFriendRequest = async (uid) => {
    if (cooldownDaysLeft(friendCooldowns, uid) > 0) return;
    patchLocal({ friendRequestsSent: [...sentRequests, uid], friendRequestCooldowns: { ...friendCooldowns, [uid]: new Date().toISOString() } });
    await sendFriendRequest(user.uid, uid);
    await setFriendRequestCooldown(user.uid, uid);
  };

  const handleAcceptFriend = async (uid) => {
    patchLocal({ friends: [...friends, uid], friendRequestsReceived: [...receivedRequests].filter(x => x !== uid) });
    await acceptFriendRequest(user.uid, uid);
  };

  const handleDeclineFriend = async (uid) => {
    patchLocal({ friendRequestsReceived: [...receivedRequests].filter(x => x !== uid) });
    await declineFriendRequest(user.uid, uid);
  };

  const friendButton = (nomad) => {
    if (friends.has(nomad.uid)) return <div style={{ flex:1, textAlign:"center", background:C.sageSoft, border:`1.5px solid ${C.sageLight}`, borderRadius:12, padding:"13px", fontFamily:"Space Mono, monospace", fontSize:11, fontWeight:700, color:C.sageDark }}>✓ FRIENDS</div>;
    if (receivedRequests.has(nomad.uid)) return (
      <div style={{ display:"flex", gap:6, flex:1 }}>
        <button onClick={(e) => { e.stopPropagation(); handleAcceptFriend(nomad.uid); }} style={{ flex:1, background:`linear-gradient(135deg, ${C.sunrise}, ${C.canyon})`, border:"none", borderRadius:12, padding:"13px", fontFamily:"Space Mono, monospace", fontSize:10, fontWeight:700, color:"#fff", cursor:"pointer" }}>ACCEPT</button>
        <button onClick={(e) => { e.stopPropagation(); handleDeclineFriend(nomad.uid); }} style={{ flex:1, background:"none", border:`1.5px solid ${C.parchmentDeep}`, borderRadius:12, padding:"13px", fontFamily:"Space Mono, monospace", fontSize:10, fontWeight:700, color:C.dust, cursor:"pointer" }}>DECLINE</button>
      </div>
    );
    if (sentRequests.has(nomad.uid)) return <div style={{ flex:1, textAlign:"center", background:"none", border:`1.5px solid ${C.parchmentDeep}`, borderRadius:12, padding:"13px", fontFamily:"Space Mono, monospace", fontSize:10, fontWeight:700, color:C.dust }}>REQUEST SENT</div>;
    const friendDaysLeft = cooldownDaysLeft(friendCooldowns, nomad.uid);
    if (friendDaysLeft > 0) return <div style={{ flex:1, textAlign:"center", background:"none", border:`1.5px solid ${C.parchmentDeep}`, borderRadius:12, padding:"13px", fontFamily:"Space Mono, monospace", fontSize:9, fontWeight:700, color:C.dust }}>TRY AGAIN IN {friendDaysLeft}D</div>;
    return <button onClick={(e) => { e.stopPropagation(); handleSendFriendRequest(nomad.uid); }} style={{ flex:1, background:"none", border:`1.5px solid ${C.sage}`, borderRadius:12, padding:"13px", fontFamily:"Space Mono, monospace", fontSize:11, fontWeight:700, color:C.sage, cursor:"pointer" }}>🤝 ADD FRIEND</button>;
  };

  if (selectedProfile) {
    const nomad = allUsers.find(n => n.uid === selectedProfile);
    if (!nomad) { setSelectedProfile(null); return null; }
    return (
      <div style={{ padding: "20px 16px" }}>
        <button onClick={() => setSelectedProfile(null)} style={{ background: "none", border: "none", color: C.sage, fontFamily: "Space Mono, monospace", fontSize: 10, cursor: "pointer", marginBottom: 16, padding: 0 }}>← Back to Roads</button>
        <div style={{ background: `linear-gradient(160deg, ${C.pine}, ${C.sageDark})`, borderRadius: 16, height: 200, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 80, marginBottom: 16, position: "relative" }}>
          {(nomad.rigPhotoURL || nomad.photoURL) ? <img src={nomad.rigPhotoURL || nomad.photoURL} alt="" style={{ width:90, height:90, borderRadius:"50%", border:`3px solid ${C.sunrise}`, objectFit:"cover" }} /> : "🔥"}
          {nomad.isFounder && <div style={{ position: "absolute", bottom: 12, right: 12 }}><FounderBadge size="sm" /></div>}
        </div>
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontFamily: "Playfair Display, serif", fontSize: 24, fontWeight: 700, color: C.parchment, marginBottom: 4 }}>{nomad.displayName || "Nomad"}</div>
          <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 12, color: C.dust, fontStyle: "italic" }}>{nomad.rigType || "Rig type not set"}{distanceTo(nomad) != null ? ` · ${distanceTo(nomad).toFixed(0)} mi away` : ""}</div>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 16 }}>
          {(nomad.seeking || []).length === 0 ? <span style={{ fontFamily: "Crimson Pro, serif", fontSize: 12, color: C.dust, fontStyle:"italic" }}>No interests listed yet</span> : nomad.seeking.map(s => <span key={s} style={{ background: C.sunriseSoft, color: C.canyon, border: `1px solid ${C.canyonLight}`, borderRadius: 20, padding: "4px 12px", fontFamily: "Space Mono, monospace", fontSize: 9, fontWeight: 700 }}>{s}</span>)}
        </div>
        <Card accent={C.sage}>
          <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 15, color: C.parchment, lineHeight: 1.6, fontStyle: "italic" }}>&#x201C;{nomad.bio || "This nomad hasn't written a bio yet."}&#x201D;</div>
        </Card>
        <div style={{ display: "flex", gap: 10, marginTop: 16, marginBottom: 10 }}>
          <button onClick={(e) => toggleLike(nomad.uid, e)} style={{ flex: 1, background: liked.has(nomad.uid) ? `linear-gradient(135deg, ${C.sunrise}, ${C.canyon})` : C.parchment, border: `1.5px solid ${liked.has(nomad.uid) ? C.sunrise : C.parchmentDeep}`, borderRadius: 12, padding: "13px", fontFamily: "Space Mono, monospace", fontSize: 11, fontWeight: 700, color: liked.has(nomad.uid) ? "#fff" : C.bark, cursor: "pointer" }}>
            {liked.has(nomad.uid) ? "❤️ LIKED" : "🤍 LIKE"}
          </button>
          <button onClick={(e) => handlePass(nomad.uid, e)} style={{ flex: 1, background: "none", border: `1.5px solid ${C.parchmentDeep}`, borderRadius: 12, padding: "13px", fontFamily: "Space Mono, monospace", fontSize: 11, fontWeight: 700, color: C.dust, cursor: "pointer" }}>PASS</button>
        </div>
        <div style={{ display:"flex", gap:10, marginBottom:10 }}>
          {friendButton(nomad)}
        </div>
        <Btn variant="primary" style={{ marginBottom: 8 }} onClick={async () => {
          if (!user) return;
          const convoId = [user.uid, nomad.uid].sort().join("_");
          const alreadyTalked = await hasExistingMessages(convoId);
          if (!alreadyTalked) {
            const daysLeft = cooldownDaysLeft(messageCooldowns, nomad.uid);
            if (daysLeft > 0) { alert(`You've already reached out to ${nomad.displayName || "this nomad"} recently. You can message them again in ${daysLeft} day${daysLeft === 1 ? "" : "s"}.`); return; }
          }
          await setDoc(doc(db, "conversations", convoId), {
            participants: [user.uid, nomad.uid],
            participantNames: [
              { uid: user.uid, name: userProfile?.displayName || "You" },
              { uid: nomad.uid, name: nomad.displayName || "Nomad" }
            ],
            lastMessage: "",
            lastMessageAt: serverTimestamp(),
          }, { merge: true });
          if (!alreadyTalked) {
            patchLocal({ messageCooldowns: { ...messageCooldowns, [nomad.uid]: new Date().toISOString() } });
            await setMessageCooldown(user.uid, nomad.uid);
          }
          onOpenMessages({ id: convoId });
        }}>💬 SEND MESSAGE {!isFounder && "(1 Credit)"}</Btn>
        <button onClick={() => handleBlock(nomad.uid)} style={{ width: "100%", background: "none", border: "none", fontFamily: "Space Mono, monospace", fontSize: 9, color: C.red, cursor: "pointer", padding: "8px 0", letterSpacing: 0.5 }}>🚫 Block & Hide This Profile</button>
      </div>
    );
  }

  return (
    <div style={{ padding: "20px 16px" }}>
      <SectionHeader icon="🛣️" title="Roads" />
      {!userProfile?.ageVerified ? (
        <div style={{ background: C.sunriseSoft, border: `1.5px solid ${C.canyonLight}`, borderRadius: 14, padding: 20, textAlign: "center" }}>
          <div style={{ fontSize: 32, marginBottom: 10 }}>🔞</div>
          <div style={{ fontFamily: "Playfair Display, serif", fontSize: 17, fontWeight: 700, color: C.bark, marginBottom: 8 }}>Roads requires you to be 18+</div>
          <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 13, color: C.dirt, lineHeight: 1.5 }}>Add your birthdate on your Profile tab to unlock Roads and start meeting other nomads.</div>
        </div>
      ) : <>
      {!isProfileComplete(userProfile) && (
        <div style={{ background: C.sunriseSoft, border: `1.5px solid ${C.canyonLight}`, borderRadius: 14, padding: "16px 18px", marginBottom: 16, display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{ fontSize: 28, flexShrink: 0 }}>📝</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: "Playfair Display, serif", fontSize: 15, fontWeight: 700, color: C.bark, marginBottom: 3 }}>Finish your profile</div>
            <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 12, color: C.dirt, lineHeight: 1.4 }}>Add a bio, rig type, and what you're seeking so other nomads know who you are.</div>
          </div>
          <button onClick={onNavigateToProfile} style={{ background: `linear-gradient(135deg, ${C.sunrise}, ${C.canyon})`, border: "none", borderRadius: 10, padding: "9px 14px", fontFamily: "Space Mono, monospace", fontSize: 9, fontWeight: 700, color: "#fff", cursor: "pointer", flexShrink: 0, whiteSpace: "nowrap" }}>EDIT →</button>
        </div>
      )}
      <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 14, color: C.parchment, fontStyle: "italic", marginBottom: 16 }}>Meet nomads near you — wherever the road takes you</div>
      <div style={{ display: "flex", gap: 6, marginBottom: 20 }}>
        {[["browse","🗺 Near Me"],["liked","❤️ Liked"],["requests","🤝 Requests"]].map(([k,l]) => (
          <button key={k} onClick={() => setView(k)} style={{ flex: 1, padding: "9px 4px", background: view === k ? `linear-gradient(135deg, ${C.sunrise}, ${C.canyon})` : "#231408", color: view === k ? "#fff" : C.dust, border: `1.5px solid ${view === k ? C.sunrise : C.parchmentDeep}`, borderRadius: 10, fontFamily: "Space Mono, monospace", fontSize: 9, fontWeight: 700, letterSpacing: 0.5, cursor: "pointer" }}>{l}{k==="requests" && receivedRequests.size > 0 ? ` (${receivedRequests.size})` : ""}</button>
        ))}
      </div>

      {view === "browse" && <>
        <div style={{ display: "flex", gap: 6, marginBottom: 10, overflowX: "auto", paddingBottom: 4 }}>
          {RIG_FILTERS.map(f => <button key={f} onClick={() => setActiveFilter(f)} style={{ flexShrink: 0, padding: "6px 12px", background: activeFilter === f ? `linear-gradient(135deg, ${C.sunrise}, ${C.canyon})` : "#231408", border: `1.5px solid ${activeFilter === f ? C.sunrise : C.parchmentDeep}`, borderRadius: 20, fontFamily: "Space Mono, monospace", fontSize: 8, fontWeight: 700, color: activeFilter === f ? "#fff" : C.dust, cursor: "pointer" }}>{f}</button>)}
        </div>
        <div style={{ display: "flex", gap: 6, marginBottom: 16, overflowX: "auto", paddingBottom: 4 }}>
          {SEEKING_FILTERS.map(f => <button key={f} onClick={() => setSeekingFilter(f)} style={{ flexShrink: 0, padding: "6px 12px", background: seekingFilter === f ? C.sageSoft : "#231408", border: `1.5px solid ${seekingFilter === f ? C.sageLight : C.parchmentDeep}`, borderRadius: 20, fontFamily: "Space Mono, monospace", fontSize: 8, fontWeight: 700, color: seekingFilter === f ? C.sageDark : C.dust, cursor: "pointer" }}>{f}</button>)}
        </div>
        <div style={{ fontFamily: "Space Mono, monospace", fontSize: 9, color: C.dust, marginBottom: 12 }}>{loadingUsers ? "LOADING..." : `${visible.length} NOMAD${visible.length !== 1 ? "S" : ""} ON THE ROAD`}</div>
        {!loadingUsers && visible.length === 0 ? (
          <div style={{ textAlign: "center", padding: "40px 20px", color: C.dust, fontFamily: "Crimson Pro, serif", fontStyle: "italic" }}>No nomads match your filters right now. The road is long — check back soon.</div>
        ) : visible.map(nomad => (
          <div key={nomad.uid} onClick={() => setSelectedProfile(nomad.uid)} style={{ background: "#231408", border: `1.5px solid ${C.parchmentDeep}`, borderRadius: 14, marginBottom: 12, overflow: "hidden", cursor: "pointer" }}>
            <div style={{ height: 140, background: `linear-gradient(160deg, ${C.pine}, ${C.sageDark})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 64, position: "relative" }}>
              {(nomad.rigPhotoURL || nomad.photoURL) ? <img src={nomad.rigPhotoURL || nomad.photoURL} alt="" style={{ width:64, height:64, borderRadius:"50%", border:`2px solid ${C.sunrise}`, objectFit:"cover" }} /> : "🔥"}
              {nomad.isFounder && <div style={{ position: "absolute", top: 10, right: 10 }}><FounderBadge size="sm" /></div>}
            </div>
            <div style={{ padding: "12px 14px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                <div>
                  <div style={{ fontFamily: "Playfair Display, serif", fontSize: 16, fontWeight: 700, color: C.parchment }}>{nomad.displayName || "Nomad"}</div>
                  <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 12, color: C.dust, fontStyle: "italic" }}>{nomad.rigType || "Rig type not set"}{distanceTo(nomad) != null ? ` · ${distanceTo(nomad).toFixed(0)} mi away` : ""}</div>
                </div>
                <button onClick={(e) => toggleLike(nomad.uid, e)} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", padding: 0 }}>{liked.has(nomad.uid) ? "❤️" : "🤍"}</button>
              </div>
              <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 13, color: C.parchment, lineHeight: 1.4, marginBottom: 8 }}>{(nomad.bio || "No bio yet.").substring(0, 85)}{(nomad.bio||"").length > 85 ? "..." : ""}</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                {(nomad.seeking || []).map(s => <span key={s} style={{ background: C.sunriseSoft, color: C.canyon, border: `1px solid ${C.canyonLight}`, borderRadius: 20, padding: "2px 8px", fontFamily: "Space Mono, monospace", fontSize: 8, fontWeight: 700 }}>{s}</span>)}
              </div>
            </div>
          </div>
        ))}
      </>}

      {view === "liked" && <>
        {visible.filter(n => liked.has(n.uid)).length === 0 ? (
          <div style={{ textAlign: "center", padding: "40px 20px", color: C.dust, fontFamily: "Crimson Pro, serif", fontStyle: "italic" }}>You haven't liked anyone yet. Get out there!</div>
        ) : visible.filter(n => liked.has(n.uid)).map(nomad => (
          <div key={nomad.uid} onClick={() => setSelectedProfile(nomad.uid)} style={{ background: "#231408", border: `1.5px solid ${C.parchmentDeep}`, borderRadius: 14, marginBottom: 12, padding: 14, display: "flex", gap: 12, alignItems: "center", cursor: "pointer" }}>
            <div style={{ width:48, height:48, borderRadius:"50%", background:`linear-gradient(160deg, ${C.pine}, ${C.sageDark})`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:24, overflow:"hidden" }}>{(nomad.rigPhotoURL || nomad.photoURL) ? <img src={nomad.rigPhotoURL || nomad.photoURL} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} /> : "🔥"}</div>
            <div>
              <div style={{ fontFamily: "Playfair Display, serif", fontSize: 15, fontWeight: 700, color: C.parchment }}>{nomad.displayName || "Nomad"}</div>
              <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 12, color: C.dust, fontStyle: "italic" }}>{nomad.rigType || "Rig type not set"}</div>
            </div>
          </div>
        ))}
      </>}

      {view === "requests" && <>
        <div style={{ fontFamily:"Space Mono, monospace", fontSize:10, fontWeight:700, color:C.dust, marginBottom:10, letterSpacing:0.5 }}>INCOMING REQUESTS</div>
        {allUsers.filter(n => receivedRequests.has(n.uid)).length === 0 ? (
          <div style={{ textAlign: "center", padding: "20px 20px 30px", color: C.dust, fontFamily: "Crimson Pro, serif", fontStyle: "italic" }}>No pending friend requests.</div>
        ) : allUsers.filter(n => receivedRequests.has(n.uid)).map(nomad => (
          <div key={nomad.uid} style={{ background: "#231408", border: `1.5px solid ${C.parchmentDeep}`, borderRadius: 14, marginBottom: 12, padding: 14 }}>
            <div style={{ display:"flex", gap:12, alignItems:"center", marginBottom:10 }}>
              <div style={{ width:40, height:40, borderRadius:"50%", background:`linear-gradient(160deg, ${C.pine}, ${C.sageDark})`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:20, overflow:"hidden" }}>{(nomad.rigPhotoURL || nomad.photoURL) ? <img src={nomad.rigPhotoURL || nomad.photoURL} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} /> : "🔥"}</div>
              <div style={{ fontFamily: "Playfair Display, serif", fontSize: 15, fontWeight: 700, color: C.parchment }}>{nomad.displayName || "Nomad"}</div>
            </div>
            <div style={{ display:"flex", gap:8 }}>
              <button onClick={() => handleAcceptFriend(nomad.uid)} style={{ flex:1, background:`linear-gradient(135deg, ${C.sunrise}, ${C.canyon})`, border:"none", borderRadius:10, padding:"10px", fontFamily:"Space Mono, monospace", fontSize:10, fontWeight:700, color:"#fff", cursor:"pointer" }}>ACCEPT</button>
              <button onClick={() => handleDeclineFriend(nomad.uid)} style={{ flex:1, background:"none", border:`1.5px solid ${C.parchmentDeep}`, borderRadius:10, padding:"10px", fontFamily:"Space Mono, monospace", fontSize:10, fontWeight:700, color:C.dust, cursor:"pointer" }}>DECLINE</button>
            </div>
          </div>
        ))}
        <div style={{ fontFamily:"Space Mono, monospace", fontSize:10, fontWeight:700, color:C.dust, margin:"20px 0 10px", letterSpacing:0.5 }}>SENT REQUESTS</div>
        {allUsers.filter(n => sentRequests.has(n.uid)).length === 0 ? (
          <div style={{ textAlign: "center", padding: "10px 20px 20px", color: C.dust, fontFamily: "Crimson Pro, serif", fontStyle: "italic" }}>No outgoing requests waiting.</div>
        ) : allUsers.filter(n => sentRequests.has(n.uid)).map(nomad => (
          <div key={nomad.uid} style={{ background: "#231408", border: `1.5px solid ${C.parchmentDeep}`, borderRadius: 14, marginBottom: 10, padding: "12px 14px", display:"flex", alignItems:"center", gap:10 }}>
            <div style={{ fontFamily: "Playfair Display, serif", fontSize: 14, fontWeight: 700, color: C.parchment }}>{nomad.displayName || "Nomad"}</div>
            <span style={{ marginLeft:"auto", fontFamily:"Space Mono, monospace", fontSize:9, color:C.dust }}>PENDING</span>
          </div>
        ))}
      </>}
      </>}
    </div>
  );
}

// ─── MARKET TAB ──────────────────────────────────────────────
function MarketTab({ isFounder, user, userProfile }) {
  const CATS = ["All", "Vehicles", "Solar & Power", "Conversion", "Gear", "Free"];
  const [cat, setCat] = useState("All");
  const [listings, setListings] = useState([]);
  const [selectedListing, setSelectedListing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ title: "", price: "", description: "", category: "Gear", condition: "Good", location: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Load real listings from Firebase
  useEffect(() => {
    const q = query(collection(db, "listings"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(q, snap => {
      setListings(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, err => console.error("Listings error:", err));
    return () => unsub();
  }, []);

  const filtered = cat === "All" ? listings : listings.filter(l => l.category === cat);

  const CATEGORY_EMOJIS = {
    "Vehicles": "🚌", "Solar & Power": "☀️", "Conversion": "🔨",
    "Gear": "🎒", "Free": "🎁"
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.price || !formData.description) return;
    setSubmitting(true);
    try {
      await addDoc(collection(db, "listings"), {
        title: formData.title,
        price: formData.price.startsWith("$") ? formData.price : `$${formData.price}`,
        description: formData.description,
        category: formData.category,
        condition: formData.condition,
        location: formData.location || "On the Road",
        sellerName: userProfile?.displayName || user?.email?.split("@")[0] || "Nomad",
        sellerUid: user?.uid || "",
        isFounder: isFounder || false,
        verified: false,
        emoji: CATEGORY_EMOJIS[formData.category] || "📦",
        createdAt: serverTimestamp(),
      });
      setSubmitSuccess(true);
      setTimeout(() => {
        setSubmitSuccess(false);
        setShowForm(false);
        setFormData({ title: "", price: "", description: "", category: "Gear", condition: "Good", location: "" });
      }, 2000);
    } catch (err) {
      console.error("Error submitting listing:", err);
      alert("Error submitting — check console");
    }
    setSubmitting(false);
  };

  // Detail view
  if (selectedListing) {
    const l = listings.find(x => x.id === selectedListing);
    if (!l) { setSelectedListing(null); return null; }
    return (
      <div style={{ padding: "20px 16px" }}>
        <button onClick={() => setSelectedListing(null)} style={{ background: "none", border: "none", color: C.sage, fontFamily: "Space Mono, monospace", fontSize: 10, cursor: "pointer", marginBottom: 16, padding: 0 }}>← Back to Market</button>
        <div style={{ background: `linear-gradient(160deg, ${C.pine}, ${C.sageDark})`, borderRadius: 16, height: 180, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 72, marginBottom: 16, position: "relative" }}>
          {l.emoji || "📦"}
          <div style={{ position: "absolute", bottom: 12, right: 14, background: "rgba(0,0,0,0.6)", borderRadius: 8, padding: "4px 12px", fontFamily: "Playfair Display, serif", fontSize: 20, fontWeight: 900, color: "#fff" }}>{l.price}</div>
          {l.isFounder && <div style={{ position: "absolute", top: 12, right: 12 }}><FounderBadge size="sm" /></div>}
        </div>

        <div style={{ fontFamily: "Playfair Display, serif", fontSize: 22, fontWeight: 700, color: C.parchment, marginBottom: 6 }}>{l.title}</div>
        <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 13, color: C.dust, fontStyle: "italic", marginBottom: 16 }}>
          {l.category} · {l.condition} condition · {l.location}
        </div>

        <Card accent={C.sage}>
          <div style={{ fontFamily: "Space Mono, monospace", fontSize: 9, color: C.sageDark, letterSpacing: 1, marginBottom: 8 }}>📋 DESCRIPTION</div>
          <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 14, color: C.parchment, lineHeight: 1.6 }}>{l.description}</div>
        </Card>

        <div style={{ background: "#231408", border: `1.5px solid ${C.parchmentDeep}`, borderRadius: 14, padding: 16, marginBottom: 12 }}>
          <div style={{ fontFamily: "Space Mono, monospace", fontSize: 9, color: C.dust, letterSpacing: 1, marginBottom: 10 }}>👤 SELLER</div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 44, height: 44, borderRadius: "50%", background: `linear-gradient(135deg, ${C.sunrise}, ${C.canyon})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>🔥</div>
            <div>
              <div style={{ fontFamily: "Playfair Display, serif", fontSize: 15, fontWeight: 700, color: C.parchment }}>{l.sellerName}</div>
              <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 12, color: C.dust, fontStyle: "italic" }}>{l.location}</div>
            </div>
            {l.isFounder && <div style={{ marginLeft: "auto" }}><FounderBadge size="sm" /></div>}
          </div>
        </div>

        <Btn>📩 CONTACT SELLER</Btn>
        <Btn variant="sage" style={{ marginTop: 8 }}>🚩 Report Listing</Btn>
      </div>
    );
  }

  // Submit form
  if (showForm) {
    const CONDITIONS = ["New", "Like New", "Good", "Fair", "For Parts"];
    return (
      <div style={{ padding: "20px 16px" }}>
        <button onClick={() => setShowForm(false)} style={{ background: "none", border: "none", color: C.sage, fontFamily: "Space Mono, monospace", fontSize: 10, cursor: "pointer", marginBottom: 16, padding: 0 }}>← Cancel</button>
        <SectionHeader icon="🏪" title="List an Item" />
        <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 13, color: C.dust, fontStyle: "italic", marginBottom: 20 }}>Your listing will be visible to all RoadBurnAI users</div>

        <InputField label="Title" value={formData.title} onChange={v => setFormData(p => ({ ...p, title: v }))} placeholder="2004 Thomas Built Skoolie — Full Convert" />
        <InputField label="Price" value={formData.price} onChange={v => setFormData(p => ({ ...p, price: v }))} placeholder="28,500" prefix="$" />

        <div style={{ marginBottom: 14 }}>
          <label style={{ display: "block", fontFamily: "Space Mono, monospace", fontSize: 9, fontWeight: 700, color: C.dust, letterSpacing: 1.2, marginBottom: 6, textTransform: "uppercase" }}>Category</label>
          <select value={formData.category} onChange={e => setFormData(p => ({ ...p, category: e.target.value }))} style={{ width: "100%", background: "#231408", border: `1.5px solid ${C.parchmentDeep}`, borderRadius: 10, padding: "12px 14px", fontFamily: "Crimson Pro, serif", fontSize: 15, color: "#f0e8d8", outline: "none" }}>
            {["Vehicles", "Solar & Power", "Conversion", "Gear", "Free"].map(c => <option key={c}>{c}</option>)}
          </select>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={{ display: "block", fontFamily: "Space Mono, monospace", fontSize: 9, fontWeight: 700, color: C.dust, letterSpacing: 1.2, marginBottom: 6, textTransform: "uppercase" }}>Condition</label>
          <select value={formData.condition} onChange={e => setFormData(p => ({ ...p, condition: e.target.value }))} style={{ width: "100%", background: "#231408", border: `1.5px solid ${C.parchmentDeep}`, borderRadius: 10, padding: "12px 14px", fontFamily: "Crimson Pro, serif", fontSize: 15, color: "#f0e8d8", outline: "none" }}>
            {CONDITIONS.map(c => <option key={c}>{c}</option>)}
          </select>
        </div>

        <InputField label="Your Location" value={formData.location} onChange={v => setFormData(p => ({ ...p, location: v }))} placeholder="Flagstaff, AZ (or: On the Road)" />

        <div style={{ marginBottom: 14 }}>
          <label style={{ display: "block", fontFamily: "Space Mono, monospace", fontSize: 9, fontWeight: 700, color: C.dust, letterSpacing: 1.2, marginBottom: 6, textTransform: "uppercase" }}>Description</label>
          <textarea value={formData.description} onChange={e => setFormData(p => ({ ...p, description: e.target.value }))} placeholder="Describe your item — condition details, history, what's included..." style={{ width: "100%", background: "#231408", border: `1.5px solid ${C.parchmentDeep}`, borderRadius: 10, padding: "12px 14px", fontFamily: "Crimson Pro, serif", fontSize: 14, color: "#f0e8d8", outline: "none", minHeight: 100, resize: "vertical" }} />
        </div>

        {submitSuccess ? (
          <div style={{ background: C.sageSoft, border: `1.5px solid ${C.sageLight}`, borderRadius: 12, padding: 20, textAlign: "center" }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>✅</div>
            <div style={{ fontFamily: "Playfair Display, serif", fontSize: 16, fontWeight: 700, color: C.sageDark }}>Listing posted!</div>
            <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 13, color: C.dirt, fontStyle: "italic", marginTop: 4 }}>Your item is now live on the Marketplace.</div>
          </div>
        ) : (
          <Btn onClick={handleSubmit} style={{ background: submitting ? C.parchmentDeep : `linear-gradient(135deg, ${C.sunrise}, ${C.canyon})` }}>
            {submitting ? "POSTING..." : "🏪 POST LISTING"}
          </Btn>
        )}
      </div>
    );
  }

  // Main listing view
  return (
    <div style={{ padding: "20px 16px" }}>
      <SectionHeader icon="🏪" title="Marketplace" />
      <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 13, color: C.dust, fontStyle: "italic", marginBottom: 16 }}>GPS-aware listings — sellers move, so do their listings</div>

      <div style={{ display: "flex", gap: 6, marginBottom: 16, overflowX: "auto", paddingBottom: 4 }}>
        {CATS.map(c => <button key={c} onClick={() => setCat(c)} style={{ flexShrink: 0, padding: "7px 14px", background: cat === c ? `linear-gradient(135deg, ${C.sunrise}, ${C.canyon})` : "#231408", border: `1.5px solid ${cat === c ? C.sunrise : C.parchmentDeep}`, borderRadius: 20, fontFamily: "Space Mono, monospace", fontSize: 9, fontWeight: 700, color: cat === c ? "#fff" : C.dust, cursor: "pointer" }}>{c}</button>)}
      </div>

      <div style={{ background: `linear-gradient(135deg, ${C.sunriseSoft}, ${C.canyonSoft})`, border: `1.5px solid ${C.canyonLight}`, borderRadius: 14, padding: "14px 16px", marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div style={{ fontFamily: "Space Mono, monospace", fontSize: 9, fontWeight: 700, color: C.canyon, letterSpacing: 1 }}>YOUR CREDITS</div>
          <div style={{ fontFamily: "Playfair Display, serif", fontSize: 22, fontWeight: 900, color: C.bark }}>{isFounder ? "5 Credits" : "0 Credits"}</div>
        </div>
        <button onClick={async () => {
  const BUNDLES = [
    { label: "Starter", price: "$2.99", credits: 3, priceId: "price_1Tj5usHTLfUO55Yhg71ofW3G" },
    { label: "Regular", price: "$5.00", credits: 12, priceId: "price_1Tj66PHTLfUO55YhSIsLS8hb" },
    { label: "Power", price: "$10.00", credits: 25, priceId: "price_1Tj68gHTLfUO55Yh3HwgDBh1" },
    { label: "Dealer", price: "$25.00", credits: 65, priceId: "price_1Tj6DsHTLfUO55YhP5d76FH7" },
  ];
  const choice = window.prompt("Choose a bundle:\n1. Starter — $2.99 — 3 Credits\n2. Regular — $5.00 — 12 Credits\n3. Power — $10.00 — 25 Credits\n4. Dealer — $25.00 — 65 Credits\n\nEnter 1, 2, 3, or 4:");
  const bundle = BUNDLES[parseInt(choice) - 1];
  if (!bundle) return;
  try {
    const res = await fetch("https://roadburnai-proxy.skoolie309.workers.dev/stripe/create-checkout", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ priceId: bundle.priceId, userId: user?.uid || "", credits: bundle.credits }),
    });
    const data = await res.json();
    if (data.url) window.open(data.url, "_blank");
    else alert("Error creating checkout. Try again.");
  } catch { alert("Network error. Try again."); }
}} style={{ background: `linear-gradient(135deg, ${C.sunrise}, ${C.canyon})`, border: "none", borderRadius: 10, padding: "10px 16px", fontFamily: "Space Mono, monospace", fontSize: 11, fontWeight: 700, color: "#fff", cursor: "pointer" }}>BUY CREDITS</button>
      </div>

      {listings.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px 20px" }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🏪</div>
          <div style={{ fontFamily: "Playfair Display, serif", fontSize: 18, fontWeight: 700, color: C.parchment, marginBottom: 8 }}>No listings yet</div>
          <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 14, color: C.dust, fontStyle: "italic" }}>Be the first to list something!</div>
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px 20px", color: C.dust, fontFamily: "Crimson Pro, serif", fontStyle: "italic" }}>No listings in this category yet.</div>
      ) : filtered.map(l => (
        <div key={l.id} onClick={() => setSelectedListing(l.id)} style={{ background: "#231408", border: `1.5px solid ${C.parchmentDeep}`, borderRadius: 14, marginBottom: 12, overflow: "hidden", cursor: "pointer" }}>
          <div style={{ height: 120, background: `linear-gradient(160deg, ${C.pine}, ${C.sageDark})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 48, position: "relative" }}>
            {l.emoji || "📦"}
            <div style={{ position: "absolute", bottom: 8, right: 10, background: "rgba(0,0,0,0.5)", borderRadius: 6, padding: "3px 10px", fontFamily: "Playfair Display, serif", fontSize: 14, fontWeight: 900, color: "#fff" }}>{l.price}</div>
            {l.isFounder && <div style={{ position: "absolute", top: 8, right: 8 }}><FounderBadge size="sm" /></div>}
          </div>
          <div style={{ padding: 14 }}>
            <div style={{ fontFamily: "Playfair Display, serif", fontSize: 15, fontWeight: 700, color: C.parchment, marginBottom: 4 }}>{l.title}</div>
            <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 12, color: C.dust, fontStyle: "italic", marginBottom: 8 }}>{l.sellerName} · {l.location}</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              <span style={{ background: C.sageSoft, color: C.sageDark, border: `1px solid ${C.sageLight}`, borderRadius: 6, padding: "2px 8px", fontFamily: "Space Mono, monospace", fontSize: 8 }}>{l.category}</span>
              <span style={{ background: "#2c1a08", color: C.dust, border: `1px solid ${C.parchmentDeep}`, borderRadius: 6, padding: "2px 8px", fontFamily: "Space Mono, monospace", fontSize: 8 }}>{l.condition}</span>
            </div>
          </div>
        </div>
      ))}

      <button onClick={() => setShowForm(true)} style={{ width: "100%", background: "none", border: `1.5px dashed ${C.sageLight}`, borderRadius: 14, padding: 16, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginTop: 4 }}>
        <span style={{ fontSize: 20 }}>🏪</span>
        <div style={{ textAlign: "left" }}>
          <div style={{ fontFamily: "Space Mono, monospace", fontSize: 10, fontWeight: 700, color: C.sageDark }}>LIST AN ITEM</div>
          <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 12, color: C.dust, fontStyle: "italic" }}>Post to the RoadBurnAI Marketplace</div>
        </div>
      </button>
    </div>
  );
}

// ─── FORUM TAB ───────────────────────────────────────────────
function ForumTab({ isFounder }) {
  const [activeThread, setActiveThread] = useState(null);
  const [newPost, setNewPost] = useState("");

  if (activeThread) {
    const t = ALL_THREADS.find(x => x.id === activeThread);
    return (
      <div style={{ padding: "20px 16px" }}>
        <button onClick={() => setActiveThread(null)} style={{ background: "none", border: "none", color: C.sage, fontFamily: "Space Mono, monospace", fontSize: 10, cursor: "pointer", marginBottom: 16, padding: 0 }}>← Back to Forum</button>
        <div style={{ fontFamily: "Playfair Display, serif", fontSize: 19, fontWeight: 700, color: C.parchment, marginBottom: 6 }}>{t.title}</div>
        <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 12, color: C.dirt, fontStyle: "italic", marginBottom: 16 }}>{t.author} · {t.location} · {t.time}</div>
        <Card><div style={{ fontFamily: "Crimson Pro, serif", fontSize: 15, color: C.parchment, lineHeight: 1.6 }}>{t.preview} This is the full thread. Tap to add your reply below.</div></Card>
        <div style={{ marginTop: 8 }}>
          {isFounder && <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}><span style={{ fontFamily: "Space Mono, monospace", fontSize: 9, color: C.dust }}>REPLYING AS YOU</span><FounderBadge size="sm" /></div>}
          <textarea value={newPost} onChange={e => setNewPost(e.target.value)} placeholder="Add your reply..." style={{ width: "100%", background: "#231408", border: `1.5px solid ${C.parchmentDeep}`, borderRadius: 10, padding: "12px 14px", fontFamily: "Crimson Pro, serif", fontSize: 15, color: "#f0e8d8", outline: "none", minHeight: 80
          , resize: "vertical", marginBottom: 8 }} />
          <Btn>Post Reply</Btn>
        </div>
      </div>
    );
  }
  return (
    <div style={{ padding: "20px 16px" }}>
      <SectionHeader icon="💬" title="Community Forum" />
      {ALL_THREADS.map(t => (
        <div key={t.id} onClick={() => setActiveThread(t.id)} style={{ background: "#231408", border: `1.5px solid ${C.parchmentDeep}`, borderRadius: 14, padding: 16, marginBottom: 12, cursor: "pointer" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: "50%", background: C.sageSoft, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Space Mono, monospace", fontSize: 10, fontWeight: 700, color: C.sageDark }}>{t.author[0]}</div>
            <span style={{ fontFamily: "Space Mono, monospace", fontSize: 10, fontWeight: 700, color: C.parchment }}>{t.author}</span>
            {t.founder && <span style={{ background: `linear-gradient(135deg, ${C.sunrise}, ${C.canyon})`, color: "#fff", borderRadius: 6, padding: "1px 6px", fontFamily: "Space Mono, monospace", fontSize: 8, fontWeight: 700 }}>🔥 FOUNDER</span>}
            <span style={{ marginLeft: "auto", fontFamily: "Crimson Pro, serif", fontSize: 11, color: C.dust, fontStyle: "italic" }}>{t.time}</span>
          </div>
          <div style={{ fontFamily: "Playfair Display, serif", fontSize: 14, fontWeight: 700, color: C.parchment, marginBottom: 6 }}>{t.title}</div>
          <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 13, color: C.dust, lineHeight: 1.4, marginBottom: 10 }}>{t.preview}</div>
          <div style={{ display: "flex", gap: 14, borderTop: `1px solid ${C.parchmentDeep}`, paddingTop: 10 }}>
            <span style={{ fontFamily: "Space Mono, monospace", fontSize: 9, color: C.dust }}>💬 {t.replies}</span>
            <span style={{ fontFamily: "Space Mono, monospace", fontSize: 9, color: C.dust }}>👁 {t.views}</span>
            <span style={{ fontFamily: "Space Mono, monospace", fontSize: 9, color: C.dust }}>📍 {t.location}</span>
          </div>
        </div>
      ))}
      <Btn variant="sage">+ Start a Thread</Btn>
    </div>
  );
}

// ─── SAVINGS TAB ─────────────────────────────────────────────
function SavingsTab() {
  return (
    <div style={{ padding: "20px 16px" }}>
      <SectionHeader icon="💰" title="Road Savings" />
      <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 14, color: C.parchment, fontStyle: "italic", marginBottom: 20 }}>
        Money-saving tools every nomad should know about
      </div>
      {SAVINGS_TIPS.map(tip => (
        <div key={tip.id} style={{ background: tip.highlight ? `linear-gradient(160deg, ${C.pine}, ${C.sageDark})` : "#231408", border: `1.5px solid ${tip.highlight ? C.sage : C.parchmentDeep}`, borderRadius: 16, padding: 20, marginBottom: 14, position: "relative", overflow: "hidden" }}>
          {tip.highlight && (
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${C.sunrise}, ${C.canyon})` }} />
          )}
          {tip.badge && (
            <div style={{ display: "inline-flex", alignItems: "center", background: `linear-gradient(135deg, ${C.sunrise}, ${C.canyon})`, borderRadius: 8, padding: "3px 10px", fontFamily: "Space Mono, monospace", fontSize: 8, fontWeight: 700, color: "#fff", marginBottom: 12, letterSpacing: 0.5 }}>
              {tip.badge}
            </div>
          )}
          <div style={{ display: "flex", gap: 14, alignItems: "flex-start", marginBottom: 12 }}>
            <div style={{ width: 48, height: 48, borderRadius: 14, background: tip.highlight ? "rgba(255,255,255,0.1)" : C.barkMid, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, flexShrink: 0 }}>{tip.emoji}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: "Playfair Display, serif", fontSize: 17, fontWeight: 700, color: "#fff", marginBottom: 3 }}>{tip.title}</div>
              <div style={{ fontFamily: "Space Mono, monospace", fontSize: 9, color: tip.highlight ? C.sageLight : C.dust, letterSpacing: 0.5 }}>{tip.subtitle}</div>
            </div>
          </div>
          <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 14, color: tip.highlight ? "rgba(255,255,255,0.8)" : C.parchment, lineHeight: 1.6, marginBottom: 16 }}>{tip.description}</div>
          <button onClick={() => window.open(tip.link, "_blank")} style={{ width: "100%", background: tip.highlight ? `linear-gradient(135deg, ${C.sunrise}, ${C.canyon})` : C.sageSoft, border: "none", borderRadius: 12, padding: "13px 16px", fontFamily: "Space Mono, monospace", fontSize: 11, fontWeight: 700, letterSpacing: 1, color: tip.highlight ? "#fff" : C.sageDark, cursor: "pointer" }}>
            {tip.cta}
          </button>
        </div>
      ))}
      <div style={{ background: C.sunriseSoft, border: `1.5px solid ${C.canyonLight}`, borderRadius: 12, padding: "14px 16px", marginTop: 4 }}>
        <div style={{ fontFamily: "Space Mono, monospace", fontSize: 9, fontWeight: 700, color: C.canyon, letterSpacing: 1, marginBottom: 6 }}>💡 NOMAD TIP</div>
        <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 13, color: C.bark, lineHeight: 1.5 }}>Mudflap saves most skoolie owners $50–$150 per fill-up compared to pump price. On a 40-gallon tank at $1.00/gal savings, that's $40 back in your pocket every stop.</div>
      </div>
    </div>
  );
}

// ─── RESCUE TAB ──────────────────────────────────────────────
function RescueTab() {
  const [engine, setEngine] = useState("Cummins");
  return (
    <div style={{ padding: "20px 16px" }}>
      <SectionHeader icon="🔧" title="RoadBurnAI Rescue" />
      <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 13, color: C.dirt, fontStyle: "italic", marginBottom: 16 }}>Nearest repair, filtered by your engine type</div>
      <div style={{ marginBottom: 16 }}>
        <label style={{ display: "block", fontFamily: "Space Mono, monospace", fontSize: 9, fontWeight: 700, color: C.dust, letterSpacing: 1.2, marginBottom: 6, textTransform: "uppercase" }}>Engine Type</label>
        <select value={engine} onChange={e => setEngine(e.target.value)} style={{ width: "100%", background: "#231408", border: `1.5px solid ${C.parchmentDeep}`, borderRadius: 10, padding: "12px 14px", fontFamily: "Crimson Pro, serif", fontSize: 15, color: "#f0e8d8", outline: "none" }}>
          {["Cummins", "Detroit", "Mercedes Sprinter", "International", "Caterpillar", "Ford Transit", "Other"].map(e => <option key={e}>{e}</option>)}
        </select>
      </div>
      {ALL_RESCUE.map(r => (
        <Card key={r.id} accent={r.type === "Mobile Mechanic" ? C.sage : C.canyon}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
            <div>
              <div style={{ fontFamily: "Playfair Display, serif", fontSize: 15, fontWeight: 700, color: C.parchment }}>{r.name}</div>
              <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 12, color: C.dirt, fontStyle: "italic" }}>{r.type}</div>
            </div>
            <div style={{ fontFamily: "Playfair Display, serif", fontSize: 16, fontWeight: 900, color: C.canyon }}>{r.distance}</div>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 10 }}>
            {r.engines.map(e => <span key={e} style={{ background: C.sageSoft, color: C.sageDark, border: `1px solid ${C.sageLight}`, borderRadius: 6, padding: "2px 8px", fontFamily: "Space Mono, monospace", fontSize: 9 }}>{e}</span>)}
          </div>
          <div style={{ fontFamily: "Space Mono, monospace", fontSize: 9, color: C.dust, marginBottom: 10 }}>📞 {r.phone}</div>
          <div style={{ display: "flex", gap: 8 }}>
            <Btn variant="secondary" style={{ fontSize: 9, letterSpacing: 0.5, padding: "9px 10px" }}>📞 CALL</Btn>
            <Btn variant="secondary" style={{ fontSize: 9, letterSpacing: 0.5, padding: "9px 10px" }}>🗺 DIRECTIONS</Btn>
          </div>
        </Card>
      ))}
    </div>
  );
}

// ─── TRIP TAB ────────────────────────────────────────────────
function TripTab() {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [planned, setPlanned] = useState(false);
  const [livePrice, setLivePrice] = useState(null);

  useEffect(() => {
    fetch("https://roadburnai-proxy.skoolie309.workers.dev/eia")
      .then(r => r.json())
      .then(json => {
        const national = (json.response?.data || []).find(d => d.duoarea === "NUS");
        if (national) setLivePrice(parseFloat(national.value));
      })
      .catch(() => null);
  }, []);

  const fuelPrice = livePrice || 3.94;
  const handlePlan = () => { if (from && to) setPlanned(true); };
  return (
    <div style={{ padding: "20px 16px" }}>
      <SectionHeader icon="🗺️" title="Trip Planner" />
      <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 13, color: C.dirt, fontStyle: "italic", marginBottom: 16 }}>Real fuel cost using YOUR actual MPG</div>
      <InputField label="Starting Point" value={from} onChange={setFrom} placeholder="Flagstaff, AZ" />
      <InputField label="Destination" value={to} onChange={setTo} placeholder="San Diego, CA" />
      <Btn onClick={handlePlan}>Calculate Fuel Cost</Btn>
      {planned && (
        <div style={{ marginTop: 20 }}>
          <div style={{ background: "linear-gradient(135deg, #0a3838, #1a6868)", borderRadius: 14, padding: "20px 16px", marginBottom: 12 }}>
            <div style={{ fontFamily: "Space Mono, monospace", fontSize: 9, color: C.sageLight, letterSpacing: 1.5, marginBottom: 16, textTransform: "uppercase" }}>{from} → {to}</div>
            {livePrice && <div style={{ background: "rgba(58,152,152,0.2)", borderRadius: 8, padding: "6px 12px", marginBottom: 12, fontFamily: "Space Mono, monospace", fontSize: 9, color: C.sageLight }}>⛽ LIVE PRICE: ${livePrice.toFixed(2)}/gal (EIA national avg)</div>}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              {[["Total Distance", "492 mi"], ["Est. Fuel Cost", `$${(492 / 7 * fuelPrice).toFixed(2)}`], ["Fill-Ups Needed", "~2"], ["Avg Fuel Price", `$${fuelPrice.toFixed(2)}/gal`]].map(([l, v]) => (

                <div key={l}>
                  <div style={{ fontFamily: "Space Mono, monospace", fontSize: 8, color: "rgba(255,255,255,0.45)", letterSpacing: 1, textTransform: "uppercase" }}>{l}</div>
                  <div style={{ fontFamily: "Playfair Display, serif", fontSize: 18, fontWeight: 900, color: "#fff", marginTop: 4 }}>{v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── AI TAB ──────────────────────────────────────────────────
function AITab() {
  const [messages, setMessages] = useState([{ role: "assistant", text: "Hey — I'm your RoadBurnAI Assistant. Ask me about fuel history, campsites, trip planning, or anything nomad life. What do you need?" }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);
  const SUGGESTIONS = ["How much did I spend on fuel this month?", "Find a free campsite near me", "Plan a trip to New Mexico", "What's my average MPG?"];

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (text) => {
    const q = text || input; if (!q.trim()) return;
    setMessages(m => [...m, { role: "user", text: q }]); setInput(""); setLoading(true);
    try {
      const res = await fetch("https://roadburnai-proxy.skoolie309.workers.dev", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ model: "claude-sonnet-4-5", max_tokens: 1000, system: "You are the RoadBurnAI Assistant — a helpful, knowledgeable AI built into RoadBurnAI, the complete nomadic life app for van lifers, skoolie owners, and bus nomads. You know about fuel tracking, free camping (BLM, USFS), diesel repair, trip planning, and the nomadic lifestyle. Keep responses concise and practical.", messages: [{ role: "user", content: q }] }) });
      const textData = await res.text();
      let reply = "No response received.";
      try { const data = JSON.parse(textData); if (data.content?.[0]?.text) reply = data.content[0].text; else if (data.error) reply = "Error: " + (data.error.message || JSON.stringify(data.error)); } catch(e) { reply = "Raw response: " + textData.substring(0, 200); }
      setMessages(m => [...m, { role: "assistant", text: reply }]);
    } catch { setMessages(m => [...m, { role: "assistant", text: "Network error — try again." }]); }
    setLoading(false);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 160px)", padding: "16px 16px 0" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
        <div style={{ width: 32, height: 32, borderRadius: 10, background: `linear-gradient(135deg, ${C.sunrise}, ${C.canyon})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>🔥</div>
        <div style={{ fontFamily: "Playfair Display, serif", fontSize: 18, fontWeight: 700, color: C.parchment }}>RoadBurnAI Assistant</div>
        <span style={{ marginLeft: "auto", background: C.greenSoft, color: C.green, borderRadius: 8, padding: "3px 10px", fontFamily: "Space Mono, monospace", fontSize: 8, fontWeight: 700 }}>LIVE</span>
      </div>
      {messages.length === 1 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
          {SUGGESTIONS.map((s, i) => <button key={i} onClick={() => send(s)} style={{ background: C.parchment, border: `1.5px solid ${C.parchmentDeep}`, borderRadius: 10, padding: "8px 12px", fontFamily: "Crimson Pro, serif", fontSize: 12, color: C.dirt, cursor: "pointer", textAlign: "left" }}>{s}</button>)}
        </div>
      )}
      <div style={{ flex: 1, overflowY: "auto", paddingBottom: 16 }}>
        {messages.map((m, i) => (
          <div key={i} style={{ display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start", marginBottom: 12 }}>
            <div style={{ maxWidth: "82%", background: m.role === "user" ? `linear-gradient(135deg, ${C.sunrise}, ${C.canyon})` : C.cream, border: m.role === "assistant" ? `1.5px solid ${C.parchmentDeep}` : "none", borderRadius: m.role === "user" ? "14px 14px 4px 14px" : "14px 14px 14px 4px", padding: "10px 14px", fontFamily: "Crimson Pro, serif", fontSize: 14, lineHeight: 1.5, color: m.role === "user" ? "#fff" : C.bark }}>
              {m.role === "assistant" && <div style={{ fontFamily: "Space Mono, monospace", fontSize: 8, color: C.sunrise, marginBottom: 4, fontWeight: 700 }}>ROADBURNAI</div>}
              {m.text}
            </div>
          </div>
        ))}
        {loading && <div style={{ display: "flex", gap: 5, padding: "10px 14px" }}>{[0,1,2].map(i => <div key={i} style={{ width: 8, height: 8, borderRadius: "50%", background: C.sunrise, animation: "pulse 1.2s ease-in-out infinite", animationDelay: `${i * 0.2}s` }} />)}</div>}
        <div ref={bottomRef} />
      </div>
      <div style={{ display: "flex", gap: 8, paddingBottom: 16, borderTop: `1.5px solid ${C.parchmentDeep}`, paddingTop: 12 }}>
        <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === "Enter" && send()} placeholder="Ask anything about your journey..." style={{ flex: 1, background: "#231408", border: `1.5px solid ${C.parchmentDeep}`, borderRadius: 12, padding: "12px 16px", fontFamily: "Crimson Pro, serif", fontSize: 15, color: "#f0e8d8", outline: "none", caretColor: "#f0e8d8" }} />
        <button onClick={() => send()} style={{ background: `linear-gradient(135deg, ${C.sunrise}, ${C.canyon})`, border: "none", borderRadius: 12, padding: "0 18px", fontSize: 18, cursor: "pointer" }}>➤</button>
      </div>
    </div>
  );
}
// ─── NEWS TAB ────────────────────────────────────────────────
const NEWS_SOURCES = [
  {
    id: 1,
    emoji: "🚐",
    title: "Vanlife World",
    subtitle: "Van life news, builds & culture",
    description: "One of the best van life editorial sites. Build tours, destination guides, gear reviews, and community stories from the van life world.",
    cta: "READ VANLIFE WORLD →",
    link: "https://vanlifeworld.com",
    category: "Van Life",
  },
  {
    id: 2,
    emoji: "🚌",
    title: "Skoolie.net",
    subtitle: "The original skoolie community",
    description: "Forum-based community with decades of skoolie build knowledge. Classifieds, build threads, mechanical help, and the most experienced bus converters on the internet.",
    cta: "VISIT SKOOLIE.NET →",
    link: "https://skoolie.net",
    category: "Skoolie",
  },
  {
    id: 3,
    emoji: "📱",
    title: "r/vandwellers",
    subtitle: "Reddit's van life community",
    description: "800,000+ members sharing builds, stories, tips, and real talk about life on the road. One of the most active nomadic communities online.",
    cta: "OPEN SUBREDDIT →",
    link: "https://reddit.com/r/vandwellers",
    category: "Van Life",
  },
  {
    id: 4,
    emoji: "📱",
    title: "r/skoolie",
    subtitle: "Reddit's skoolie community",
    description: "The skoolie subreddit — bus builds, conversion progress, mechanical questions, and life on the road in a converted school bus.",
    cta: "OPEN SUBREDDIT →",
    link: "https://reddit.com/r/skoolie",
    category: "Skoolie",
  },
  {
    id: 5,
    emoji: "▶️",
    title: "Nomadic Fanatic",
    subtitle: "YouTube · Full-time RV & nomad life",
    description: "One of the longest-running nomadic life YouTube channels. Real, unfiltered coverage of full-time travel, costs, campgrounds, and the honest reality of life on the road.",
    cta: "WATCH ON YOUTUBE →",
    link: "https://www.youtube.com/@NomadicFanatic",
    category: "YouTube",
  },
  {
    id: 6,
    emoji: "▶️",
    title: "Drivin & Vibin",
    subtitle: "YouTube · Van life & travel",
    description: "High quality van life content covering builds, destinations, and the realities of nomadic living. Great production, honest storytelling.",
    cta: "WATCH ON YOUTUBE →",
    link: "https://www.youtube.com/@DrivinAndVibin",
    category: "YouTube",
  },
  {
    id: 7,
    emoji: "▶️",
    title: "Keep Your Daydream",
    subtitle: "YouTube · Full-time RV family travel",
    description: "Family of five living full-time on the road. Practical advice, campground reviews, budgeting tips, and genuine family nomadic life content.",
    cta: "WATCH ON YOUTUBE →",
    link: "https://www.youtube.com/@KeepYourDaydream",
    category: "YouTube",
  },
  {
    id: 8,
    emoji: "⛺",
    title: "The Dyrt Magazine",
    subtitle: "Camping news, guides & gear",
    description: "Editorial camping content from the team behind The Dyrt app. Destination guides, gear reviews, dispersed camping tips, and camping culture coverage.",
    cta: "READ THE DYRT →",
    link: "https://thedyrt.com/magazine",
    category: "Camping",
  },
  {
    id: 9,
    emoji: "🔧",
    title: "Technomadia",
    subtitle: "Tech-forward nomadic living",
    description: "Deep dives into the technical side of nomadic life — connectivity, solar, lithium, and living well on the road. Run by two full-time nomads with serious technical chops.",
    cta: "VISIT TECHNOMADIA →",
    link: "https://technomadia.com",
    category: "Tech & Gear",
  },
  {
    id: 10,
    emoji: "📰",
    title: "Workamper News",
    subtitle: "Work camping jobs & lifestyle",
    description: "The go-to resource for nomads who work as they travel — camp host positions, seasonal jobs, Amazon Camperforce, and the work camping lifestyle.",
    cta: "READ WORKAMPER NEWS →",
    link: "https://workamper.com",
    category: "Work & Income",
  },
];

const NEWS_CATEGORIES = ["All", "Van Life", "Skoolie", "YouTube", "Camping", "Tech & Gear", "Work & Income"];

function NewsTab() {
  const [activeCategory, setActiveCategory] = useState("All");

  const filtered = NEWS_SOURCES.filter(s =>
    activeCategory === "All" ? true : s.category === activeCategory
  );

  return (
    <div style={{ padding: "20px 16px" }}>
      <SectionHeader icon="📰" title="Nomad News" />
      <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 14, color: C.parchment, fontStyle: "italic", marginBottom: 20 }}>
        The best van life, skoolie, and nomadic living sources — all in one place
      </div>

      {/* Category filter */}
      <div style={{ display: "flex", gap: 6, marginBottom: 20, overflowX: "auto", paddingBottom: 4 }}>
        {NEWS_CATEGORIES.map(cat => (
          <button key={cat} onClick={() => setActiveCategory(cat)} style={{
            flexShrink: 0, padding: "7px 14px",
            background: activeCategory === cat ? `linear-gradient(135deg, ${C.sunrise}, ${C.canyon})` : "#231408",
            border: `1.5px solid ${activeCategory === cat ? C.sunrise : C.parchmentDeep}`,
            borderRadius: 20, fontFamily: "Space Mono, monospace", fontSize: 9,
            fontWeight: 700, color: activeCategory === cat ? "#fff" : C.dust, cursor: "pointer"
          }}>{cat}</button>
        ))}
      </div>

      {/* Source cards */}
      {filtered.map(source => (
        <div key={source.id} style={{
          background: "#231408", border: `1.5px solid ${C.parchmentDeep}`,
          borderRadius: 16, padding: 20, marginBottom: 14, position: "relative", overflow: "hidden"
        }}>
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${C.sage}, ${C.sageDark})` }} />
          <div style={{ display: "flex", gap: 14, alignItems: "flex-start", marginBottom: 12 }}>
            <div style={{
              width: 48, height: 48, borderRadius: 14,
              background: C.barkMid,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 24, flexShrink: 0
            }}>{source.emoji}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: "Playfair Display, serif", fontSize: 17, fontWeight: 700, color: C.parchment, marginBottom: 3 }}>{source.title}</div>
              <div style={{ fontFamily: "Space Mono, monospace", fontSize: 9, color: C.dust, letterSpacing: 0.5 }}>{source.subtitle}</div>
            </div>
            <span style={{
              background: C.sageSoft, color: C.sageDark,
              borderRadius: 8, padding: "3px 8px",
              fontFamily: "Space Mono, monospace", fontSize: 7,
              fontWeight: 700, flexShrink: 0
            }}>{source.category}</span>
          </div>
          <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 14, color: "rgba(240,232,216,0.8)", lineHeight: 1.6, marginBottom: 16 }}>
            {source.description}
          </div>
          <button onClick={() => window.open(source.link, "_blank")} style={{
            width: "100%", background: C.sageSoft,
            border: "none", borderRadius: 12, padding: "13px 16px",
            fontFamily: "Space Mono, monospace", fontSize: 11,
            fontWeight: 700, letterSpacing: 1, color: C.sageDark, cursor: "pointer"
          }}>
            {source.cta}
          </button>
        </div>
      ))}

      <div style={{ background: C.sunriseSoft, border: `1.5px solid ${C.canyonLight}`, borderRadius: 12, padding: "14px 16px", marginTop: 4 }}>
        <div style={{ fontFamily: "Space Mono, monospace", fontSize: 9, fontWeight: 700, color: C.canyon, letterSpacing: 1, marginBottom: 6 }}>💡 NOMAD TIP</div>
        <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 13, color: C.bark, lineHeight: 1.5 }}>
          Know a great source we missed? Drop it in the Forum and we'll consider adding it to the list.
        </div>
      </div>
    </div>
  );
}

// ─── LEARN TAB ───────────────────────────────────────────────
const LEARN_CATEGORIES = [
  {
    id: "maintenance",
    emoji: "🔧",
    title: "Maintenance",
    subtitle: "Engine, drivetrain, brakes, fluids",
    color: C.canyon,
    colorSoft: C.canyonSoft,
    resources: [
      { title: "Cummins ISB Owners Group", description: "Facebook group — 50,000+ Cummins owners sharing maintenance tips, repair guides, and real-world advice.", link: "https://facebook.com/groups/cumminsisg", type: "Community" },
      { title: "Detroit Diesel Owners Forum", description: "Series 60 and other Detroit diesel owners. Maintenance schedules, troubleshooting, and parts sourcing.", link: "https://detroitdieselowners.com", type: "Forum" },
      { title: "School Bus Fleet Magazine", description: "Professional-grade maintenance guides originally written for fleet managers. Detailed, reliable, and free to read online.", link: "https://www.schoolbusfleet.com", type: "Guide" },
      { title: "r/MechanicAdvice", description: "Reddit community for mechanical questions. Post your issue and get real answers from mechanics and experienced owners.", link: "https://reddit.com/r/MechanicAdvice", type: "Community" },
    ]
  },
  {
    id: "plumbing",
    emoji: "🪠",
    title: "Plumbing",
    subtitle: "Fresh water, grey water, tanks, pumps",
    color: C.sage,
    colorSoft: C.sageSoft,
    resources: [
      { title: "Skoolie.net Plumbing Forum", description: "Years of archived plumbing threads from real bus converters. Tank sizing, pump selection, winterizing, and grey water solutions.", link: "https://skoolie.net", type: "Forum" },
      { title: "Hank the Tank (YouTube)", description: "Detailed DIY plumbing tutorials for van and bus conversions. Clear explanations, real builds, practical solutions.", link: "https://youtube.com", type: "YouTube" },
      { title: "Fresh Water System Guide — Technomadia", description: "In-depth guide covering pumps, tanks, filtration, and full-time water system design from experienced nomads.", link: "https://technomadia.com", type: "Guide" },
    ]
  },
  {
    id: "solar",
    emoji: "☀️",
    title: "Solar",
    subtitle: "Panels, batteries, inverters, charge controllers",
    color: C.sunrise,
    colorSoft: C.sunriseSoft,
    resources: [
      { title: "Explorist Life (YouTube)", description: "The best free solar education on the internet for RV and van builds. Wiring diagrams, component reviews, system sizing calculators.", link: "https://www.youtube.com/@exploristlife", type: "YouTube" },
      { title: "DIY Solar Forum", description: "Active community for off-grid solar builders. System design help, component recommendations, and real build threads.", link: "https://diysolarforum.com", type: "Forum" },
      { title: "Battle Born Batteries Blog", description: "Lithium battery guides, charging tips, system design articles, and honest technical content from one of the top lithium manufacturers.", link: "https://battlebornbatteries.com/blog", type: "Guide" },
      { title: "Renogy Learning Center", description: "Free solar sizing calculators, wiring guides, and product tutorials from the most popular solar brand in the van life community.", link: "https://www.renogy.com/learning-center", type: "Guide" },
    ]
  },
  {
    id: "electrical",
    emoji: "⚡",
    title: "Electrical",
    subtitle: "Wiring, fusing, 12V systems, shore power",
    color: C.sage,
    colorSoft: C.sageSoft,
    resources: [
      { title: "Explorist Life Wiring Diagrams", description: "Free downloadable wiring diagrams for 12V systems in van and bus builds. The gold standard for DIY electrical planning.", link: "https://www.exploristlife.com/wiring-diagrams", type: "Guide" },
      { title: "r/vanlife Electrical Wiki", description: "Community-maintained electrical guide covering 12V basics, fusing, shore power hookups, and common mistakes to avoid.", link: "https://reddit.com/r/vandwellers/wiki", type: "Guide" },
      { title: "ABYC Electrical Standards (Overview)", description: "Marine-grade electrical standards widely used in vehicle builds. Overkill in a good way — if it's safe on a boat, it's safe in your rig.", link: "https://abycinc.org", type: "Reference" },
    ]
  },
  {
    id: "conversion",
    emoji: "🏗️",
    title: "Conversion & Build",
    subtitle: "Insulation, framing, flooring, layout",
    color: C.dirt,
    colorSoft: C.parchmentDark,
    resources: [
      { title: "Skoolie.net Build Threads", description: "Hundreds of documented skoolie conversions from start to finish. The single best resource for bus-specific build knowledge.", link: "https://skoolie.net", type: "Forum" },
      { title: "Faroutride Van Build Guides", description: "Extremely detailed free build guides for every aspect of a van conversion — insulation, flooring, walls, furniture, electrical.", link: "https://faroutride.com", type: "Guide" },
      { title: "r/vandwellers Build Wiki", description: "Community-maintained build guide covering all aspects of van and bus conversion with honest assessments of different approaches.", link: "https://reddit.com/r/vandwellers/wiki", type: "Guide" },
      { title: "Hein's Bus Conversion Resources", description: "One of the most comprehensive free resources specifically for school bus conversion. Layout planning, structural considerations, and build order.", link: "https://busconversionmagazine.com", type: "Guide" },
    ]
  },
  {
    id: "safety",
    emoji: "🦺",
    title: "Safety",
    subtitle: "CO detectors, fire safety, road safety, insurance",
    color: C.red,
    colorSoft: C.redSoft,
    resources: [
      { title: "NFPA RV Fire Safety Guide", description: "National Fire Protection Association guidelines adapted for RV and conversion vehicle living. CO, propane, and fire safety standards.", link: "https://nfpa.org", type: "Guide" },
      { title: "Propane Safety — PERC", description: "Propane Education & Research Council safety guides for RV and off-grid propane use. Installation standards and emergency procedures.", link: "https://propane.com/safety", type: "Guide" },
      { title: "National General RV Insurance", description: "One of the top-rated insurers for converted vehicles and non-traditional RVs. Skoolie and van-friendly policies.", link: "https://nationalgeneral.com/rv-insurance", type: "Resource" },
      { title: "What3Words", description: "Emergency location app — gives any spot on earth a unique 3-word address. Essential for calling for help when you have no street address.", link: "https://what3words.com", type: "App" },
    ]
  },
];

function LearnTab() {
  const [activeCategory, setActiveCategory] = useState(null);
  const [disclaimerDismissed, setDisclaimerDismissed] = useState(() => {
    try { return localStorage.getItem("learnDisclaimerDismissed") === "true"; } catch { return false; }
  });

  const handleDismissDisclaimer = () => {
    try { localStorage.setItem("learnDisclaimerDismissed", "true"); } catch {}
    setDisclaimerDismissed(true);
  };

  // Resource detail view
  if (activeCategory) {
    const cat = LEARN_CATEGORIES.find(c => c.id === activeCategory);
    return (
      <div style={{ padding: "20px 16px" }}>
        <button onClick={() => setActiveCategory(null)} style={{
          background: "none", border: "none", color: C.sage,
          fontFamily: "Space Mono, monospace", fontSize: 10,
          cursor: "pointer", marginBottom: 16, padding: 0
        }}>← Back to Learn</button>

        <div style={{
          background: `linear-gradient(160deg, ${C.pine}, ${C.sageDark})`,
          borderRadius: 16, padding: 20, marginBottom: 20,
          display: "flex", alignItems: "center", gap: 16
        }}>
          <div style={{ fontSize: 40 }}>{cat.emoji}</div>
          <div>
            <div style={{ fontFamily: "Playfair Display, serif", fontSize: 22, fontWeight: 700, color: "#fff" }}>{cat.title}</div>
            <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 13, color: "rgba(255,255,255,0.7)", fontStyle: "italic" }}>{cat.subtitle}</div>
          </div>
        </div>

        {cat.resources.map((r, i) => (
          <div key={i} style={{
            background: "#231408", border: `1.5px solid ${C.parchmentDeep}`,
            borderLeft: `4px solid ${cat.color}`,
            borderRadius: 14, padding: 16, marginBottom: 12
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
              <div style={{ fontFamily: "Playfair Display, serif", fontSize: 15, fontWeight: 700, color: C.parchment, flex: 1, paddingRight: 8 }}>{r.title}</div>
              <span style={{
                background: cat.colorSoft, color: cat.color,
                borderRadius: 6, padding: "2px 8px", flexShrink: 0,
                fontFamily: "Space Mono, monospace", fontSize: 8, fontWeight: 700
              }}>{r.type}</span>
            </div>
            <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 13, color: "rgba(240,232,216,0.8)", lineHeight: 1.6, marginBottom: 12 }}>
              {r.description}
            </div>
            <button onClick={() => window.open(r.link, "_blank")} style={{
              width: "100%", background: "none",
              border: `1.5px solid ${cat.color}`,
              borderRadius: 10, padding: "10px",
              fontFamily: "Space Mono, monospace", fontSize: 10,
              fontWeight: 700, color: cat.color, cursor: "pointer", letterSpacing: 0.5
            }}>OPEN RESOURCE →</button>
          </div>
        ))}

        <div style={{ background: C.sunriseSoft, border: `1.5px solid ${C.canyonLight}`, borderRadius: 12, padding: "14px 16px", marginTop: 8 }}>
          <div style={{ fontFamily: "Space Mono, monospace", fontSize: 9, fontWeight: 700, color: C.canyon, letterSpacing: 1, marginBottom: 6 }}>💡 NOMAD TIP</div>
          <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 13, color: C.bark, lineHeight: 1.5 }}>
            Know a great resource we missed? Post it in the Forum and we'll consider adding it here.
          </div>
        </div>
      </div>
    );
  }

  // Main category grid
  return (
    <div style={{ padding: "20px 16px" }}>
      <SectionHeader icon="📚" title="Learn" />
      <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 14, color: C.parchment, fontStyle: "italic", marginBottom: 20 }}>
        Curated resources for building, maintaining, and living in your rig
      </div>

      {/* Disclaimer */}
      {!disclaimerDismissed && (
        <div style={{
          background: C.redSoft, border: `1.5px solid #e8b8b8`,
          borderRadius: 14, padding: 16, marginBottom: 20
        }}>
          <div style={{ fontFamily: "Space Mono, monospace", fontSize: 9, fontWeight: 700, color: C.red, letterSpacing: 1, marginBottom: 8 }}>⚠️ DISCLAIMER</div>
          <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 13, color: C.bark, lineHeight: 1.6, marginBottom: 12 }}>
            Resources in this section are curated links to third-party content. RoadBurnAI does not verify, endorse, or take responsibility for the accuracy of external resources. Always consult a qualified professional before performing mechanical, electrical, or structural work on your vehicle. Work safely.
          </div>
          <button onClick={handleDismissDisclaimer} style={{
            width: "100%", background: C.red, border: "none",
            borderRadius: 10, padding: "10px",
            fontFamily: "Space Mono, monospace", fontSize: 10,
            fontWeight: 700, color: "#fff", cursor: "pointer", letterSpacing: 0.5
          }}>I UNDERSTAND — LET'S LEARN →</button>
        </div>
      )}

      {/* Category cards */}
      {LEARN_CATEGORIES.map(cat => (
        <div key={cat.id} onClick={() => setActiveCategory(cat.id)} style={{
          background: "#231408", border: `1.5px solid ${C.parchmentDeep}`,
          borderLeft: `4px solid ${cat.color}`,
          borderRadius: 14, padding: 16, marginBottom: 12,
          cursor: "pointer", display: "flex", alignItems: "center", gap: 16
        }}>
          <div style={{
            width: 52, height: 52, borderRadius: 14,
            background: cat.colorSoft,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 26, flexShrink: 0
          }}>{cat.emoji}</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: "Playfair Display, serif", fontSize: 17, fontWeight: 700, color: C.parchment, marginBottom: 3 }}>{cat.title}</div>
            <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 12, color: C.dust, fontStyle: "italic" }}>{cat.subtitle}</div>
            <div style={{ fontFamily: "Space Mono, monospace", fontSize: 8, color: cat.color, marginTop: 4 }}>{cat.resources.length} RESOURCES</div>
          </div>
          <div style={{ color: C.dust, fontSize: 20 }}>›</div>
        </div>
      ))}

      <div style={{ background: C.sunriseSoft, border: `1.5px solid ${C.canyonLight}`, borderRadius: 12, padding: "14px 16px", marginTop: 4 }}>
        <div style={{ fontFamily: "Space Mono, monospace", fontSize: 9, fontWeight: 700, color: C.canyon, letterSpacing: 1, marginBottom: 6 }}>🔥 COMING SOON</div>
        <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 13, color: C.bark, lineHeight: 1.5 }}>
          Community-submitted tips and repair guides. Earn Credits for contributing knowledge that helps other nomads.
        </div>
      </div>
    </div>
  );
}
// ─── PROFILE TAB ─────────────────────────────────────────────
function FriendsList({ user, userProfile, onOpenMessages }) {
  const [friendProfiles, setFriendProfiles] = useState([]);
  const friendUids = userProfile?.friends || [];

  useEffect(() => {
    let active = true;
    (async () => {
      const docs = await Promise.all(friendUids.map(uid => getDoc(doc(db, "users", uid))));
      if (active) setFriendProfiles(docs.filter(d => d.exists()).map(d => ({ uid: d.id, ...d.data() })));
    })();
    return () => { active = false; };
  }, [JSON.stringify(friendUids)]);

  const messageFriend = async (friend) => {
    const convoId = [user.uid, friend.uid].sort().join("_");
    await setDoc(doc(db, "conversations", convoId), {
      participants: [user.uid, friend.uid],
      participantNames: [
        { uid: user.uid, name: userProfile?.displayName || "You" },
        { uid: friend.uid, name: friend.displayName || "Nomad" }
      ],
      lastMessage: "",
      lastMessageAt: serverTimestamp(),
    }, { merge: true });
    onOpenMessages({ id: convoId });
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontFamily: "Space Mono, monospace", fontSize: 9, color: C.dust, letterSpacing: 1.2, marginBottom: 10, textTransform: "uppercase" }}>🤝 Friends ({friendProfiles.length})</div>
      {friendProfiles.length === 0 ? (
        <div style={{ background: "#231408", border: `1.5px solid ${C.parchmentDeep}`, borderRadius: 14, padding: 16, fontFamily: "Crimson Pro, serif", fontSize: 13, color: C.dust, fontStyle: "italic" }}>No friends yet — head to Roads to connect with other nomads.</div>
      ) : friendProfiles.map(f => (
        <div key={f.uid} style={{ background: "#231408", border: `1.5px solid ${C.parchmentDeep}`, borderRadius: 14, padding: "12px 14px", marginBottom: 10, display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 40, height: 40, borderRadius: "50%", background: `linear-gradient(135deg, ${C.pine}, ${C.sageDark})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, overflow: "hidden", flexShrink: 0 }}>
            {f.photoURL ? <img src={f.photoURL} alt="" style={{ width: "100%", height: "100%" }} /> : "🔥"}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: "Playfair Display, serif", fontSize: 14, fontWeight: 700, color: C.parchment }}>{f.displayName || "Nomad"}</div>
            <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 11, color: C.dust, fontStyle: "italic" }}>{f.rigType || "Rig type not set"}</div>
          </div>
          <button onClick={() => messageFriend(f)} style={{ background: `linear-gradient(135deg, ${C.sunrise}, ${C.canyon})`, border: "none", borderRadius: 10, padding: "8px 12px", fontFamily: "Space Mono, monospace", fontSize: 9, fontWeight: 700, color: "#fff", cursor: "pointer" }}>💬 MESSAGE</button>
        </div>
      ))}
    </div>
  );
}

function ProfileTab({ user, userProfile, fillUps, onProfileUpdate, vehicles, activeVehicleId, onVehicleChange, onOpenMessages }) {
  const [editingName, setEditingName] = useState(false);
  const [displayName, setDisplayName] = useState(userProfile?.displayName || "");
  const [savingName, setSavingName] = useState(false);

  const [editingVehicleId, setEditingVehicleId] = useState(null);
  const [addingVehicle, setAddingVehicle] = useState(false);
  const [vehicleForm, setVehicleForm] = useState({ name: "", rigType: "", rigYear: "", rigMake: "", rigModel: "", rigLength: "", engineType: "" });
  const [savingVehicle, setSavingVehicle] = useState(false);
  const [savedVehicle, setSavedVehicle] = useState(false);
  const [vehicleError, setVehicleError] = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [bio, setBio] = useState(userProfile?.bio || "");
  const [rigType, setRigType] = useState(userProfile?.rigType || "");
  const [seeking, setSeeking] = useState(userProfile?.seeking || []);
  const [birthdate, setBirthdate] = useState(userProfile?.birthdate || "");
  const [roadsVisible, setRoadsVisible] = useState(userProfile?.roadsVisible !== false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savedProfile, setSavedProfile] = useState(false);
  const [rigPhotoURL, setRigPhotoURL] = useState(userProfile?.rigPhotoURL || "");
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoError, setPhotoError] = useState("");

  const defaultVehicleId = vehicles[0]?.id;
  const activeVehicle = vehicles.find(v => v.id === activeVehicleId) || vehicles[0];
  const vehicleFillUps = vehicles.length === 0 ? fillUps : fillUps.filter(f => (f.vehicleId || defaultVehicleId) === activeVehicleId);

  const totalFillUps = vehicleFillUps.length;
  const totalGallons = vehicleFillUps.reduce((sum, f) => sum + (f.gallons || 0), 0);
  const totalSpent = vehicleFillUps.reduce((sum, f) => sum + ((f.gallons || 0) * (f.ppg || 0)), 0);
  const totalMiles = vehicleFillUps.reduce((sum, f) => sum + (f.miles || 0), 0);
  const avgMPG = vehicleFillUps.filter(f => f.mpg).length > 0
    ? (vehicleFillUps.filter(f => f.mpg).reduce((sum, f) => sum + parseFloat(f.mpg), 0) / vehicleFillUps.filter(f => f.mpg).length).toFixed(1)
    : null;
  const memberSince = userProfile?.joinedAt?.toDate
    ? userProfile.joinedAt.toDate().toLocaleDateString("en-US", { month: "long", year: "numeric" })
    : "Recently";

  const handleSaveName = async () => {
    setSavingName(true);
    try {
      await setDoc(doc(db, "users", user.uid), { displayName }, { merge: true });
      if (onProfileUpdate) onProfileUpdate({ ...userProfile, displayName });
      setEditingName(false);
    } catch (err) { console.error("Name save error:", err); }
    setSavingName(false);
  };

  const startEditVehicle = (v) => {
    setVehicleForm({ name: v.name || "", rigType: v.rigType || "", rigYear: v.rigYear || "", rigMake: v.rigMake || "", rigModel: v.rigModel || "", rigLength: v.rigLength || "", engineType: v.engineType || "" });
    setEditingVehicleId(v.id);
    setAddingVehicle(false);
    setVehicleError("");
  };

  const startAddVehicle = () => {
    setVehicleForm({ name: "", rigType: "", rigYear: "", rigMake: "", rigModel: "", rigLength: "", engineType: "" });
    setAddingVehicle(true);
    setEditingVehicleId(null);
    setVehicleError("");
  };

  const cancelVehicleForm = () => { setEditingVehicleId(null); setAddingVehicle(false); setVehicleError(""); };

  const handleSaveVehicle = async () => {
    if (!vehicleForm.name.trim()) { setVehicleError("Please enter a Vehicle Nickname \u2014 it's required to save."); return; }
    setVehicleError("");
    setSavingVehicle(true);
    try {
      if (editingVehicleId) {
        await setDoc(doc(db, "vehicles", editingVehicleId), vehicleForm, { merge: true });
      } else {
        await addDoc(collection(db, "vehicles"), { uid: user.uid, ...vehicleForm, createdAt: serverTimestamp() });
      }
      setSavedVehicle(true);
      setTimeout(() => { setSavedVehicle(false); setEditingVehicleId(null); setAddingVehicle(false); }, 1200);
    } catch (err) { console.error("Vehicle save error:", err); }
    setSavingVehicle(false);
  };

  const handleDeleteVehicle = async (id) => {
    if (vehicles.length <= 1) return;
    try { await deleteDoc(doc(db, "vehicles", id)); } catch (err) { console.error("Vehicle delete error:", err); }
    setConfirmDeleteId(null);
  };

  const RIG_TYPES = ["Skoolie", "Van", "Bus", "Truck Camper", "Class A", "Class B", "Class C", "Car Camp", "Other"];
  const ENGINE_TYPES = ["Cummins", "Detroit", "Mercedes Sprinter", "International", "Caterpillar", "Ford Transit", "Chevy/GM", "Other"];

  useEffect(() => {
    setBio(userProfile?.bio || "");
    setRigType(userProfile?.rigType || "");
    setSeeking(userProfile?.seeking || []);
    setBirthdate(userProfile?.birthdate || "");
    setRoadsVisible(userProfile?.roadsVisible !== false);
    setRigPhotoURL(userProfile?.rigPhotoURL || "");
  }, [userProfile?.uid]);

  const handlePhotoSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { setPhotoError("Please select an image file."); return; }
    if (file.size > 5 * 1024 * 1024) { setPhotoError("Image must be under 5MB."); return; }
    setPhotoError(""); setPhotoUploading(true);
    try {
      const url = await uploadRigPhoto(user.uid, file);
      setRigPhotoURL(url);
      if (onProfileUpdate) onProfileUpdate({ ...userProfile, rigPhotoURL: url });
    } catch (err) {
      console.error("Photo upload error:", err);
      setPhotoError("Upload failed — try again.");
    }
    setPhotoUploading(false);
  };

  const handleRemovePhoto = async () => {
    setPhotoUploading(true);
    try {
      await setDoc(doc(db, "users", user.uid), { rigPhotoURL: "" }, { merge: true });
      setRigPhotoURL("");
      if (onProfileUpdate) onProfileUpdate({ ...userProfile, rigPhotoURL: "" });
    } catch (err) { console.error("Photo remove error:", err); }
    setPhotoUploading(false);
  };

  const handleSaveRoadsProfile = async () => {
    const age = calculateAge(birthdate);
    if (!birthdate || age === null) { alert("Please enter your birthdate to save your Roads profile."); return; }
    if (age < 18) { alert("You must be 18 or older to use Roads."); return; }
    setSavingProfile(true);
    try {
      const patch = { bio, rigType, seeking, birthdate, ageVerified: true, roadsVisible };
      await setDoc(doc(db, "users", user.uid), patch, { merge: true });
      if (onProfileUpdate) onProfileUpdate({ ...userProfile, ...patch });
      setSavedProfile(true);
      setTimeout(() => setSavedProfile(false), 2000);
    } catch (err) { console.error("Profile save error:", err); }
    setSavingProfile(false);
  };

  const vehicleFormUI = (
    <div>
      <div style={{ marginBottom: 12 }}>
        <label style={{ display: "block", fontFamily: "Space Mono, monospace", fontSize: 9, fontWeight: 700, color: C.dust, letterSpacing: 1.2, marginBottom: 6, textTransform: "uppercase" }}>Vehicle Nickname <span style={{ color: C.red }}>*</span></label>
        <input value={vehicleForm.name} onChange={e => { setVehicleForm(p => ({ ...p, name: e.target.value })); if (vehicleError) setVehicleError(""); }} placeholder="My Rig, The Tow Rig, Daily Driver..." style={{ width: "100%", background: "#231408", border: `1.5px solid ${vehicleError ? C.red : C.parchmentDeep}`, borderRadius: 10, padding: "12px 14px", fontFamily: "Crimson Pro, serif", fontSize: 15, color: "#f0e8d8", outline: "none", boxSizing: "border-box" }} />
        {vehicleError && <div style={{ marginTop: 6, fontFamily: "Crimson Pro, serif", fontSize: 12, color: C.red }}>{vehicleError}</div>}
      </div>
      <div style={{ marginBottom: 12 }}>
        <label style={{ display: "block", fontFamily: "Space Mono, monospace", fontSize: 9, fontWeight: 700, color: C.dust, letterSpacing: 1.2, marginBottom: 6, textTransform: "uppercase" }}>Rig Type</label>
        <select value={vehicleForm.rigType} onChange={e => setVehicleForm(p => ({ ...p, rigType: e.target.value }))} style={{ width: "100%", background: "#231408", border: `1.5px solid ${C.parchmentDeep}`, borderRadius: 10, padding: "12px 14px", fontFamily: "Crimson Pro, serif", fontSize: 15, color: "#f0e8d8", outline: "none" }}>
          <option value="">Select type...</option>
          {RIG_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>
      <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
        <div style={{ flex: 1 }}>
          <label style={{ display: "block", fontFamily: "Space Mono, monospace", fontSize: 9, fontWeight: 700, color: C.dust, letterSpacing: 1.2, marginBottom: 6, textTransform: "uppercase" }}>Year</label>
          <input value={vehicleForm.rigYear} onChange={e => setVehicleForm(p => ({ ...p, rigYear: e.target.value }))} placeholder="2004" style={{ width: "100%", background: "#231408", border: `1.5px solid ${C.parchmentDeep}`, borderRadius: 10, padding: "12px 14px", fontFamily: "Crimson Pro, serif", fontSize: 15, color: "#f0e8d8", outline: "none", boxSizing: "border-box" }} />
        </div>
        <div style={{ flex: 1 }}>
          <label style={{ display: "block", fontFamily: "Space Mono, monospace", fontSize: 9, fontWeight: 700, color: C.dust, letterSpacing: 1.2, marginBottom: 6, textTransform: "uppercase" }}>Length</label>
          <input value={vehicleForm.rigLength} onChange={e => setVehicleForm(p => ({ ...p, rigLength: e.target.value }))} placeholder="40 ft" style={{ width: "100%", background: "#231408", border: `1.5px solid ${C.parchmentDeep}`, borderRadius: 10, padding: "12px 14px", fontFamily: "Crimson Pro, serif", fontSize: 15, color: "#f0e8d8", outline: "none", boxSizing: "border-box" }} />
        </div>
      </div>
      <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
        <div style={{ flex: 1 }}>
          <label style={{ display: "block", fontFamily: "Space Mono, monospace", fontSize: 9, fontWeight: 700, color: C.dust, letterSpacing: 1.2, marginBottom: 6, textTransform: "uppercase" }}>Make</label>
          <input value={vehicleForm.rigMake} onChange={e => setVehicleForm(p => ({ ...p, rigMake: e.target.value }))} placeholder="Thomas Built" style={{ width: "100%", background: "#231408", border: `1.5px solid ${C.parchmentDeep}`, borderRadius: 10, padding: "12px 14px", fontFamily: "Crimson Pro, serif", fontSize: 15, color: "#f0e8d8", outline: "none", boxSizing: "border-box" }} />
        </div>
        <div style={{ flex: 1 }}>
          <label style={{ display: "block", fontFamily: "Space Mono, monospace", fontSize: 9, fontWeight: 700, color: C.dust, letterSpacing: 1.2, marginBottom: 6, textTransform: "uppercase" }}>Model</label>
          <input value={vehicleForm.rigModel} onChange={e => setVehicleForm(p => ({ ...p, rigModel: e.target.value }))} placeholder="Saf-T-Liner" style={{ width: "100%", background: "#231408", border: `1.5px solid ${C.parchmentDeep}`, borderRadius: 10, padding: "12px 14px", fontFamily: "Crimson Pro, serif", fontSize: 15, color: "#f0e8d8", outline: "none", boxSizing: "border-box" }} />
        </div>
      </div>
      <div style={{ marginBottom: 16 }}>
        <label style={{ display: "block", fontFamily: "Space Mono, monospace", fontSize: 9, fontWeight: 700, color: C.dust, letterSpacing: 1.2, marginBottom: 6, textTransform: "uppercase" }}>Engine Type</label>
        <select value={vehicleForm.engineType} onChange={e => setVehicleForm(p => ({ ...p, engineType: e.target.value }))} style={{ width: "100%", background: "#231408", border: `1.5px solid ${C.parchmentDeep}`, borderRadius: 10, padding: "12px 14px", fontFamily: "Crimson Pro, serif", fontSize: 15, color: "#f0e8d8", outline: "none" }}>
          <option value="">Select engine...</option>
          {ENGINE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>
      <div style={{ fontFamily: "Space Mono, monospace", fontSize: 9, color: C.dust, marginBottom: 12 }}><span style={{ color: C.red }}>*</span> Required field</div>
      <div style={{ display: "flex", gap: 10 }}>
        <button onClick={handleSaveVehicle} disabled={savingVehicle} style={{ flex: 1, background: savedVehicle ? `linear-gradient(135deg, ${C.green}, #3a7a3a)` : `linear-gradient(135deg, ${C.sunrise}, ${C.canyon})`, border: "none", borderRadius: 12, padding: "13px", fontFamily: "Space Mono, monospace", fontSize: 11, fontWeight: 700, color: "#fff", cursor: "pointer" }}>
          {savedVehicle ? "✓ SAVED!" : savingVehicle ? "SAVING..." : "SAVE VEHICLE"}
        </button>
        <button onClick={cancelVehicleForm} style={{ flex: 1, background: "none", border: `1.5px solid ${C.parchmentDeep}`, borderRadius: 12, padding: "13px", fontFamily: "Space Mono, monospace", fontSize: 11, fontWeight: 700, color: C.dust, cursor: "pointer" }}>CANCEL</button>
      </div>
    </div>
  );

  return (
    <div style={{ padding: "20px 16px" }}>
      <SectionHeader icon="🔥" title="My Profile" />

      <div style={{ background: `linear-gradient(160deg, ${C.pine}, ${C.sageDark})`, borderRadius: 16, padding: 20, marginBottom: 16, display: "flex", alignItems: "center", gap: 16 }}>
        <div style={{ width: 64, height: 64, borderRadius: "50%", background: `linear-gradient(135deg, ${C.sunrise}, ${C.canyon})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, flexShrink: 0 }}>
          {activeVehicle?.rigType === "Skoolie" ? "🚌" : activeVehicle?.rigType === "Van" ? "🚐" : activeVehicle?.rigType === "Bus" ? "🚎" : "🔥"}
        </div>
        <div style={{ flex: 1 }}>
          {editingName ? (
            <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 4 }}>
              <input value={displayName} onChange={e => setDisplayName(e.target.value)} style={{ flex: 1, background: "rgba(255,255,255,0.1)", border: `1.5px solid rgba(255,255,255,0.3)`, borderRadius: 8, padding: "6px 10px", fontFamily: "Playfair Display, serif", fontSize: 16, color: "#fff", outline: "none" }} />
              <button onClick={handleSaveName} disabled={savingName} style={{ background: C.sunrise, border: "none", borderRadius: 8, padding: "6px 10px", fontSize: 11, color: "#fff", cursor: "pointer" }}>{savingName ? "..." : "✓"}</button>
            </div>
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
              <div style={{ fontFamily: "Playfair Display, serif", fontSize: 20, fontWeight: 700, color: "#fff" }}>{userProfile?.displayName || "Nomad"}</div>
              <button onClick={() => { setDisplayName(userProfile?.displayName || ""); setEditingName(true); }} style={{ background: "none", border: "none", color: "rgba(255,255,255,0.5)", fontSize: 11, cursor: "pointer", padding: 0 }}>✏️</button>
            </div>
          )}
          <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 13, color: "rgba(255,255,255,0.7)", fontStyle: "italic" }}>{activeVehicle?.rigYear ? `${activeVehicle.rigYear} ` : ""}{activeVehicle?.rigMake ? `${activeVehicle.rigMake} ` : ""}{activeVehicle?.rigModel || (activeVehicle?.rigType || "Road Warrior")}</div>
          <div style={{ fontFamily: "Space Mono, monospace", fontSize: 9, color: C.sageLight, marginTop: 4 }}>Member since {memberSince}</div>
        </div>
        {userProfile?.isFounder && <FounderBadge size="sm" />}
      </div>

      <div style={{ marginBottom: 16 }}>
        <div style={{ fontFamily: "Space Mono, monospace", fontSize: 9, color: C.dust, letterSpacing: 1.2, marginBottom: 10, textTransform: "uppercase" }}>
          📊 {vehicles.length > 1 ? `${activeVehicle?.name || "Vehicle"} Road Stats` : "My Road Stats"}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {[
            ["Fill-Ups Logged", totalFillUps.toString(), "total stops"],
            ["Miles Tracked", totalMiles > 0 ? totalMiles.toLocaleString() : "—", "miles driven"],
            ["Fuel Spent", totalSpent > 0 ? `$${totalSpent.toFixed(0)}` : "—", "total fuel cost"],
            ["Avg MPG", avgMPG || "—", "miles per gallon"],
          ].map(([l, v, s]) => (
            <div key={l} style={{ background: "#231408", border: `1.5px solid ${C.parchmentDeep}`, borderRadius: 12, padding: "12px 14px" }}>
              <div style={{ fontFamily: "Space Mono, monospace", fontSize: 10, color: C.parchment, letterSpacing: 1, marginBottom: 4, textTransform: "uppercase" }}>{l}</div>
              <div style={{ fontFamily: "Playfair Display, serif", fontSize: 22, fontWeight: 900, color: C.sunrise, marginBottom: 2 }}>{v}</div>
              <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 13, color: C.parchmentDeep, fontStyle: "italic" }}>{s}</div>
            </div>
          ))}
        </div>
      </div>

      <FriendsList user={user} userProfile={userProfile} onOpenMessages={onOpenMessages} />

      <div style={{ marginBottom: 16 }}>
        <div style={{ fontFamily: "Space Mono, monospace", fontSize: 9, color: C.dust, letterSpacing: 1.2, marginBottom: 10, textTransform: "uppercase" }}>🚐 My Vehicles</div>

        {vehicles.map(v => (
          <div key={v.id} style={{ background: "#231408", border: `1.5px solid ${v.id === activeVehicleId ? C.sage : C.parchmentDeep}`, borderRadius: 14, padding: 16, marginBottom: 12 }}>
            {editingVehicleId === v.id ? vehicleFormUI : (
              <>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ fontFamily: "Playfair Display, serif", fontSize: 16, fontWeight: 700, color: C.parchment }}>{v.name}</div>
                    {v.id === activeVehicleId && <span style={{ background: C.sageSoft, color: C.sageDark, borderRadius: 6, padding: "2px 8px", fontFamily: "Space Mono, monospace", fontSize: 8, fontWeight: 700 }}>ACTIVE</span>}
                  </div>
                  <button onClick={() => startEditVehicle(v)} style={{ background: "none", border: `1px solid ${C.sage}`, borderRadius: 8, padding: "4px 12px", fontFamily: "Space Mono, monospace", fontSize: 8, color: C.sage, cursor: "pointer" }}>EDIT</button>
                </div>
                {(v.rigType || v.rigYear || v.rigMake || v.rigModel || v.rigLength || v.engineType) ? (
                  <div style={{ marginBottom: 10 }}>
                    {[["Rig Type", v.rigType], ["Year", v.rigYear], ["Make", v.rigMake], ["Model", v.rigModel], ["Length", v.rigLength], ["Engine", v.engineType]].map(([l, val]) => val ? (
                      <div key={l} style={{ display: "flex", justifyContent: "space-between", paddingBottom: 6, marginBottom: 6, borderBottom: `1px solid ${C.parchmentDeep}` }}>
                        <div style={{ fontFamily: "Space Mono, monospace", fontSize: 10, color: C.parchmentDeep, textTransform: "uppercase" }}>{l}</div>
                        <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 13, color: C.parchment }}>{val}</div>
                      </div>
                    ) : null)}
                  </div>
                ) : (
                  <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 12, color: C.dust, fontStyle: "italic", marginBottom: 10 }}>No rig details yet — tap EDIT to add them.</div>
                )}
                <div style={{ display: "flex", gap: 8 }}>
                  {v.id !== activeVehicleId && <Btn variant="sage" style={{ flex: 1, marginTop: 0 }} onClick={() => onVehicleChange(v.id)}>SET ACTIVE</Btn>}
                  {vehicles.length > 1 && (
                    confirmDeleteId === v.id ? (
                      <>
                        <button onClick={() => handleDeleteVehicle(v.id)} style={{ flex: 1, background: C.red, border: "none", borderRadius: 12, padding: "13px", fontFamily: "Space Mono, monospace", fontSize: 10, fontWeight: 700, color: "#fff", cursor: "pointer" }}>CONFIRM DELETE</button>
                        <button onClick={() => setConfirmDeleteId(null)} style={{ flex: 1, background: "none", border: `1.5px solid ${C.parchmentDeep}`, borderRadius: 12, padding: "13px", fontFamily: "Space Mono, monospace", fontSize: 10, fontWeight: 700, color: C.dust, cursor: "pointer" }}>CANCEL</button>
                      </>
                    ) : (
                      <button onClick={() => setConfirmDeleteId(v.id)} style={{ background: "none", border: "none", color: C.red, fontFamily: "Space Mono, monospace", fontSize: 9, cursor: "pointer", padding: "8px 12px" }}>🗑 DELETE</button>
                    )
                  )}
                </div>
              </>
            )}
          </div>
        ))}

        {addingVehicle ? (
          <div style={{ background: "#231408", border: `1.5px solid ${C.sage}`, borderRadius: 14, padding: 16, marginBottom: 12 }}>
            <div style={{ fontFamily: "Playfair Display, serif", fontSize: 15, fontWeight: 700, color: C.parchment, marginBottom: 12 }}>New Vehicle</div>
            {vehicleFormUI}
          </div>
        ) : (
          <button onClick={startAddVehicle} style={{ width: "100%", background: "none", border: `1.5px dashed ${C.sageLight}`, borderRadius: 14, padding: 16, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
            <span style={{ fontSize: 20 }}>🚐</span>
            <div style={{ textAlign: "left" }}>
              <div style={{ fontFamily: "Space Mono, monospace", fontSize: 10, fontWeight: 700, color: C.sageDark }}>ADD A VEHICLE</div>
              <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 12, color: C.dirt, fontStyle: "italic" }}>Track a tow vehicle, second rig, or seasonal ride separately</div>
            </div>
          </button>
        )}
      </div>

      <div style={{ background: "#231408", border: `1.5px solid ${C.parchmentDeep}`, borderRadius: 14, padding: 16, marginBottom: 16 }}>
        <div style={{ fontFamily: "Space Mono, monospace", fontSize: 9, color: C.dust, letterSpacing: 1.2, marginBottom: 6, textTransform: "uppercase" }}>🛣️ Roads Profile</div>
        <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 12, color: C.dust, fontStyle: "italic", marginBottom: 14 }}>What other nomads see when they find you on Roads</div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", fontFamily: "Space Mono, monospace", fontSize: 9, fontWeight: 700, color: C.dust, letterSpacing: 1.2, marginBottom: 8, textTransform: "uppercase" }}>Photo (you, your rig, or both)</label>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{ width: 64, height: 64, borderRadius: 14, background: `linear-gradient(135deg, ${C.pine}, ${C.sageDark})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, flexShrink: 0, overflow: "hidden", border: `1.5px solid ${C.parchmentDeep}` }}>
              {rigPhotoURL ? <img src={rigPhotoURL} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : "🔥"}
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ display: "inline-block", background: photoUploading ? C.parchmentDeep : `linear-gradient(135deg, ${C.sunrise}, ${C.canyon})`, color: "#fff", borderRadius: 10, padding: "9px 14px", fontFamily: "Space Mono, monospace", fontSize: 9, fontWeight: 700, cursor: photoUploading ? "default" : "pointer", letterSpacing: 0.5 }}>
                {photoUploading ? "UPLOADING..." : rigPhotoURL ? "CHANGE PHOTO" : "UPLOAD PHOTO"}
                <input type="file" accept="image/*" onChange={handlePhotoSelect} disabled={photoUploading} style={{ display: "none" }} />
              </label>
              {rigPhotoURL && !photoUploading && (
                <button onClick={handleRemovePhoto} style={{ marginLeft: 8, background: "none", border: `1px solid ${C.parchmentDeep}`, borderRadius: 10, padding: "9px 12px", fontFamily: "Space Mono, monospace", fontSize: 9, fontWeight: 700, color: C.dust, cursor: "pointer" }}>REMOVE</button>
              )}
              {photoError && <div style={{ marginTop: 6, fontFamily: "Crimson Pro, serif", fontSize: 11, color: C.red }}>{photoError}</div>}
            </div>
          </div>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: "block", fontFamily: "Space Mono, monospace", fontSize: 9, fontWeight: 700, color: C.dust, letterSpacing: 1.2, marginBottom: 6, textTransform: "uppercase" }}>Birthdate (Roads requires 18+)</label>
          <input type="date" value={birthdate} onChange={e => setBirthdate(e.target.value)} max={new Date(Date.now() - 18*365.25*86400000).toISOString().split("T")[0]} style={{ width: "100%", minWidth: 0, maxWidth: "100%", background: "#1a0e04", border: `1.5px solid ${C.parchmentDeep}`, borderRadius: 10, padding: "12px 10px", fontFamily: "Crimson Pro, serif", fontSize: 14, color: "#f0e8d8", outline: "none", boxSizing: "border-box", WebkitAppearance: "none" }} />

          {birthdate && calculateAge(birthdate) !== null && calculateAge(birthdate) < 18 && (
            <div style={{ marginTop: 6, fontFamily: "Crimson Pro, serif", fontSize: 12, color: C.red }}>You must be 18 or older to use Roads.</div>
          )}
        </div>
        <div style={{ marginBottom: 12, display: "flex", alignItems: "center", justifyContent: "space-between", background: roadsVisible ? C.sageSoft : "#2c1a08", border: `1.5px solid ${roadsVisible ? C.sageLight : C.parchmentDeep}`, borderRadius: 12, padding: "10px 14px" }}>
          <div>
            <div style={{ fontFamily: "Space Mono, monospace", fontSize: 10, fontWeight: 700, color: roadsVisible ? C.sageDark : C.dust, letterSpacing: 0.5 }}>{roadsVisible ? "✓ VISIBLE ON ROADS" : "HIDDEN FROM ROADS"}</div>
            <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 11, color: C.dust, marginTop: 2 }}>{roadsVisible ? "Other nomads can find and message you" : "You won't appear in anyone's browse list"}</div>
          </div>
          <button onClick={() => setRoadsVisible(!roadsVisible)} style={{ width: 44, height: 26, borderRadius: 13, border: "none", cursor: "pointer", background: roadsVisible ? C.sage : C.parchmentDeep, position: "relative", flexShrink: 0 }}>
            <div style={{ width: 20, height: 20, borderRadius: "50%", background: "#fff", position: "absolute", top: 3, left: roadsVisible ? 21 : 3, transition: "left 0.2s" }} />
          </button>
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: "block", fontFamily: "Space Mono, monospace", fontSize: 9, fontWeight: 700, color: C.dust, letterSpacing: 1.2, marginBottom: 6, textTransform: "uppercase" }}>Rig Type</label>
          <select value={rigType} onChange={e => setRigType(e.target.value)} style={{ width: "100%", background: "#1a0e04", border: `1.5px solid ${C.parchmentDeep}`, borderRadius: 10, padding: "12px 14px", fontFamily: "Crimson Pro, serif", fontSize: 15, color: "#f0e8d8", outline: "none" }}>
            <option value="">Select your rig...</option>
            {["Skoolie", "Van", "Bus", "Truck Camper", "Class A", "Class B", "Class C", "Car Camp", "Other"].map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: "block", fontFamily: "Space Mono, monospace", fontSize: 9, fontWeight: 700, color: C.dust, letterSpacing: 1.2, marginBottom: 8, textTransform: "uppercase" }}>I'm Looking For</label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {["Romance", "Friendship", "Travel Companion", "Convoy Partner"].map(tag => {
              const active = seeking.includes(tag);
              return (
                <button key={tag} onClick={() => setSeeking(prev => active ? prev.filter(s => s !== tag) : [...prev, tag])} style={{ padding: "7px 14px", borderRadius: 20, border: `1.5px solid ${active ? C.sunrise : C.parchmentDeep}`, background: active ? C.sunriseSoft : "#1a0e04", color: active ? C.canyon : C.dust, fontFamily: "Space Mono, monospace", fontSize: 9, fontWeight: 700, cursor: "pointer" }}>{tag}</button>
              );
            })}
          </div>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={{ display: "block", fontFamily: "Space Mono, monospace", fontSize: 9, fontWeight: 700, color: C.dust, letterSpacing: 1.2, marginBottom: 6, textTransform: "uppercase" }}>Bio</label>
          <textarea value={bio} onChange={e => setBio(e.target.value)} placeholder="Tell other nomads about yourself, your rig, where you've been, where you're heading..." style={{ width: "100%", background: "#1a0e04", border: `1.5px solid ${C.parchmentDeep}`, borderRadius: 10, padding: "12px 14px", fontFamily: "Crimson Pro, serif", fontSize: 14, color: "#f0e8d8", outline: "none", minHeight: 90, resize: "vertical" }} />
        </div>

        <button onClick={handleSaveRoadsProfile} disabled={savingProfile} style={{ width: "100%", background: savedProfile ? `linear-gradient(135deg, ${C.green}, #3a7a3a)` : `linear-gradient(135deg, ${C.sunrise}, ${C.canyon})`, border: "none", borderRadius: 12, padding: "13px", fontFamily: "Space Mono, monospace", fontSize: 11, fontWeight: 700, color: "#fff", cursor: "pointer", letterSpacing: 0.5 }}>
          {savedProfile ? "✓ PROFILE SAVED!" : savingProfile ? "SAVING..." : "SAVE ROADS PROFILE"}
        </button>
      </div>

      <div style={{ background: "#231408", border: `1.5px solid ${C.parchmentDeep}`, borderRadius: 14, padding: 16, marginBottom: 12 }}>
        <div style={{ fontFamily: "Space Mono, monospace", fontSize: 9, color: C.dust, letterSpacing: 1.2, marginBottom: 12, textTransform: "uppercase" }}>⚙️ Account</div>
        <div style={{ display: "flex", justifyContent: "space-between", paddingBottom: 8, marginBottom: 8, borderBottom: `1px solid ${C.parchmentDeep}` }}>
          <div style={{ fontFamily: "Space Mono, monospace", fontSize: 9, color: C.dust, textTransform: "uppercase" }}>Email</div>
          <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 13, color: C.dust }}>{user.email}</div>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", paddingBottom: 8, marginBottom: 8, borderBottom: `1px solid ${C.parchmentDeep}` }}>
          <div style={{ fontFamily: "Space Mono, monospace", fontSize: 9, color: C.dust, textTransform: "uppercase" }}>Plan</div>
          <div style={{ fontFamily: "Space Mono, monospace", fontSize: 9, color: userProfile?.isPremium ? C.sunrise : C.dust }}>{userProfile?.isPremium ? "⭐ PREMIUM" : "FREE"}</div>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <div style={{ fontFamily: "Space Mono, monospace", fontSize: 9, color: C.dust, textTransform: "uppercase" }}>Credits</div>
          <div style={{ fontFamily: "Space Mono, monospace", fontSize: 9, color: C.sunrise }}>{userProfile?.credits || 0} Credits</div>
        </div>
      </div>

      {userProfile?.isFounder && (
        <div style={{ background: `linear-gradient(135deg, ${C.sunriseSoft}, ${C.canyonSoft})`, border: `1.5px solid ${C.canyonLight}`, borderRadius: 12, padding: "14px 16px", marginBottom: 12, display: "flex", alignItems: "center", gap: 12 }}>
          <FounderBadge size="md" />
          <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 13, color: C.bark, lineHeight: 1.4 }}>You're a founding member of RoadBurnAI. Your support built this.</div>
        </div>
      )}
    </div>
  );
}
// ─── MESSAGES TAB ────────────────────────────────────────────
function MessagesTab({ user, userProfile, initialConvo }) {
  const [view, setView] = useState("inbox");
  const [conversations, setConversations] = useState([]);
  const [activeConvo, setActiveConvo] = useState(initialConvo || null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const MESSAGE_TTL_DAYS = userProfile?.isPremium ? 29 : 7;

  // Load conversations
  useEffect(() => {
    if (!user) return;
    const q = query(
      collection(db, "conversations"),
      where("participants", "array-contains", user.uid),
      orderBy("lastMessageAt", "desc")
    );
    const unsub = onSnapshot(q, snap => {
      setConversations(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, err => console.error("Conversations error:", err));
    return () => unsub();
  }, [user]);

  // Load messages for active conversation
  useEffect(() => {
    if (!activeConvo) return;
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - MESSAGE_TTL_DAYS);

    const q = query(
      collection(db, "conversations", activeConvo.id, "messages"),
      orderBy("createdAt", "asc")
    );
    const unsub = onSnapshot(q, async snap => {
      const msgs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      // Auto-delete expired messages
      for (const msg of msgs) {
        if (msg.createdAt?.toDate && msg.createdAt.toDate() < cutoff) {
          await deleteDoc(doc(db, "conversations", activeConvo.id, "messages", msg.id));
        }
      }
      setMessages(msgs.filter(m => m.createdAt?.toDate && m.createdAt.toDate() >= cutoff));
    });
    return () => unsub();
  }, [activeConvo]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async () => {
    if (!newMessage.trim() || !activeConvo || sending) return;
    setSending(true);
    try {
      await addDoc(collection(db, "conversations", activeConvo.id, "messages"), {
        text: newMessage.trim(),
        senderUid: user.uid,
        senderName: userProfile?.displayName || user.email?.split("@")[0] || "Nomad",
        createdAt: serverTimestamp(),
      });
      await setDoc(doc(db, "conversations", activeConvo.id), {
        lastMessage: newMessage.trim(),
        lastMessageAt: serverTimestamp(),
        lastSenderUid: user.uid,
      }, { merge: true });
      setNewMessage("");
    } catch (err) {
      console.error("Send error:", err);
    }
    setSending(false);
  };

  const getOtherName = (convo) => {
    if (!convo.participantNames) return "Nomad";
    return convo.participantNames.find(n => n.uid !== user.uid)?.name || "Nomad";
  };

  // Thread view
  if (activeConvo) {
    return (
      <div style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 160px)", padding: "16px 16px 0" }}>
        <button onClick={() => setActiveConvo(null)} style={{ background: "none", border: "none", color: C.sage, fontFamily: "Space Mono, monospace", fontSize: 10, cursor: "pointer", marginBottom: 12, padding: 0, textAlign: "left" }}>← Back to Messages</button>
        <div style={{ background: "#231408", borderRadius: 12, padding: "10px 14px", marginBottom: 12, display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 36, height: 36, borderRadius: "50%", background: `linear-gradient(135deg, ${C.pine}, ${C.sageDark})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>🔥</div>
          <div>
            <div style={{ fontFamily: "Playfair Display, serif", fontSize: 15, fontWeight: 700, color: C.parchment }}>{getOtherName(activeConvo)}</div>
            <div style={{ fontFamily: "Space Mono, monospace", fontSize: 8, color: C.dust }}>Messages delete after {MESSAGE_TTL_DAYS} days</div>
          </div>
        </div>
        <div style={{ flex: 1, overflowY: "auto", paddingBottom: 16 }}>
          {messages.length === 0 && (
            <div style={{ textAlign: "center", padding: "40px 20px", color: C.dust, fontFamily: "Crimson Pro, serif", fontStyle: "italic" }}>No messages yet. Say hello!</div>
          )}
          {messages.map((m, i) => {
            const isMe = m.senderUid === user.uid;
            return (
              <div key={m.id || i} style={{ display: "flex", justifyContent: isMe ? "flex-end" : "flex-start", marginBottom: 10 }}>
                <div style={{ maxWidth: "78%", background: isMe ? `linear-gradient(135deg, ${C.sunrise}, ${C.canyon})` : "#231408", border: isMe ? "none" : `1.5px solid ${C.parchmentDeep}`, borderRadius: isMe ? "14px 14px 4px 14px" : "14px 14px 14px 4px", padding: "10px 14px" }}>
                  {!isMe && <div style={{ fontFamily: "Space Mono, monospace", fontSize: 8, color: C.sage, marginBottom: 4, fontWeight: 700 }}>{m.senderName}</div>}
                  <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 14, color: isMe ? "#fff" : C.parchment, lineHeight: 1.5 }}>{m.text}</div>
                </div>
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>
        <div style={{ display: "flex", gap: 8, paddingBottom: 16, borderTop: `1.5px solid ${C.parchmentDeep}`, paddingTop: 12 }}>
          <input
            value={newMessage}
            onChange={e => setNewMessage(e.target.value)}
            onKeyDown={e => e.key === "Enter" && sendMessage()}
            placeholder="Say something..."
            style={{ flex: 1, background: "#231408", border: `1.5px solid ${C.parchmentDeep}`, borderRadius: 12, padding: "12px 16px", fontFamily: "Crimson Pro, serif", fontSize: 15, color: "#f0e8d8", outline: "none", caretColor: "#f0e8d8" }}
          />
          <button onClick={sendMessage} disabled={sending} style={{ background: `linear-gradient(135deg, ${C.sunrise}, ${C.canyon})`, border: "none", borderRadius: 12, padding: "0 18px", fontSize: 18, cursor: "pointer" }}>➤</button>
        </div>
      </div>
    );
  }

  // Inbox view
  return (
    <div style={{ padding: "20px 16px" }}>
      <SectionHeader icon="✉️" title="Messages" />
      <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 13, color: C.dust, fontStyle: "italic", marginBottom: 16 }}>
        Messages disappear after {MESSAGE_TTL_DAYS} days — live light, travel free
      </div>
      {conversations.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px 20px" }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>✉️</div>
          <div style={{ fontFamily: "Playfair Display, serif", fontSize: 18, fontWeight: 700, color: C.parchment, marginBottom: 8 }}>No messages yet</div>
          <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 14, color: C.dust, fontStyle: "italic" }}>Head to Roads and send someone a message</div>
        </div>
      ) : conversations.map(convo => (
        <div key={convo.id} onClick={() => setActiveConvo(convo)} style={{ background: "#231408", border: `1.5px solid ${C.parchmentDeep}`, borderRadius: 14, padding: 16, marginBottom: 12, cursor: "pointer", display: "flex", gap: 12, alignItems: "center" }}>
          <div style={{ width: 46, height: 46, borderRadius: "50%", background: `linear-gradient(135deg, ${C.pine}, ${C.sageDark})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0 }}>🔥</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: "Playfair Display, serif", fontSize: 15, fontWeight: 700, color: C.parchment, marginBottom: 2 }}>{getOtherName(convo)}</div>
            <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 13, color: C.dust, fontStyle: "italic", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{convo.lastMessage || "..."}</div>
          </div>
          <div style={{ fontFamily: "Space Mono, monospace", fontSize: 8, color: C.dust, flexShrink: 0 }}>›</div>
        </div>
      ))}
    </div>
  );
}

// ─── SOS PANEL ───────────────────────────────────────────────
function SOSPanel({ onClose }) {
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 500, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "flex-end" }}>
      <div style={{ background: "#231408", borderRadius: "20px 20px 0 0", padding: 24, width: "100%", maxWidth: 430, margin: "0 auto" }}>
        <div style={{ fontFamily: "Playfair Display, serif", fontSize: 20, fontWeight: 700, color: C.red, marginBottom: 6 }}>🚨 Emergency SOS</div>
        <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 13, color: C.dirt, fontStyle: "italic", marginBottom: 20 }}>Two-tap activation. Your GPS location will be shared.</div>
        {[{ ic: "📞", t: "Call 911", s: "Dials emergency services immediately" }, { ic: "📍", t: "Alert My Convoy", s: "Sends GPS pin to your convoy group" }, { ic: "🆘", t: "Send SOS Message", s: "Texts emergency contacts with What3Words location" }, { ic: "🔦", t: "Flash SOS Signal", s: "Uses phone flashlight as emergency beacon" }].map((o, i) => (
          <button key={i} onClick={onClose} style={{ width: "100%", background: C.parchment, border: `1.5px solid ${C.parchmentDeep}`, borderRadius: 14, padding: "14px 16px", marginBottom: 10, display: "flex", alignItems: "center", gap: 14, cursor: "pointer", textAlign: "left" }}>
            <span style={{ fontSize: 24 }}>{o.ic}</span>
            <div>
              <div style={{ fontFamily: "Playfair Display, serif", fontSize: 15, fontWeight: 700, color: C.parchment }}>{o.t}</div>
              <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 12, color: C.dirt, fontStyle: "italic" }}>{o.s}</div>
            </div>
          </button>
        ))}
        <button onClick={onClose} style={{ width: "100%", background: "none", border: `1.5px solid ${C.parchmentDeep}`, borderRadius: 14, padding: "12px", cursor: "pointer", fontFamily: "Space Mono, monospace", fontSize: 10, color: C.dust }}>CANCEL</button>
      </div>
    </div>
  );
}

// ─── MAIN APP ────────────────────────────────────────────────
export default function RoadBurnAI() {
  const [launched, setLaunched] = useState(false);
  const [tab, setTab] = useState("fuel");
  const [fillUps, setFillUps] = useState([]);
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [loadingFillUps, setLoadingFillUps] = useState(true);
  const [userLocation, setUserLocation] = useState("Getting location...");
  const [sos, setSOS] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [showFounderReveal, setShowFounderReveal] = useState(false);
  const [activeConvoFromRoads, setActiveConvoFromRoads] = useState(null);
  const [showProfile, setShowProfile] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [activeVehicleId, setActiveVehicleId] = useState(null);

  // ── Handle Stripe payment success redirect ──
  useEffect(() => {
    if (!user) return;
    const params = new URLSearchParams(window.location.search);
    const payment = params.get("payment");
    const credits = parseInt(params.get("credits") || "0");
    if (payment === "success" && credits > 0) {
      const userRef = doc(db, "users", user.uid);
      getDoc(userRef).then(snap => {
        if (snap.exists()) {
          const current = (snap.data().credits || 0) + credits;
          updateDoc(userRef, { credits: current }).then(() => {
            setUserProfile(prev => ({ ...prev, credits: current }));
            window.history.replaceState({}, "", window.location.pathname);
            alert(`✅ ${credits} Credits added to your account!`);
          }).catch(err => {
            console.error("Credits write error:", err);
            alert("Payment received but credits failed to update — contact support.");
          });
        }
      });
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const stored = localStorage.getItem(`location_${user.uid}`);
    if (stored) { setUserLocation(stored); return; }
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(async pos => {
        try {
          const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${pos.coords.latitude}&lon=${pos.coords.longitude}&format=json`);
          const data = await res.json();
          const city = data.address?.city || data.address?.town || data.address?.village || data.address?.county || "";
          const state = data.address?.state || "";
          const loc = city && state ? `${city}, ${state}` : state || "On the Road";
          setUserLocation(loc);
          localStorage.setItem(`location_${user.uid}`, loc);
          setTimeout(() => localStorage.removeItem(`location_${user.uid}`), 7200000);
        } catch { setUserLocation("On the Road"); }
      }, () => setUserLocation("On the Road"), { timeout: 10000, maximumAge: 300000 });
    } else { setUserLocation("On the Road"); }
  }, [user]);

  useEffect(() => {
    getRedirectResult(auth).then(result => { if (result?.user) { setUser(result.user); setAuthLoading(false); } }).catch(console.error);
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async u => {
      setUser(u);
      if (u) {
        const existingSnap = await getDoc(doc(db, "users", u.uid));
        const isNewUser = !existingSnap.exists();
        const profile = await ensureUserProfile(u);
        setUserProfile(profile);
        if (isNewUser && profile.isFounder) setShowFounderReveal(true);
      } else { setUserProfile(null); setShowFounderReveal(false); }
      setAuthLoading(false);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!user) { setFillUps([]); setLoadingFillUps(false); return; }
    const q = query(collection(db, "fillups"), where("uid", "==", user.uid), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(q, snap => { setFillUps(snap.docs.map(d => ({ id: d.id, ...d.data() }))); setLoadingFillUps(false); }, err => { console.error("Firebase error:", err); setLoadingFillUps(false); });
    return () => unsub();
  }, [user]);

  // ── Load vehicle profiles — create a default one from existing rig info if none exist ──
  useEffect(() => {
    if (!user) { setVehicles([]); setActiveVehicleId(null); return; }
    const q = query(collection(db, "vehicles"), where("uid", "==", user.uid), orderBy("createdAt", "asc"));
    const unsub = onSnapshot(q, async snap => {
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      if (list.length === 0 && userProfile) {
        try {
          await addDoc(collection(db, "vehicles"), {
            uid: user.uid,
            name: "My Rig",
            rigType: userProfile.rigType || "",
            rigYear: userProfile.rigYear || "",
            rigMake: userProfile.rigMake || "",
            rigModel: userProfile.rigModel || "",
            rigLength: userProfile.rigLength || "",
            engineType: userProfile.engineType || "",
            createdAt: serverTimestamp(),
          });
        } catch (err) { console.error("Error creating default vehicle:", err); }
        return;
      }
      setVehicles(list);
      setActiveVehicleId(prev => (prev && list.some(v => v.id === prev)) ? prev : (list[0]?.id || null));
    }, err => console.error("Vehicles error:", err));
    return () => unsub();
  }, [user, userProfile]);

  if (authLoading) return <div style={{ minHeight: "100vh", background: C.parchment, display: "flex", alignItems: "center", justifyContent: "center" }}><div style={{ fontFamily: "Playfair Display, serif", fontSize: 24, fontWeight: 700, color: C.parchment }}>Loading...</div></div>;
  if (!user) return <LoginScreen />;

  const tabs = [
    { id: "profile", icon: "👤",  label: "Profile",  sub: "My rig & travel stats" },
    { id: "fuel",   icon: "⛽",  label: "Fuel",    sub: "Track fill-ups & MPG" },
    { id: "camp",   icon: "⛺",  label: "Camp + Bath",    sub: "Campsites & restrooms" },
    { id: "market", icon: "🛒",  label: "Market",  sub: "Buy & sell gear" },
    { id: "roads",  icon: "🛣️", label: "Roads",   sub: "Nomadic connections" },
    { id: "messages", icon: "✉️",  label: "Messages", sub: "Nomad to nomad" },
    { id: "forum",  icon: "💬",  label: "Forum",   sub: "Community threads" },
    { id: "news",   icon: "📰",  label: "News",    sub: "Nomad news & sources" },
  { id: "savings", icon: "💰",  label: "Savings",  sub: "Money-saving tools" },
  { id: "learn",  icon: "📚",  label: "Learn",   sub: "Repair & build resources" },
{ id: "weather", icon: "🌤️",  label: "Weather",  sub: "Forecast & alerts" },
  { id: "rescue", icon: "🔧",  label: "Rescue",  sub: "Breakdown directory" },
    { id: "trip",   icon: "🗺️", label: "Trip",    sub: "Plan your route" },
    { id: "ai",     icon: "🔥",  label: "AI",      sub: "Your road assistant" },
  ];

  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";

  return (
    <div style={{ background: "#1a0e04", minHeight: "100vh", maxWidth: 430, margin: "0 auto", fontFamily: "Crimson Pro, serif" }}>
      <style>{`
        ${fonts}
        * { box-sizing: border-box; }
        @keyframes twinkle { 0%,100%{opacity:0.2;transform:scale(0.8)} 50%{opacity:1;transform:scale(1)} }
        @keyframes fadeIn { from{opacity:0} to{opacity:1} }
        @keyframes fadeUp { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
        @keyframes globeFloat { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-8px)} }
        @keyframes pulse { 0%,100%{opacity:0.3;transform:scale(0.8)} 50%{opacity:1;transform:scale(1)} }
        @keyframes pingRing { 0%{transform:translate(-50%,-50%) scale(0.3);opacity:0.9} 100%{transform:translate(-50%,-50%) scale(3);opacity:0} }
        @keyframes pingPulse { 0%,100%{box-shadow:0 0 8px #f5a623,0 0 16px rgba(245,166,35,0.4)} 50%{box-shadow:0 0 16px #f5a623,0 0 32px rgba(245,166,35,0.7)} }
        @keyframes sosPulse { 0%,100%{box-shadow:0 0 0 0 rgba(200,64,64,0.3)} 50%{box-shadow:0 0 0 8px rgba(200,64,64,0)} }
        @keyframes radarPulse { 0%{opacity:0.9;transform:translate(-50%,-50%) scale(0.3)} 100%{opacity:0;transform:translate(-50%,-50%) scale(1)} }
        select option { background: ${C.cream}; color: ${C.bark}; }
        ::-webkit-scrollbar { width: 3px; }
        ::-webkit-scrollbar-thumb { background: ${C.parchmentDeep}; border-radius: 2px; }
        input, textarea, select { color: #f0e8d8 !important; caret-color: #f0e8d8 !important; }
        input::placeholder, textarea::placeholder { color: rgba(240,232,216,0.5) !important; }
        input:-webkit-autofill, input:-webkit-autofill:hover, input:-webkit-autofill:focus, input:-webkit-autofill:active { -webkit-text-fill-color: #f0e8d8 !important; -webkit-box-shadow: 0 0 0 1000px #231408 inset !important; box-shadow: 0 0 0 1000px #231408 inset !important; }
        input:-webkit-autofill, input:-webkit-autofill:hover, input:-webkit-autofill:focus, input:-webkit-autofill:active { -webkit-text-fill-color: #f0e8d8 !important; -webkit-box-shadow: 0 0 0 1000px #231408 inset !important; box-shadow: 0 0 0 1000px #231408 inset !important; }
        [style*="font-size:7px"], [style*="font-size: 7px"] { font-size: 9px !important; }
        [style*="font-size:8px"], [style*="font-size: 8px"] { font-size: 10px !important; }
        [style*="font-size:9px"], [style*="font-size: 9px"] { font-size: 11px !important; }
      `}</style>

      {!launched && <LaunchScreen onComplete={() => setLaunched(true)} />}
      {sos && <SOSPanel onClose={() => setSOS(false)} />}

      {/* DRAWER OVERLAY */}
      {drawerOpen && <div onClick={() => setDrawerOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 150 }} />}

      {/* DRAWER */}
      <div style={{ position: "fixed", top: 0, left: 0, bottom: 0, width: "72%", maxWidth: 300, background: "#1a0e04", borderRight: "1px solid #3a2418", zIndex: 200, display: "flex", flexDirection: "column", paddingTop: 60, transform: drawerOpen ? "translateX(0)" : "translateX(-100%)", transition: "transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)" }}>

        <div style={{ padding: "0 24px 24px", borderBottom: "1px solid #3a2418" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: 12, background: `linear-gradient(135deg, ${C.sunrise}, ${C.sunriseDeep})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>🔥</div>
            <div>
              <div style={{ fontFamily: "Playfair Display, serif", fontSize: 18, fontWeight: 900, color: C.parchment }}>Road<span style={{ color: C.sunrise }}>Burn</span><span style={{ fontFamily: "Space Mono, monospace", fontSize: 14, color: C.sage }}>AI</span></div>
              <div style={{ fontFamily: "Space Mono, monospace", fontSize: 8, color: C.dust, letterSpacing: 1.5 }}>THE NOMADIC LIFE APP</div>
            </div>
          </div>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: "12px 0" }}>
          {tabs.map(t => (
            <button key={t.id} onClick={() => { setTab(t.id); setDrawerOpen(false); }} style={{ width: "100%", background: tab === t.id ? "rgba(232,114,10,0.12)" : "transparent", border: "none", borderLeft: tab === t.id ? `3px solid ${C.sunrise}` : "3px solid transparent", padding: "14px 24px", display: "flex", alignItems: "center", gap: 14, cursor: "pointer", textAlign: "left" }}>
              {t.id === "camp" ? (
                <div style={{ width: 38, height: 38, borderRadius: 12, overflow: "hidden", display: "flex", flexShrink: 0 }}>
                  <div style={{ flex: 1, background: tab === t.id ? `linear-gradient(135deg, ${C.sage}, ${C.sageDark})` : "#231408", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13 }}>⛺</div>
                  <div style={{ flex: 1, background: tab === t.id ? `linear-gradient(135deg, ${C.sunrise}, ${C.canyon})` : "#1a0e04", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13 }}>🚻</div>
                </div>
              ) : (
                <div style={{ width: 38, height: 38, borderRadius: 12, background: tab === t.id ? `linear-gradient(135deg, ${C.sunrise}, ${C.sunriseDeep})` : "#231408", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>{t.icon}</div>
              )}
              <div>
                <div style={{ fontFamily: "Playfair Display, serif", fontSize: 15, fontWeight: 700, color: tab === t.id ? C.sunrise : C.parchment }}>{t.label}</div>
                <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 11, color: C.dust, marginTop: 1 }}>{t.sub}</div>
              </div>
              {tab === t.id && <div style={{ marginLeft: "auto", color: C.sunrise, fontSize: 16 }}>›</div>}
            </button>
          ))}
        </div>
        <div style={{ padding: "16px 24px", borderTop: "1px solid #3a2418" }}>
          <div style={{ fontFamily: "Space Mono, monospace", fontSize: 9, color: C.dust, letterSpacing: 1 }}>roadburnai.app</div>
        </div>
      </div>

      {/* MAIN CONTENT */}
      <div style={{ position: "relative", zIndex: 100, minHeight: "100vh", background: "#1a0e04" }}>
        {/* HEADER */}
        <div style={{ background: "#231408", borderBottom: `1.5px solid #3a2418`, padding: "12px 16px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button onClick={() => setDrawerOpen(!drawerOpen)} style={{ width: 38, height: 38, borderRadius: 11, background: drawerOpen ? `linear-gradient(135deg, ${C.sunrise}, ${C.sunriseDeep})` : "#2c1a08", border: `1.5px solid ${drawerOpen ? C.sunrise : "#3a2418"}`, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 5, cursor: "pointer", padding: 0, flexShrink: 0 }}>
              {[0,1,2].map(i => <div key={i} style={{ width: 16, height: 2, background: drawerOpen ? "#fff" : C.parchment, borderRadius: 1 }} />)}
            </button>
            <div>
              <div style={{ fontFamily: "Playfair Display, serif", fontSize: 20, fontWeight: 900, color: C.parchment }}>Road<span style={{ color: C.sunrise }}>Burn</span><span style={{ fontFamily: "Space Mono, monospace", fontSize: 14, color: C.sage }}>AI</span></div>
              <div style={{ fontFamily: "Space Mono, monospace", fontSize: 7, color: C.dust, letterSpacing: 0.8, whiteSpace: "nowrap" }}>THE NOMADIC LIFE APP</div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginLeft: "auto" }}>
              <button onClick={() => signOut(auth)} style={{ background: "#231408", border: `1.5px solid ${C.parchmentDeep}`, borderRadius: 10, padding: "7px 12px", cursor: "pointer", display: "flex", alignItems: "center", gap: 8, transition: "opacity 0.15s" }}>
  {user.photoURL
    ? <img src={user.photoURL} alt="" style={{ width: 22, height: 22, borderRadius: "50%", border: `1.5px solid ${C.parchmentDeep}` }} />
    : <div style={{ width: 22, height: 22, borderRadius: "50%", background: `linear-gradient(135deg, ${C.sunrise}, ${C.canyon})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11 }}>🔥</div>
  }
  <div>
    <div style={{ fontFamily: "Space Mono, monospace", fontSize: 8, fontWeight: 700, color: C.parchment, letterSpacing: 0.5, lineHeight: 1.2 }}>{userProfile?.displayName?.split(" ")[0] || "Nomad"}</div>
    <div style={{ fontFamily: "Space Mono, monospace", fontSize: 7, color: C.dust, letterSpacing: 0.5, lineHeight: 1.2 }}>SIGN OUT</div>
  </div>
</button>
              <button onClick={() => setSOS(true)} style={{ background: C.redSoft, border: `1.5px solid #e8b8b8`, borderRadius: 10, padding: "8px 12px", cursor: "pointer", display: "flex", alignItems: "center", gap: 5, animation: "sosPulse 2s ease-in-out infinite" }}>
                <span style={{ fontSize: 14 }}>🚨</span>
                <span style={{ fontFamily: "Space Mono, monospace", fontSize: 9, fontWeight: 700, color: C.red }}>SOS</span>
              </button>
            </div>
          </div>
        </div>

        {/* GREETING */}
        <div style={{ background: "linear-gradient(135deg, #0a3838, #1a6868)", padding: "16px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontFamily: "Space Mono, monospace", fontSize: 8, color: C.sageLight, letterSpacing: 1.5, textTransform: "uppercase" }}>{greeting}</div>
            <div style={{ fontFamily: "Playfair Display, serif", fontSize: 20, fontWeight: 700, color: "#fff" }}>{user.displayName?.split(" ")[0] || "Nomad"}</div>
            <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 13, color: "rgba(255,255,255,0.6)", fontStyle: "italic" }}>{userLocation}</div>
            {userProfile?.isFounder && (
              <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8 }}>
                <FounderBadge size="md" />
                <span style={{ fontFamily: "Crimson Pro, serif", fontSize: 12, color: "rgba(255,255,255,0.6)" }}>Early community member</span>
              </div>
            )}
          </div>
          <div style={{ fontSize: 36, animation: "globeFloat 4s ease-in-out infinite", filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.3))" }}>🌎</div>
        </div>

        {user && !user.emailVerified && !user.providerData?.some(p => p.providerId === "google.com") && user.email && !user.email.includes("gmail.com") && (
  <div style={{ background: C.sunriseSoft, border: `1.5px solid ${C.canyonLight}`, borderRadius: 12, margin: "12px 16px 0", padding: "12px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
    <div>
      <div style={{ fontFamily: "Space Mono, monospace", fontSize: 9, fontWeight: 700, color: C.canyon, letterSpacing: 1, marginBottom: 3 }}>📧 VERIFY YOUR EMAIL</div>
      <div style={{ fontFamily: "Crimson Pro, serif", fontSize: 12, color: C.bark, lineHeight: 1.4 }}>Check your inbox to verify your account.</div>
    </div>
    <button onClick={async () => { try { await sendEmailVerification(user); } catch {} }} style={{ background: `linear-gradient(135deg, ${C.sunrise}, ${C.canyon})`, border: "none", borderRadius: 8, padding: "8px 12px", fontFamily: "Space Mono, monospace", fontSize: 8, fontWeight: 700, color: "#fff", cursor: "pointer", whiteSpace: "nowrap" }}>RESEND</button>
  </div>
)}
{showFounderReveal && (
          <div style={{ padding: "0 16px" }}>
            <FounderRevealCard onDismiss={() => setShowFounderReveal(false)} />
          </div>
        )}

        {/* CONTENT */}
        <div style={{ paddingBottom: 80, minHeight: "60vh" }}>
          {tab === "profile" && <ProfileTab user={user} userProfile={userProfile} fillUps={fillUps} onProfileUpdate={p => setUserProfile(p)} vehicles={vehicles} activeVehicleId={activeVehicleId} onVehicleChange={setActiveVehicleId} onOpenMessages={(convo) => { setActiveConvoFromRoads(convo); setTab("messages"); }} />}
          {tab === "fuel"   && <FuelTab fillUps={fillUps} loading={loadingFillUps} user={user} vehicles={vehicles} activeVehicleId={activeVehicleId} onVehicleChange={setActiveVehicleId} />}
          {tab === "camp"   && <CampTab userLocation={userLocation} user={user} />}
          {tab === "market" && <MarketTab isFounder={userProfile?.isFounder} user={user} userProfile={userProfile} />}
          {tab === "roads"  && <RoadsTab isFounder={userProfile?.isFounder} user={user} userProfile={userProfile} onOpenMessages={(convo) => { setActiveConvoFromRoads(convo); setTab("messages"); }} onProfileUpdate={p => setUserProfile(p)} onNavigateToProfile={() => setTab("profile")} />}          {tab === "messages" && <MessagesTab user={user} userProfile={userProfile} initialConvo={activeConvoFromRoads} />}
          {tab === "forum"  && <ForumTab isFounder={userProfile?.isFounder} />}
          {tab === "news"    && <NewsTab />}
          {tab === "savings" && <SavingsTab />}
          {tab === "learn"   && <LearnTab />}
          {tab === "weather" && <WeatherTab />}
          {tab === "rescue" && <RescueTab />}
          {tab === "trip"   && <TripTab />}
          {tab === "ai"     && <AITab />}
        </div>
      </div>
    </div>
  );
}